import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FileText, ChevronRight, ChevronLeft, CheckCircle2, AlertTriangle,
  Users, Beaker, FileCheck, Calculator, BookOpen, Download,
  Plus, Trash2, AlertCircle, Scale, Building2, FileWarning,
  ClipboardList, ArrowRight, Info, Flag, Printer, Save, RefreshCw,
  Upload, Paperclip, Mail, Send, Copy, X, Link2, FileSpreadsheet,
  FileImage, File, MessageSquare, Inbox, CheckSquare, Square,
  GitBranch, HelpCircle, Shield, XCircle,
  Briefcase, DollarSign, Pencil, Clock, PlayCircle, Pause,
  PenLine, CheckCheck, Target, Timer, CalendarDays
} from "lucide-react";
import { supabase } from "../lib/supabase.js";

// ============================================================
// MONEY INPUT — formats numbers with commas as the user types.
// Stores the raw digit string (no commas) so existing parseFloat
// call sites keep working unchanged.
// ============================================================
function formatMoneyDisplay(v) {
  if (v === null || v === undefined || v === "") return "";
  const s = String(v);
  const dotIdx = s.indexOf(".");
  const intPart = dotIdx >= 0 ? s.slice(0, dotIdx) : s;
  const decPart = dotIdx >= 0 ? s.slice(dotIdx) : "";
  const cleanInt = intPart.replace(/[^0-9-]/g, "");
  if (cleanInt === "" || cleanInt === "-") return cleanInt + decPart;
  const formatted = cleanInt.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formatted + decPart;
}

function MoneyInput({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={formatMoneyDisplay(value)}
      onChange={(e) => {
        const raw = e.target.value.replace(/,/g, "");
        onChange({ target: { value: raw } });
      }}
    />
  );
}

// ============================================================
// TAX CODE CITATIONS
// ============================================================
const CITATIONS = {
  "41(a)(1)": { label: "IRC §41(a)(1)(A)-(B)", text: "Regular credit = 20% of the excess of current-year QREs over the base amount." },
  "41(b)(1)": { label: "IRC §41(b)(1)(A)-(B)", text: "QREs consist of in-house research expenses and contract research expenses." },
  "41(b)(2)": { label: "IRC §41(b)(2)(A)(i)-(iii)", text: "In-house research expenses include wages, supplies, and certain computer-use amounts." },
  "41(c)(4)": { label: "IRC §41(c)(4)(A)-(C)", text: "ASC = 14% × (current-year QREs − 50% of prior 3-year average QREs). 6% of current-year QREs if no QREs in any of 3 preceding years." },
  "41(d)(1)": { label: "IRC §41(d)(1)(A)-(C)", text: "Qualified research must (A) be §174A domestic R&E expenditures (post-OBBBA; reference updated from §174), (B) be undertaken to discover information technological in nature for a new/improved business component, and (C) have substantially all activities constituting a process of experimentation." },
  "41(d)(4)": { label: "IRC §41(d)(4)(A)-(H)", text: "Excluded: research after commercial production, adaptation, duplication, surveys/studies, software (certain), foreign research, social sciences, funded research." },
  "41(f)(1)": { label: "IRC §41(f)(1)(A)(i)-(ii)", text: "Controlled group members treated as single taxpayer; credit allocated proportionately." },
  "41(h)(1)": { label: "IRC §41(h)(1)", text: "Qualified small business may elect to apply payroll tax credit against payroll tax liability." },
  "41(h)(3)": { label: "IRC §41(h)(3)(A)(i)(I)-(II)", text: "QSB = gross receipts < $5M AND no gross receipts before the 5-tax-year window ending with current year." },
  "41(h)(4)": { label: "IRC §41(h)(4)(B)(i)(I)-(II)", text: "Elected payroll tax credit amount may not exceed $500,000 for tax years beginning after Dec 31, 2022." },
  "1.41-2(c)": { label: "Treas. Reg. §1.41-2(c)(1)-(3)", text: "Qualified wage services = (1) actual conduct of qualified research, (2) direct supervision, (3) direct support." },
  "1.41-2(e)": { label: "Treas. Reg. §1.41-2(e)(1)-(3)", text: "Contract research generally counted at 65%; requires agreement before work begins, work performed on behalf of taxpayer, taxpayer bears cost if unsuccessful, taxpayer has rights to results." },
  "1.41-4": { label: "Treas. Reg. §1.41-4", text: "Uncertainty exists when available information does not establish capability, method, or appropriate design. Must fundamentally rely on physical/biological sciences, engineering, or computer science." },
  "1.41-6": { label: "Treas. Reg. §1.41-6", text: "Controlled group credit computed on aggregate basis using same method across members; intercompany transfers disregarded." },
  "174A": { label: "IRC §174A (OBBBA 2025)", text: "NEW: Enacted by OBBBA 7/4/2025. Permanently restores immediate expensing for domestic R&E expenditures paid or incurred in tax years beginning after 12/31/2024. Foreign R&E remains subject to 15-year amortization under §174." },
  "174A(c)": { label: "IRC §174A(c)", text: "Election to capitalize and amortize domestic R&E over minimum 60 months; 10-year write-off also available under §174A(c)(1)(B)." },
  "280C": { label: "IRC §280C(c) (post-OBBBA)", text: "For TY2025+: taxpayer must either (1) claim full credit and reduce §174A deduction by credit amount, or (2) elect reduced credit under §280C(c)(2). Reduced credit: regular method at 15.8% (instead of 20%); ASC at 79% of gross ASC. Election made on timely original return (including extensions); eligible small businesses may make late election on amended returns through 7/6/2026 per Rev. Proc. 2025-28." },
  "OBBBA-retro": { label: "OBBBA §70302 / Rev. Proc. 2025-28", text: "Small business retroactive election: taxpayers with 3-year avg gross receipts ≤ $31M per §448(c) may retroactively apply §174A to TY2022-2024. Must file by earlier of 7/6/2026 or §6511 statute of limitations. Amended returns required for each affected year." },
};

// Current regulatory date stamps - update as law changes
const REG_LAST_UPDATED = "Rev. Proc. 2025-28 (Aug 28, 2025) · OBBBA signed July 4, 2025";

const PHASES = [
  { id: "scope", label: "Scope & Entity", icon: Building2, short: "01" },
  { id: "engagement", label: "Engagement & Fees", icon: Briefcase, short: "02" },
  { id: "tree", label: "Decision Tree", icon: GitBranch, short: "03" },
  { id: "screen", label: "Qualification Screen", icon: FileWarning, short: "04" },
  { id: "requests", label: "Client Requests", icon: Inbox, short: "05" },
  { id: "documents", label: "Document Vault", icon: Paperclip, short: "06" },
  { id: "projects", label: "Project Qualification", icon: ClipboardList, short: "07" },
  { id: "wages", label: "Wage QREs", icon: Users, short: "08" },
  { id: "supplies", label: "Supplies", icon: Beaker, short: "09" },
  { id: "contracts", label: "Contract Research", icon: FileCheck, short: "10" },
  { id: "compute", label: "Credit Computation", icon: Calculator, short: "11" },
  { id: "elections", label: "Elections & Groups", icon: Scale, short: "12" },
  { id: "impact", label: "Client Impact", icon: Target, short: "13" },
  { id: "report", label: "Final Report", icon: FileText, short: "14" },
];

const FOUR_PART_TEST = [
  { id: "purpose", title: "Permitted Purpose", cite: "41(d)(1)", q: "Is the work aimed at developing a new or improved business component — product, process, software, formulation, technique, or invention — with respect to function, performance, reliability, or quality?" },
  { id: "uncertainty", title: "Technical Uncertainty", cite: "1.41-4", q: "At the outset, was there uncertainty about the capability, method, or appropriate design of the business component?" },
  { id: "experimentation", title: "Process of Experimentation", cite: "41(d)(1)", q: "Did the work involve evaluating alternatives — modeling, simulating, systematic trial and error, or iterative testing?" },
  { id: "technological", title: "Technological in Nature", cite: "41(d)(1)", q: "Did the process fundamentally rely on principles of engineering, computer science, physical science, or biological science?" },
];

const EXCLUSIONS = [
  { id: "post_production", text: "Research after commercial production began", cite: "41(d)(4)" },
  { id: "adaptation", text: "Adaptation to a particular customer's requirement", cite: "41(d)(4)" },
  { id: "duplication", text: "Duplication of an existing business component", cite: "41(d)(4)" },
  { id: "surveys", text: "Surveys, studies, market research, routine testing", cite: "41(d)(4)" },
  { id: "foreign", text: "Research conducted outside the United States", cite: "41(d)(4)" },
  { id: "social", text: "Research in social sciences, arts, or humanities", cite: "41(d)(4)" },
  { id: "funded", text: "Funded research (customer bears risk / retains rights)", cite: "41(d)(4)" },
  { id: "cosmetic", text: "Style, taste, cosmetic, or seasonal design factors only", cite: "1.41-4" },
];

// ============================================================
// SCREENING RESPONSE TEMPLATE LIBRARY
// ============================================================
// Pre-written narrative templates that a non-technical staff member
// can select and adapt. Each template invokes the correct §41 four-part
// test language (business component, technological in nature, uncertainty,
// process of experimentation) without requiring the staff member to know
// the underlying law. Staff selects an industry + variant, then edits the
// resulting narrative with client-specific facts.
// ============================================================

const SCREEN_INDUSTRIES = [
  { id: "software", label: "Software / SaaS / Tech" },
  { id: "manufacturing", label: "Manufacturing / Fabrication" },
  { id: "engineering", label: "Engineering Services / AEC" },
  { id: "food", label: "Food & Beverage / Formulation" },
  { id: "construction", label: "Construction / MEP" },
  { id: "biotech", label: "Biotech / Life Sciences / Medical" },
  { id: "agriculture", label: "Agriculture / AgTech" },
  { id: "generic", label: "Other / Generic" },
];

// SCREEN_TEMPLATES: [questionKey][industry][answer] = array of template strings
// Each template uses {placeholders} that staff should fill in from interviews.
const SCREEN_TEMPLATES = {
  // ============================================================
  // Q1: Does the company develop or improve products, processes, software,
  // formulations, or techniques? (Business Component element — §41(d)(1)(B)(ii))
  // ============================================================
  hasDevelopment: {
    software: {
      YES: [
        "Client develops {product/platform name} — a {SaaS application / mobile app / internal platform / software product} that {describes primary function}. During the study year, the engineering team worked on {N} distinct development initiatives including new feature development, architectural improvements, performance optimization, and integration work. Verified via interview with {CTO/VP Engineering name} on {interview date} and review of the {Jira/Linear/GitHub} project history. This work constitutes development of a new or improved business component (software) under IRC §41(d)(1)(B)(ii).",
        "The company builds and maintains {describe software product line}. The development team actively releases new versions and iterates on the platform throughout the year to improve functionality, scalability, and user-facing features. Evidence of ongoing development includes release notes, sprint retrospectives, and the product roadmap. Business component: the software product itself (§41(d)(1)(B)(ii)).",
      ],
      NO: [
        "The company's development work during the study year was limited to {cosmetic UI updates / routine maintenance / standard configuration of third-party software}. There were no substantive product or process improvements that would rise to the level of a new or improved business component.",
      ],
    },
    manufacturing: {
      YES: [
        "Client designs and manufactures {product category} at their {location} facility. During the study year the engineering and production teams worked on {new product development / existing product improvements / manufacturing process improvements} including {brief description of projects}. The work targeted improvements in {yield / throughput / quality / reliability / cost / material usage}. Verified via plant tour on {date} and interview with {engineering manager name}. Business components include both the products being developed and the manufacturing processes themselves, per IRC §41(d)(1)(B)(ii).",
        "The company performs ongoing product development and process improvement work on its manufacturing line. Specific initiatives during the study year included {describe 2-3 projects}. Engineering drawings, ECNs (engineering change notices), and production trial records corroborate the work. Business components: the products manufactured and the production processes used to make them.",
      ],
      NO: [
        "During the study year the client performed only routine production work with no design changes or process improvements. No prototyping, no engineering changes, no testing programs.",
      ],
    },
    engineering: {
      YES: [
        "Client is a {structural / civil / mechanical / electrical} engineering firm providing design services to {client type}. The firm's engineers develop custom designs for each project that involve {describe typical design challenges}. Project designs qualify as business components because they involve the application of engineering principles to develop new or improved designs that the client retains rights to per the terms of their engagements. Verified via review of {N} project files during fieldwork on {date}.",
      ],
      NO: [
        "The engineering work performed during the study year consisted of {routine drafting / standard template adaptation / pure project management} without substantive engineering design or analysis.",
      ],
    },
    food: {
      YES: [
        "Client develops {food products / beverages / formulations} at their R&D kitchen/lab at {location}. During the study year the product development team worked on {N} formulation projects including new product development and reformulation of existing SKUs for {shelf stability / allergen removal / cost reduction / regulatory compliance / taste improvement}. Bench trials, pilot production runs, and sensory panel results are documented. Business components: the formulations and production processes developed or improved.",
      ],
      NO: [
        "During the study year the client's activities were limited to {routine batching / packaging updates / label changes} without formulation or process development.",
      ],
    },
    construction: {
      YES: [
        "Client is a {general contractor / specialty trade contractor / design-build firm} that performs {describe scope — e.g., commercial MEP, heavy civil, custom residential}. On {project name(s)}, the team encountered project-specific technical challenges requiring development of custom methods, means, or assemblies. Business components include the custom methods and designs developed for these projects, per IRC §41(d)(1)(B)(ii). Verified via interview with {project manager name} and review of submittals/RFIs on {date}.",
      ],
      NO: [
        "The work during the study year involved only standard construction methods following published specifications with no custom design or means-and-methods development.",
      ],
    },
    biotech: {
      YES: [
        "Client is a {biotech / pharma / medical device / diagnostics} company developing {describe product}. During the study year the research team performed {assay development / formulation development / device design / preclinical testing / process development} aimed at {describe goal}. Lab notebooks, protocol documents, and internal reports corroborate. Business components: the products and methods under development, per IRC §41(d)(1)(B)(ii).",
      ],
      NO: [
        "During the study year the company's activities were limited to {routine QC testing / commercial manufacturing / contract services executed against an established protocol}.",
      ],
    },
    agriculture: {
      YES: [
        "Client operates a {farming operation / ag technology company / food production business} and during the study year worked on {crop variety trials / precision agriculture technology / irrigation system development / new equipment design / process improvements}. Trial plots, yield data, and technical reports document the work. Business components: {the crop varieties / equipment / processes / software} developed or improved.",
      ],
      NO: [
        "The operation during the study year was limited to routine farming/production activities using established practices with no trials or development work.",
      ],
    },
    generic: {
      YES: [
        "During the study year the client performed development or improvement work on {product/process/software/technique — fill in}. Specific initiatives included {describe 2-3 initiatives}. The work was aimed at {developing new capability / improving an existing offering}. Business component: {fill in what the client is developing or improving}, per IRC §41(d)(1)(B)(ii). Verified via interview with {technical lead name} on {date}.",
      ],
      NO: [
        "The client's activities during the study year consisted of routine operational work with no substantive development or improvement of any business component.",
      ],
    },
  },

  // ============================================================
  // Q2: Technical uncertainty at the outset? (Treas. Reg. §1.41-4(a)(3)(i))
  // ============================================================
  hasUncertainty: {
    software: {
      YES: [
        "At the start of the work on {feature/project name}, the team did not know whether the desired technical result could be achieved within the required {performance / scale / latency / reliability} constraints. Specifically, the uncertainty was {capability — whether it could be built at all / method — which technical approach would work / design — which architectural choice would meet the requirements}. This uncertainty is documented in {design spec / RFC / architecture decision record} dated {date}. Satisfies uncertainty element per Treas. Reg. §1.41-4(a)(3)(i).",
        "Technical uncertainty existed because the team needed to {integrate with an undocumented third-party system / achieve a performance target that prior approaches had not met / reconcile conflicting technical requirements / solve a scalability problem that had not been solved before in their stack}. The available information at the outset did not establish the capability, method, or appropriate design.",
      ],
      NO: [
        "The work in question was implementation of a well-documented, previously-solved pattern. The team knew from the outset what approach would work and followed standard practice. No technical uncertainty present.",
      ],
    },
    manufacturing: {
      YES: [
        "At the outset of {project name}, the team was uncertain whether {new product could meet spec / new process could achieve target yield / new material would behave as required in the finished product}. Uncertainty was around {capability / method / design} per Treas. Reg. §1.41-4(a)(3)(i). Initial engineering assessments and early trial runs document the open technical questions.",
        "The team faced method uncertainty: they knew the desired outcome (e.g., tighter tolerance, higher throughput, lower scrap) but did not know which combination of {process parameters / tooling / material / machine settings} would achieve it. Multiple approaches were open at the start.",
      ],
      NO: [
        "The production work followed a fully-defined, previously-validated process. The engineering team knew the outcome with confidence. No technical uncertainty.",
      ],
    },
    engineering: {
      YES: [
        "For {project name}, the design team faced design uncertainty at the outset because the project conditions — {unique site conditions / unusual load cases / client-specific performance requirements / integration with existing structures} — had not been previously solved by the firm. The final design was not known at the start; the engineers had to evaluate multiple configurations against code requirements and performance goals. Satisfies uncertainty element per Treas. Reg. §1.41-4(a)(3)(i).",
      ],
      NO: [
        "The design work was a straightforward application of firm templates to a standard condition, with no open technical questions at the outset.",
      ],
    },
    food: {
      YES: [
        "At the start of the {formulation project}, the R&D team did not know what combination of ingredients and processing parameters would achieve the target {taste profile / shelf life / texture / nutritional target / cost}. Multiple formulation paths were open — whether to approach the target via {ingredient A vs. B / process X vs. Y} was an unresolved technical question.",
      ],
      NO: [
        "The formulation work was a minor variation on an existing recipe with no open technical questions.",
      ],
    },
    construction: {
      YES: [
        "For {project name}, uncertainty existed around means-and-methods because the project involved {unusual site conditions / constrained access / unconventional sequencing / first-time material or system}. The team did not know at the outset which approach would achieve the construction objective within cost and schedule constraints. RFIs, submittals, and mockup documentation show the unresolved technical questions.",
      ],
      NO: [
        "The work used industry-standard means and methods on a routine site condition. No design or construction uncertainty.",
      ],
    },
    biotech: {
      YES: [
        "At project initiation, the team faced technical uncertainty around {whether the assay could achieve target sensitivity / whether the formulation would remain stable / whether the device would meet performance specs / whether the manufacturing process would produce clinical-grade material}. Open scientific questions are captured in {protocol document / DOE plan / project charter} dated {date}. Satisfies uncertainty per Treas. Reg. §1.41-4(a)(3)(i).",
      ],
      NO: [
        "The work executed an established protocol with known outcome. No scientific uncertainty.",
      ],
    },
    agriculture: {
      YES: [
        "Uncertainty existed at the outset because the team did not know whether {new crop variety would perform in the target climate / new irrigation approach would reduce water use without compromising yield / new equipment design would handle field conditions reliably / new data-analytics approach would produce actionable insights}.",
      ],
      NO: [
        "The work followed established agronomic practice with known outcomes. No technical uncertainty.",
      ],
    },
    generic: {
      YES: [
        "At the outset of {project/activity name}, the team was uncertain about {capability — whether the desired result could be achieved / method — what approach would work / design — what final configuration would meet requirements}. The available information did not establish the answer. Specifically, {brief fact pattern describing the open question}. Satisfies uncertainty element per Treas. Reg. §1.41-4(a)(3)(i).",
      ],
      NO: [
        "There was no uncertainty at the outset — the team knew what approach would work and executed it without open technical questions.",
      ],
    },
  },

  // ============================================================
  // Q3: Process of experimentation — alternatives evaluated? (Treas. Reg. §1.41-4(a)(5))
  // ============================================================
  hasExperimentation: {
    software: {
      YES: [
        "The team evaluated multiple technical approaches before arriving at the solution. Specifically, they considered {approach A}, {approach B}, and {approach C}, building and testing each to evaluate performance, maintainability, and fit with existing systems. {Approach B} was selected after iterative benchmarking and refactoring. Git commit history and architecture decision records document the alternatives evaluated.",
        "Iterative development occurred throughout the work. Engineers built proof-of-concept implementations, measured against the target criteria, refactored based on findings, and repeated. {N} significant iterations are documented in the commit history. Failed approaches — including {describe 1-2 failures} — were abandoned in favor of the ultimately-adopted design.",
      ],
      NO: [
        "No alternatives were evaluated. The team implemented a single predetermined approach without iterative testing or comparison against alternatives.",
      ],
    },
    manufacturing: {
      YES: [
        "The engineering team systematically evaluated alternatives. For {parameter/variable}, they tested {option A} vs. {option B} via controlled trial runs, measured results against {yield/quality/throughput/cost}, and selected the better-performing option. {N} total trial runs were conducted, documented in production logs and internal DOE reports.",
        "Multiple prototype or trial configurations were built and tested. The team iterated through {N} design revisions — each revision driven by measured results from the prior iteration. Engineering change notices and test records document the progression.",
      ],
      NO: [
        "The process was executed as originally specified with no alternatives tested or iterations performed.",
      ],
    },
    engineering: {
      YES: [
        "During design development, the engineers evaluated multiple design alternatives — including {scheme A}, {scheme B}, and {scheme C} — against the project performance criteria and code requirements. Analysis memos, calculations, and design review notes document the comparison. The selected design emerged from this alternatives analysis.",
      ],
      NO: [
        "Only a single design was developed with no consideration of alternatives.",
      ],
    },
    food: {
      YES: [
        "The R&D team ran {N} bench trials evaluating alternative {ingredients / ratios / processing parameters / temperatures / times}. Each trial was tasted/tested against the target profile, results were logged, and the next iteration incorporated what was learned. Sensory panel scoresheets and batch records document the experimentation.",
      ],
      NO: [
        "The formulation was produced as specified with no trial variations.",
      ],
    },
    construction: {
      YES: [
        "The team evaluated multiple construction approaches — considering {method A vs. method B}, or {sequencing option 1 vs. 2}, or {temporary works design alternatives} — through mockups, constructability reviews, and field trials. RFIs and submittals capture the evaluation. The selected approach emerged from that analysis.",
      ],
      NO: [
        "Only the baseline approach specified in the contract documents was executed, with no alternatives evaluated.",
      ],
    },
    biotech: {
      YES: [
        "The team designed and executed a structured experimental program. {N} alternatives were evaluated — including {specific alternatives} — through controlled experiments. Lab notebook entries and data reports document the comparisons. Failed approaches are preserved in the record. The final approach emerged from this iterative experimentation.",
      ],
      NO: [
        "The work executed a single pre-specified protocol with no alternatives evaluated.",
      ],
    },
    agriculture: {
      YES: [
        "The team conducted side-by-side trials comparing {treatment A vs. B}, {variety 1 vs. 2 vs. 3}, or {method X vs. Y}. Yield, quality, and performance data were collected over the season and compared. Trial plot records and harvest data document the experimentation.",
      ],
      NO: [
        "A single standard treatment was applied across the operation with no trial comparisons.",
      ],
    },
    generic: {
      YES: [
        "The team evaluated multiple alternatives during the work. Specifically, they considered {approach A} and {approach B} — building, testing, or analyzing each — and selected the option that best met the technical objective. Documented in {specify evidence}. Satisfies process-of-experimentation element per Treas. Reg. §1.41-4(a)(5).",
      ],
      NO: [
        "No alternatives were evaluated. The team executed a single approach without comparison or iteration.",
      ],
    },
  },

  // ============================================================
  // Q4: Technological in nature — engineering, CS, physical/bio sciences?
  // (§41(d)(1)(B)(i); Treas. Reg. §1.41-4(a)(4))
  // ============================================================
  hasTechBasis: {
    software: {
      YES: [
        "The work fundamentally relied on principles of computer science — including {software architecture / algorithms / data structures / distributed systems / databases / networking / machine learning / systems engineering}. The team's engineering staff hold degrees or extensive experience in computer science or software engineering.",
        "Computer science principles governed the technical choices, including {algorithmic complexity / concurrency models / caching strategies / data modeling / security architecture}. The work could not have been performed without application of these technical principles.",
      ],
      NO: [
        "The work was primarily business process / configuration / content editing and did not fundamentally rely on computer science principles.",
      ],
    },
    manufacturing: {
      YES: [
        "The work fundamentally relied on principles of {mechanical / electrical / chemical / industrial / materials} engineering. Decisions on {tolerances, materials selection, process parameters, tooling design, thermal management, mechanical design} were driven by engineering analysis and calculation. Staff engineers hold relevant technical degrees.",
      ],
      NO: [
        "The work was primarily operational or business-focused and did not rely on engineering or scientific principles.",
      ],
    },
    engineering: {
      YES: [
        "The work was performed by licensed professional engineers and engineers-in-training applying principles of {structural / civil / mechanical / electrical} engineering. Calculations, code compliance analysis, and engineering judgment drove all design decisions.",
      ],
      NO: [
        "The work was administrative or drafting without substantive application of engineering principles.",
      ],
    },
    food: {
      YES: [
        "The work relied on principles of {food science / chemistry / biology / chemical engineering}, including ingredient functionality, emulsion stability, microbial safety, {shelf-life chemistry, thermal processing, water activity}. Bench work was conducted by staff with food science or related degrees.",
      ],
      NO: [
        "The work was marketing or packaging design without application of food science principles.",
      ],
    },
    construction: {
      YES: [
        "The work relied on principles of {structural / mechanical / electrical / civil} engineering and construction science. Decisions about means, methods, temporary works, and assembly sequencing were driven by engineering analysis performed by in-house or consulting engineers. Calculations and submittals document the technical basis.",
      ],
      NO: [
        "The work was purely project management or administrative and did not involve engineering analysis.",
      ],
    },
    biotech: {
      YES: [
        "The work fundamentally relied on principles of {biology / chemistry / biochemistry / molecular biology / pharmacology / biomedical engineering}. All experimental work was performed by staff with relevant scientific degrees (PhD, MS, BS) in appropriate disciplines.",
      ],
      NO: [
        "The work was regulatory, administrative, or commercial without substantive scientific research.",
      ],
    },
    agriculture: {
      YES: [
        "The work relied on principles of {agronomy / soil science / plant biology / agricultural engineering / ag data science}. Trial design, analysis, and equipment development were performed by or in consultation with qualified scientists or engineers.",
      ],
      NO: [
        "The work was routine farm management without application of scientific or engineering principles.",
      ],
    },
    generic: {
      YES: [
        "The work fundamentally relied on principles of {engineering / computer science / physical science / biological science — specify}. Technical decisions were driven by application of these principles, not by business or aesthetic considerations. Staff involved have relevant technical training. Satisfies technological-in-nature element per §41(d)(1)(B)(i) and Treas. Reg. §1.41-4(a)(4).",
      ],
      NO: [
        "The work was primarily business, aesthetic, administrative, or social-science-based — not fundamentally reliant on engineering or hard-science principles.",
      ],
    },
  },

  // ============================================================
  // Q5: Contemporaneous records — tickets, design docs, test reports, etc.
  // ============================================================
  hasDocumentation: {
    software: {
      YES: [
        "Contemporaneous records include: (1) {Jira / Linear / GitHub Issues} project tracking with {N} tickets spanning the study year, (2) Git commit history with {N} commits, (3) design documents and RFCs in {Confluence / Notion / shared drive}, (4) release notes and changelogs, (5) code review records. Records were preserved throughout the year and were not created specifically for this study.",
      ],
      NO: [
        "Contemporaneous documentation is limited or inconsistent. Many projects lack design documents or ticket trails sufficient to substantiate the technical narrative.",
      ],
    },
    manufacturing: {
      YES: [
        "Contemporaneous records include: (1) engineering change notices (ECNs) / drawing revisions, (2) production trial run logs, (3) test and inspection reports, (4) internal engineering memos, (5) meeting minutes from engineering reviews. All records are preserved in the company's {ERP / PLM / document management} system.",
      ],
      NO: [
        "Contemporaneous records are sparse. Most project decisions appear to have been undocumented or captured only informally.",
      ],
    },
    engineering: {
      YES: [
        "Contemporaneous records include: (1) calculation packages per project, (2) design review memoranda, (3) drawing revision histories, (4) RFI/submittal correspondence, (5) internal design comment markups. All records are preserved in the firm's project file system.",
      ],
      NO: [
        "Project documentation is limited to final drawings. Design rationale, alternatives considered, and intermediate analyses are not consistently captured.",
      ],
    },
    food: {
      YES: [
        "Contemporaneous records include: (1) R&D batch records, (2) bench trial logs, (3) sensory panel scoresheets, (4) shelf-life test results, (5) nutritional / analytical lab reports. Records are preserved in lab notebooks and the company's document system.",
      ],
      NO: [
        "Documentation is inconsistent. Batch records exist but trial rationale, failed trials, and iterative decision-making are not consistently captured.",
      ],
    },
    construction: {
      YES: [
        "Contemporaneous records include: (1) RFIs and submittals, (2) mockup records and photos, (3) daily reports, (4) constructability review minutes, (5) field engineer notes, (6) change order rationale memos. Records are preserved in the project management system (Procore / PlanGrid / similar).",
      ],
      NO: [
        "Documentation is standard for construction but the technical rationale for means-and-methods decisions is not always captured in writing.",
      ],
    },
    biotech: {
      YES: [
        "Contemporaneous records include: (1) bound or electronic lab notebooks, (2) protocol documents, (3) SOP revisions, (4) internal research reports, (5) raw instrument data files. Records meet standard GLP / research record-keeping practices.",
      ],
      NO: [
        "Record-keeping is partial. Some experiments are well-documented; others lack sufficient contemporaneous detail.",
      ],
    },
    agriculture: {
      YES: [
        "Contemporaneous records include: (1) trial plot maps and records, (2) planting, application, and harvest logs, (3) yield and quality data, (4) weather and soil data, (5) end-of-season trial reports. Records are preserved in farm management software and internal trial reports.",
      ],
      NO: [
        "Field records are limited. Trial design and results are not consistently captured in a form that would support technical substantiation.",
      ],
    },
    generic: {
      YES: [
        "Contemporaneous records supporting the work include {list specific records — e.g., project lists, design documents, test reports, lab notes, tickets, meeting minutes}. Records were created during the normal course of work, not generated specifically for this study, and are preserved in {specify where}.",
      ],
      NO: [
        "Contemporaneous documentation is minimal. Most of the technical narrative would rely on after-the-fact interviews rather than records created during the work.",
      ],
    },
  },
};

// ============================================================
// CLIENT REQUEST TEMPLATES (built from SOP IDR checklist)
// ============================================================
const REQUEST_TEMPLATES = {
  round1_core: {
    name: "Round 1 — Core IDR Package",
    description: "Initial document request covering technical, payroll, GL, vendor, and prior-year support",
    items: [
      { category: "Technical", text: "Full list of development, engineering, software, testing, and process-improvement projects for the tax year", cite: "41(d)(1)" },
      { category: "Technical", text: "Brief description of each project, including business objective and technical uncertainty", cite: "41(d)(1)" },
      { category: "Technical", text: "Supporting records: design specs, test plans/results, Jira or Asana tickets, sprint reports, release notes, prototype logs, lab notes", cite: "1.41-4" },
      { category: "Wages", text: "Payroll register by employee and legal entity for the tax year", cite: "41(b)(2)" },
      { category: "Wages", text: "Employee roster with job title and department", cite: "1.41-2(c)" },
      { category: "Wages", text: "Organization chart showing reporting lines (for direct supervision analysis)", cite: "1.41-2(c)" },
      { category: "Wages", text: "Job descriptions for technical roles", cite: "1.41-2(c)" },
      { category: "Wages", text: "Time-tracking reports or project-coded labor reports (if available)", cite: "1.41-2(c)" },
      { category: "Supplies", text: "Detailed GL by month for likely research-supply accounts", cite: "41(b)(2)" },
      { category: "Supplies", text: "AP detail and invoices for prototype, testing, and lab materials", cite: "41(b)(2)" },
      { category: "Contracts", text: "List of all outside engineering, software, development, and lab vendors", cite: "1.41-2(e)" },
      { category: "Contracts", text: "Executed MSAs, SOWs, and amendments for contract research vendors", cite: "1.41-2(e)" },
      { category: "Contracts", text: "Contractor invoices and proof of payment", cite: "1.41-2(e)" },
      { category: "Tax", text: "Prior 3 years of QRE schedules and any prior R&D studies", cite: "41(c)(4)" },
      { category: "Tax", text: "Prior 4 years of gross receipts", cite: "41(a)(1)" },
      { category: "Tax", text: "Prior Forms 6765 filed", cite: "41(a)(1)" },
      { category: "Corporate", text: "Legal entity structure and ownership chart", cite: "41(f)(1)" },
      { category: "Corporate", text: "Intercompany agreements (if multiple related entities)", cite: "1.41-6" },
    ],
  },
  wages_deep: {
    name: "Wage Support — Deep Dive",
    description: "Detailed wage QRE documentation request",
    items: [
      { category: "Wages", text: "Bonus/incentive compensation detail by employee", cite: "41(b)(2)" },
      { category: "Wages", text: "Manager-approved time allocations for mixed-role employees", cite: "1.41-2(c)" },
      { category: "Wages", text: "Project-assignment reports by employee", cite: "1.41-2(c)" },
      { category: "Wages", text: "Interview availability for key technical personnel", cite: "1.41-4" },
    ],
  },
  contract_research: {
    name: "Contract Research — Full Package",
    description: "Documentation to validate 65% contract research inclusion",
    items: [
      { category: "Contracts", text: "Full executed master services agreement for each vendor", cite: "1.41-2(e)" },
      { category: "Contracts", text: "All statements of work, change orders, and amendments", cite: "1.41-2(e)" },
      { category: "Contracts", text: "Deliverables, test reports, or technical output received from each vendor", cite: "1.41-2(e)" },
      { category: "Contracts", text: "Contract clauses showing rights to results and allocation of risk", cite: "1.41-2(e)" },
      { category: "Contracts", text: "Evidence that agreement was executed before work began", cite: "1.41-2(e)" },
    ],
  },
  qsb_payroll: {
    name: "QSB / Payroll Election Support",
    description: "For taxpayers potentially eligible for the §41(h) payroll tax election",
    items: [
      { category: "Tax", text: "Annual gross receipts for every year since formation", cite: "41(h)(3)" },
      { category: "Tax", text: "First year with any gross receipts", cite: "41(h)(3)" },
      { category: "Corporate", text: "Formation documents and predecessor/reorganization history", cite: "41(h)(3)" },
      { category: "Tax", text: "Prior payroll tax credit elections (if any)", cite: "41(h)(1)" },
    ],
  },
  state_credit: {
    name: "State Credit Module",
    description: "Documentation to support parallel state R&D credits",
    items: [
      { category: "State", text: "State apportionment workpapers", cite: null },
      { category: "State", text: "Project location data (which state each project was performed in)", cite: null },
      { category: "State", text: "State-specific QRE schedules for prior years", cite: null },
      { category: "State", text: "Any state R&D credit program approvals (NY Excelsior, etc.)", cite: null },
    ],
  },
};

// ============================================================
// BADGES + HELPERS
// ============================================================
function CiteBadge({ id, onClick }) {
  const c = CITATIONS[id];
  if (!c) return null;
  return (
    <button
      onClick={() => onClick && onClick(id)}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-stone-300 bg-stone-50 text-stone-700 text-[10px] font-mono tracking-tight hover:bg-sky-50 hover:border-sky-500 transition-colors"
      title={c.text}
    >
      <BookOpen size={9} strokeWidth={2.5} />
      {c.label}
    </button>
  );
}

function HumanFlag({ children }) {
  return (
    <div className="flex gap-2 p-3 border-l-2 border-rose-500 bg-rose-50/50 rounded-r-sm">
      <Flag size={14} className="text-rose-600 shrink-0 mt-0.5" />
      <div className="text-xs text-rose-900 leading-relaxed">
        <span className="font-semibold uppercase tracking-wider text-[10px] text-rose-700 block mb-1">Human Required</span>
        {children}
      </div>
    </div>
  );
}

function AIBadge({ children }) {
  return (
    <div className="flex gap-2 p-3 border-l-2 border-emerald-500 bg-emerald-50/50 rounded-r-sm">
      <CheckCircle2 size={14} className="text-emerald-600 shrink-0 mt-0.5" />
      <div className="text-xs text-emerald-900 leading-relaxed">
        <span className="font-semibold uppercase tracking-wider text-[10px] text-emerald-700 block mb-1">AI-Computable</span>
        {children}
      </div>
    </div>
  );
}

function fileIconFor(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["xlsx", "xls", "csv", "tsv"].includes(ext)) return { icon: FileSpreadsheet, color: "text-emerald-600" };
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return { icon: FileImage, color: "text-violet-600" };
  if (["pdf"].includes(ext)) return { icon: FileText, color: "text-rose-600" };
  if (["doc", "docx"].includes(ext)) return { icon: FileText, color: "text-blue-600" };
  return { icon: File, color: "text-stone-500" };
}

function humanFileSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function fmt(n) {
  if (!isFinite(n) || n === 0) return "0";
  return Math.round(n).toLocaleString("en-US");
}

// ============================================================
// MAIN APP
// ============================================================
export default function RDStudyTool() {
  const [phase, setPhase] = useState("scope");
  const [activeCite, setActiveCite] = useState(null);
  const [saveState, setSaveState] = useState("idle");

  const [scope, setScope] = useState({
    clientName: "", taxYear: "2025", additionalTaxYears: [], entityType: "C-corp", ein: "",
    jurisdiction: "", studyType: "current-year", controlledGroup: false,
    priorStudy: false, grossReceiptsCurrent: "", firstRevenueYear: "",
    clientContactName: "", clientContactEmail: "",
  });

  const [screen, setScreen] = useState({
    hasDevelopment: null, hasUncertainty: null, hasExperimentation: null,
    hasTechBasis: null, hasDocumentation: null,
    notes: {
      hasDevelopment: "", hasUncertainty: "", hasExperimentation: "",
      hasTechBasis: "", hasDocumentation: "",
    },
    exclusions: [],
    exclusionNotes: {},
    industry: "generic",
  });

  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [contractors, setContractors] = useState([]);

  const [compute, setCompute] = useState({
    method: "ASC",
    chosenMethod: "ASC",
    priorQRE: [{ year: "2024", qre: "" }, { year: "2023", qre: "" }, { year: "2022", qre: "" }],
    fixedBasePct: "", avgGrossReceipts: "",
    reducedCreditElection: true, payrollTaxElection: false,
    methodSelectionNotes: "",
  });

  const [documents, setDocuments] = useState([]);
  const [requestLists, setRequestLists] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);

  const [engagement, setEngagement] = useState({
    // Firm info
    firmName: "Linked Accounting",
    firmAddress: "",
    firmPhone: "",
    firmEmail: "",
    partnerName: "",
    partnerTitle: "Partner",
    // Fees
    feeStructure: "fixed", // "fixed" | "hourly" | "contingent" | "hybrid"
    fixedFee: "",
    hourlyRate: "",
    estimatedHours: "",
    contingentPct: "",
    contingentCap: "",
    expensesBilled: "actual", // "actual" | "flat" | "included"
    flatExpense: "",
    retainerAmount: "",
    retainerDue: "upon-signing",
    billingSchedule: "monthly", // "upon-completion" | "monthly" | "milestone"
    paymentTerms: "Net 30",
    // Lifecycle
    lifecycle: {
      proposal: { status: "not_started", date: "", notes: "" },
      letterDrafted: { status: "not_started", date: "", notes: "" },
      letterSent: { status: "not_started", date: "", notes: "" },
      letterSigned: { status: "not_started", date: "", signedBy: "", signedDate: "", notes: "" },
      retainerReceived: { status: "not_started", date: "", amount: "", notes: "" },
      studyStarted: { status: "not_started", date: "", notes: "" },
      inProgress: { status: "not_started", date: "", notes: "" },
      completed: { status: "not_started", date: "", notes: "" },
      delivered: { status: "not_started", date: "", notes: "" },
    },
    // Time tracking
    timeEntries: [], // [{ id, date, staff, hours, description, billable }]
    // Stall detection
    stallThresholdDays: 14,
    // Engagement letter custom language
    specialTerms: "",
    scopeExclusions: "",
    // Client Impact analysis
    impactFedRate: "21",      // Federal corporate rate default
    impactStateRate: "",       // State income tax rate
    impactStateName: "",       // State name for labeling
    impactStateCredit: "",     // Separate state R&D credit (if applicable)
    impactCustomNote: "",      // Custom note for client summary
    impactScenario: "corp",    // "corp" | "passthrough" | "custom"
    impactCustomRate: "",      // Custom blended rate for passthrough owners
    impactYearsOfBenefit: "1", // Years over which to show benefit
  });

  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await window.storage.get("rd_study_state_v2");
        if (r && r.value) {
          const s = JSON.parse(r.value);
          if (s.scope) setScope(s.scope);
          if (s.screen) setScreen(s.screen);
          if (s.projects) setProjects(s.projects);
          if (s.employees) setEmployees(s.employees);
          if (s.supplies) setSupplies(s.supplies);
          if (s.contractors) setContractors(s.contractors);
          if (s.compute) setCompute(s.compute);
          if (s.documents) setDocuments(s.documents);
          if (s.requestLists) setRequestLists(s.requestLists);
          if (s.engagement) setEngagement(s.engagement);
          if (s.feedback) setFeedback(s.feedback);
          if (s.phase) setPhase(s.phase);
        }
      } catch (e) { /* no saved state */ }
      setHasLoaded(true);
    })();
  }, []);

  // Load firm-wide feedback separately (shared across all engagements using the tool)
  useEffect(() => {
    (async () => {
      try {
        const r = await window.storage.get("rd_tool_feedback_shared", true);
        if (r && r.value) {
          const fb = JSON.parse(r.value);
          if (Array.isArray(fb)) setFeedback(fb);
        }
      } catch (e) { /* no shared feedback yet */ }
    })();
  }, []);

  const saveAll = async () => {
    setSaveState("saving");
    try {
      await window.storage.set("rd_study_state_v2", JSON.stringify({
        scope, screen, projects, employees, supplies, contractors,
        compute, documents, requestLists, engagement, feedback, phase
      }));
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      console.error(e);
      setSaveState("idle");
      alert("Save failed — file attachments may exceed storage limit. Try removing large files.");
    }
  };

  // Auto-save: debounce 1.5s after the last change. Skips until initial load
  // finishes so we don't overwrite Supabase with the empty default state on
  // first mount.
  const saveAllRef = useRef(saveAll);
  saveAllRef.current = saveAll;
  useEffect(() => {
    if (!hasLoaded) return;
    const timer = setTimeout(() => {
      saveAllRef.current();
    }, 1500);
    return () => clearTimeout(timer);
  }, [hasLoaded, scope, screen, projects, employees, supplies, contractors, compute, documents, requestLists, engagement, phase]);

  // Belt-and-suspenders: also flush a save when the tab is hidden (closed,
  // backgrounded, or navigated away) so in-flight typing isn't lost.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "hidden" && hasLoaded) {
        saveAllRef.current();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [hasLoaded]);

  const resetAll = async () => {
    if (!confirm("Reset all study data? This cannot be undone.")) return;
    try { await window.storage.delete("rd_study_state_v2"); } catch (e) {}
    window.location.reload();
  };

  const wageQRE = useMemo(() => employees.reduce((s, e) => s + (parseFloat(e.wages) || 0) * ((parseFloat(e.qualifiedPct) || 0) / 100), 0), [employees]);
  const supplyQRE = useMemo(() => supplies.reduce((s, x) => x.qualified ? s + (parseFloat(x.amount) || 0) : s, 0), [supplies]);
  const contractQRE = useMemo(() => contractors.reduce((s, c) => {
    if (!c.qualified) return s;
    // If invoices are broken out, sum each invoice at its own qualified %
    if (Array.isArray(c.invoices) && c.invoices.length > 0) {
      const invTotal = c.invoices.reduce((iSum, inv) => {
        const amt = parseFloat(inv.amount) || 0;
        const pct = Math.min(100, Math.max(0, parseFloat(inv.qualifiedPct) || 0));
        return iSum + (amt * pct / 100);
      }, 0);
      return s + invTotal * 0.65;
    }
    // Fall back to the single-amount model
    return s + (parseFloat(c.amount) || 0) * 0.65;
  }, 0), [contractors]);
  const totalQRE = wageQRE + supplyQRE + contractQRE;

  const ascResult = useMemo(() => {
    const prior = compute.priorQRE.map(p => parseFloat(p.qre) || 0);
    const anyZero = prior.some(v => v === 0);
    const allZero = prior.every(v => v === 0);
    const avg = prior.reduce((a, b) => a + b, 0) / 3;
    const base = avg * 0.5;
    if (allZero || anyZero) {
      const gross = totalQRE * 0.06;
      return { method: "ASC Fallback (6%)", cite: "41(c)(4)", gross, reduced: gross * 0.79, note: "No QREs in at least one prior year — 6% fallback applies per §41(c)(4)(B)(ii)." };
    }
    const excess = Math.max(0, totalQRE - base);
    const gross = excess * 0.14;
    return { method: "ASC Standard (14%)", cite: "41(c)(4)", avg, base, excess, gross, reduced: gross * 0.79 };
  }, [totalQRE, compute.priorQRE]);

  const regularResult = useMemo(() => {
    const fbp = Math.min(parseFloat(compute.fixedBasePct) || 0, 16) / 100;
    const avgGR = parseFloat(compute.avgGrossReceipts) || 0;
    const computedBase = fbp * avgGR;
    const floor = totalQRE * 0.5;
    const baseAmount = Math.max(computedBase, floor);
    const excess = Math.max(0, totalQRE - baseAmount);
    const gross = excess * 0.20;
    return { method: "Regular Credit (20%)", cite: "41(a)(1)", fbp: fbp * 100, avgGR, computedBase, floor, baseAmount, excess, gross, reduced: gross * 0.79 };
  }, [totalQRE, compute.fixedBasePct, compute.avgGrossReceipts]);

  const chosenMethod = compute.chosenMethod || compute.method;
  const selectedResult = chosenMethod === "REGULAR" ? regularResult : ascResult;
  const finalCredit = compute.reducedCreditElection ? selectedResult.reduced : selectedResult.gross;

  const qsbEligible = useMemo(() => {
    const gr = parseFloat(scope.grossReceiptsCurrent) || 0;
    const firstYear = parseInt(scope.firstRevenueYear) || 0;
    const studyYear = parseInt(scope.taxYear) || 0;
    const within5 = firstYear && (studyYear - firstYear) < 5;
    return gr > 0 && gr < 5000000 && within5;
  }, [scope]);

  const payrollCreditAmount = Math.min(finalCredit, 500000);

  const phaseIndex = PHASES.findIndex(p => p.id === phase);
  const goNext = () => { if (phaseIndex < PHASES.length - 1) setPhase(PHASES[phaseIndex + 1].id); };
  const goPrev = () => { if (phaseIndex > 0) setPhase(PHASES[phaseIndex - 1].id); };

  // Document helpers
  const addDocuments = async (files, linkedTo = {}, category = "General") => {
    const newDocs = [];
    for (const file of files) {
      const dataUrl = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      newDocs.push({
        id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        type: file.type,
        category,
        tags: [],
        linkedTo,
        dataUrl,
        notes: "",
        uploadedAt: new Date().toISOString(),
      });
    }
    setDocuments(prev => [...prev, ...newDocs]);
    return newDocs;
  };

  const deleteDocument = (id) => setDocuments(docs => docs.filter(d => d.id !== id));
  const updateDocument = (id, patch) => setDocuments(docs => docs.map(d => d.id === id ? { ...d, ...patch } : d));

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Outfit', system-ui, sans-serif", backgroundColor: "#F1F7FC" }}>
      

      <header className="border-b border-stone-300 bg-white sticky top-0 z-30 no-print">
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Actual Linked Accounting logo */}
            <img src="/linked_accounting_logo.png" alt="Linked Accounting LLP" className="h-12 w-auto shrink-0" />
            <div className="border-l border-stone-200 pl-4 ml-1">
              <div className="sans text-[10px] text-sky-700 tracking-[0.2em] uppercase font-bold">R&D Tax Credit Workbench</div>
              <TaxYearEditor scope={scope} setScope={setScope} engagement={engagement} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 px-3 py-1.5 bg-sky-50 border border-sky-200 rounded-sm">
              <span className="mono text-[10px] uppercase tracking-widest text-sky-700">Docs</span>
              <span className="mono text-sm font-bold text-stone-900">{documents.length}</span>
            </div>
            <button onClick={saveAll} className="sans text-xs font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-sky-50 hover:border-sky-400 rounded-sm flex items-center gap-2 transition-colors">
              {saveState === "saving" ? <RefreshCw size={13} className="animate-spin" /> :
               saveState === "saved" ? <CheckCircle2 size={13} className="text-emerald-600" /> :
               <Save size={13} />}
              {saveState === "saved" ? "Saved" : "Save"}
            </button>
            <button
              onClick={() => setShowFeedback(true)}
              className="relative sans text-xs font-medium px-3 py-2 border border-sky-400 bg-sky-50 hover:bg-sky-100 rounded-sm flex items-center gap-2 transition-colors text-sky-800"
              title="Prototype feedback and improvement notes"
            >
              <MessageSquare size={13} />
              Feedback
              {feedback.filter(f => f.status === "open").length > 0 && (
                <span className="mono text-[9px] font-bold bg-sky-600 text-white px-1.5 py-0.5 rounded-full">
                  {feedback.filter(f => f.status === "open").length}
                </span>
              )}
            </button>
            <button onClick={resetAll} className="sans text-xs font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-rose-50 hover:border-rose-300 rounded-sm transition-colors">Reset</button>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-8 pb-4">
          <div className="flex items-stretch gap-0 border border-stone-300 bg-white rounded-sm overflow-hidden overflow-x-auto">
            {PHASES.map((p, i) => {
              const Icon = p.icon;
              const isActive = p.id === phase;
              const isDone = i < phaseIndex;
              return (
                <button
                  key={p.id}
                  onClick={() => setPhase(p.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 border-r border-stone-200 last:border-r-0 transition-colors flex-shrink-0 ${
                    isActive ? "bg-stone-900 text-sky-400" :
                    isDone ? "bg-emerald-50 text-emerald-800 hover:bg-emerald-100" :
                    "bg-white text-stone-500 hover:bg-stone-50"
                  }`}
                >
                  <span className={`mono text-[10px] font-bold ${isActive ? "text-sky-400" : isDone ? "text-emerald-600" : "text-stone-400"}`}>
                    {p.short}
                  </span>
                  <Icon size={13} strokeWidth={2} />
                  <span className="sans text-[11px] font-medium whitespace-nowrap">{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* ENGAGEMENT PROGRESS BANNER — persistent across all phases */}
      <EngagementProgressBanner engagement={engagement} scope={scope} setPhase={setPhase} />

      <div className="max-w-[1400px] mx-auto px-8 py-8 grid grid-cols-12 gap-6">
        <main className="col-span-12 lg:col-span-8">
          <div className="bg-white border border-stone-300 rounded-sm shadow-sm">
            {phase === "scope" && <ScopePhase scope={scope} setScope={setScope} />}
            {phase === "engagement" && <EngagementPhase engagement={engagement} setEngagement={setEngagement} scope={scope} finalCredit={finalCredit} requestLists={requestLists} onCite={setActiveCite} />}
            {phase === "screen" && <ScreenPhase screen={screen} setScreen={setScreen} documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} onCite={setActiveCite} />}
            {phase === "tree" && <DecisionTreePhase onCite={setActiveCite} />}
            {phase === "requests" && <RequestsPhase requestLists={requestLists} setRequestLists={setRequestLists} scope={scope} documents={documents} addDocuments={addDocuments} onCite={setActiveCite} />}
            {phase === "documents" && <DocumentVaultPhase documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} updateDocument={updateDocument} projects={projects} employees={employees} supplies={supplies} contractors={contractors} />}
            {phase === "projects" && <ProjectsPhase projects={projects} setProjects={setProjects} documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} onCite={setActiveCite} />}
            {phase === "wages" && <WagesPhase employees={employees} setEmployees={setEmployees} wageQRE={wageQRE} documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} onCite={setActiveCite} />}
            {phase === "supplies" && <SuppliesPhase supplies={supplies} setSupplies={setSupplies} supplyQRE={supplyQRE} documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} onCite={setActiveCite} />}
            {phase === "contracts" && <ContractsPhase contractors={contractors} setContractors={setContractors} contractQRE={contractQRE} documents={documents} addDocuments={addDocuments} deleteDocument={deleteDocument} onCite={setActiveCite} />}
            {phase === "compute" && <ComputePhase compute={compute} setCompute={setCompute} totalQRE={totalQRE} wageQRE={wageQRE} supplyQRE={supplyQRE} contractQRE={contractQRE} ascResult={ascResult} regularResult={regularResult} scope={scope} qsbEligible={qsbEligible} onCite={setActiveCite} />}
            {phase === "elections" && <ElectionsPhase compute={compute} setCompute={setCompute} scope={scope} qsbEligible={qsbEligible} finalCredit={finalCredit} payrollCreditAmount={payrollCreditAmount} onCite={setActiveCite} />}
            {phase === "impact" && <ImpactPhase scope={scope} engagement={engagement} setEngagement={setEngagement} compute={compute} totalQRE={totalQRE} finalCredit={finalCredit} onCite={setActiveCite} />}
            {phase === "report" && <ReportPhase scope={scope} screen={screen} projects={projects} employees={employees} supplies={supplies} contractors={contractors} compute={compute} totalQRE={totalQRE} wageQRE={wageQRE} supplyQRE={supplyQRE} contractQRE={contractQRE} finalCredit={finalCredit} selectedResult={selectedResult} ascResult={ascResult} regularResult={regularResult} documents={documents} />}
          </div>

          <div className="flex items-center justify-between mt-6 no-print">
            <button onClick={goPrev} disabled={phaseIndex === 0} className="sans text-sm font-medium px-4 py-2.5 border border-stone-300 bg-white hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 rounded-sm transition-colors">
              <ChevronLeft size={15} /> Previous
            </button>
            <div className="mono text-[11px] text-stone-500 uppercase tracking-widest">Phase {phaseIndex + 1} of {PHASES.length}</div>
            <button onClick={goNext} disabled={phaseIndex === PHASES.length - 1} className="sans text-sm font-semibold px-4 py-2.5 bg-stone-900 text-sky-400 hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 rounded-sm transition-colors">
              Continue <ChevronRight size={15} />
            </button>
          </div>
        </main>

        <aside className="col-span-12 lg:col-span-4 space-y-5">
          <div className="bg-stone-900 text-stone-100 border border-stone-900 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="serif text-base font-semibold text-sky-400">Live Credit Dashboard</h3>
              <span className="mono text-[9px] uppercase tracking-widest text-stone-400">Realtime</span>
            </div>
            <div className="space-y-3">
              <DashRow label="Qualified wages" value={wageQRE} cite="1.41-2(c)" onCite={setActiveCite} />
              <DashRow label="Qualified supplies" value={supplyQRE} cite="41(b)(2)" onCite={setActiveCite} />
              <DashRow label="Contract research (65%)" value={contractQRE} cite="1.41-2(e)" onCite={setActiveCite} />
              <div className="border-t border-stone-700 pt-3">
                <DashRow label="Total QRE" value={totalQRE} bold />
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-stone-700">
              <div className="flex items-center justify-between mb-1">
                <span className="sans text-[10px] uppercase tracking-widest text-stone-400">
                  {chosenMethod === "REGULAR" ? "Regular" : "ASC"} · {compute.reducedCreditElection ? "§280C Reduced" : "Gross"}
                </span>
              </div>
              <div className="serif text-3xl font-bold text-sky-400 num-tabular tracking-tight">${fmt(finalCredit)}</div>
              <div className="mono text-[10px] text-stone-400 mt-1">{selectedResult.method}</div>
            </div>
          </div>

          <div className="bg-white border border-stone-300 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="serif text-sm font-semibold text-stone-900">Evidence Vault</h3>
              <Paperclip size={13} className="text-stone-500" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <MiniStat label="Total files" count={documents.length} />
              <MiniStat label="Unlinked" count={documents.filter(d => !d.linkedTo || Object.keys(d.linkedTo).length === 0).length} />
            </div>
            <button onClick={() => setPhase("documents")} className="mt-3 w-full sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-stone-50 hover:bg-stone-100 rounded-sm">
              Open vault →
            </button>
          </div>

          <div className="bg-white border border-stone-300 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="serif text-sm font-semibold text-stone-900">Client Requests</h3>
              <Inbox size={13} className="text-stone-500" />
            </div>
            {requestLists.length === 0 ? (
              <p className="sans text-xs text-stone-500 italic">No request lists yet.</p>
            ) : (
              <div className="space-y-1.5">
                {requestLists.slice(0, 3).map(r => {
                  const done = r.items.filter(i => i.status === "received").length;
                  return (
                    <div key={r.id} className="flex items-center justify-between text-xs">
                      <span className="sans text-stone-800 truncate pr-2">{r.name}</span>
                      <span className="mono text-stone-500">{done}/{r.items.length}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={() => setPhase("requests")} className="mt-3 w-full sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-stone-50 hover:bg-stone-100 rounded-sm">
              Manage requests →
            </button>
          </div>

          <div className="bg-white border border-stone-300 rounded-sm">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
              <BookOpen size={14} className="text-stone-700" />
              <h3 className="serif text-sm font-semibold text-stone-900">Authority Reference</h3>
            </div>
            <div className="p-4">
              {activeCite && CITATIONS[activeCite] ? (
                <div className="space-y-2">
                  <div className="mono text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-1 rounded-sm inline-block">
                    {CITATIONS[activeCite].label}
                  </div>
                  <p className="sans text-xs text-stone-700 leading-relaxed">{CITATIONS[activeCite].text}</p>
                </div>
              ) : (
                <p className="sans text-xs text-stone-500 italic leading-relaxed">Click any citation badge to see the underlying tax code or regulation.</p>
              )}
            </div>
            <div className="border-t border-stone-200 px-4 py-3">
              <div className="sans text-[9px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Primary Authorities</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(CITATIONS).map(key => (
                  <button key={key} onClick={() => setActiveCite(key)} className={`mono text-[9px] px-1.5 py-1 border rounded-sm transition-colors ${
                    activeCite === key ? "border-sky-500 bg-sky-100 text-sky-900" : "border-stone-200 bg-stone-50 text-stone-600 hover:border-sky-500 hover:bg-sky-50"
                  }`}>
                    §{key}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <footer className="border-t border-stone-300 bg-stone-50 mt-12 py-6 no-print">
        <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-sky-500 rounded-full"></div>
            <p className="sans text-[11px] text-stone-500">Internal Linked Accounting staff tool. Not a substitute for professional tax review. All legal qualification decisions require human sign-off.</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="mono text-[10px] text-stone-400 uppercase tracking-widest" title="Regulatory basis">{REG_LAST_UPDATED}</p>
            <p className="mono text-[10px] text-stone-400 uppercase tracking-widest">Linked Accounting LLP · TY{scope.taxYear}{scope.additionalTaxYears && scope.additionalTaxYears.length > 0 ? ` + ${scope.additionalTaxYears.length}` : ""}</p>
          </div>
        </div>
      </footer>

      {showFeedback && <FeedbackModal feedback={feedback} setFeedback={setFeedback} onClose={() => setShowFeedback(false)} phase={phase} />}
    </div>
  );
}

function DashRow({ label, value, bold, cite, onCite }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className={`sans text-xs ${bold ? "font-semibold text-sky-400" : "text-stone-300"}`}>{label}</span>
        {cite && <button onClick={() => onCite(cite)} className="mono text-[8px] text-stone-500 hover:text-sky-500 uppercase">§</button>}
      </div>
      <span className={`mono num-tabular ${bold ? "text-lg font-bold text-sky-400" : "text-sm text-stone-100"}`}>${fmt(value)}</span>
    </div>
  );
}

function MiniStat({ label, count }) {
  return (
    <div className="p-2 bg-stone-50 border border-stone-200 rounded-sm">
      <div className="sans text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      <div className="mono text-lg font-bold text-stone-900 num-tabular">{count}</div>
    </div>
  );
}

// ============================================================
// TAX YEAR EDITOR — editable chips at the top of the app
// ============================================================
function TaxYearEditor({ scope, setScope, engagement }) {
  const [editing, setEditing] = useState(false);
  const [newYear, setNewYear] = useState("");
  const additional = scope.additionalTaxYears || [];

  const primaryYear = scope.taxYear || "—";
  const allYears = [primaryYear, ...additional].filter(y => y && y !== "—");

  const addYear = () => {
    const y = newYear.trim();
    if (!y || !/^\d{4}$/.test(y)) { setNewYear(""); return; }
    if (allYears.includes(y)) { setNewYear(""); return; }
    setScope({ ...scope, additionalTaxYears: [...additional, y] });
    setNewYear("");
  };

  const removeYear = (y) => {
    if (y === primaryYear) {
      // promote first additional year to primary
      const rest = additional.filter(x => x !== y);
      if (rest.length === 0) return; // can't remove the only year
      setScope({ ...scope, taxYear: additional[0], additionalTaxYears: additional.slice(1) });
    } else {
      setScope({ ...scope, additionalTaxYears: additional.filter(x => x !== y) });
    }
  };

  const makePrimary = (y) => {
    if (y === primaryYear) return;
    const oldPrimary = primaryYear;
    const newAdditional = additional.filter(x => x !== y);
    if (oldPrimary && oldPrimary !== "—") newAdditional.unshift(oldPrimary);
    setScope({ ...scope, taxYear: y, additionalTaxYears: newAdditional });
  };

  // Determine engagement stage from lifecycle
  const stage = (() => {
    if (!engagement?.lifecycle) return { label: "Not Started", color: "stone" };
    const lc = engagement.lifecycle;
    if (lc.delivered?.status === "complete") return { label: "Delivered", color: "emerald" };
    if (lc.completed?.status === "complete") return { label: "Ready to Deliver", color: "sky" };
    if (lc.studyStarted?.status === "complete") return { label: "In Progress", color: "sky" };
    if (lc.letterSigned?.status === "complete") return { label: "Signed — Ready to Start", color: "emerald" };
    if (lc.letterSent?.status === "complete") return { label: "Awaiting Signature", color: "amber" };
    if (lc.letterDrafted?.status === "complete") return { label: "Letter Drafted", color: "amber" };
    if (lc.proposal?.status === "complete") return { label: "Proposal Stage", color: "amber" };
    // Check for any stalled step
    const anyStalled = Object.values(lc).some(s => s?.status === "stalled");
    if (anyStalled) return { label: "Stalled", color: "rose" };
    return { label: "Not Started", color: "stone" };
  })();

  const colorMap = {
    stone: "bg-stone-100 text-stone-600 border-stone-300",
    amber: "bg-amber-100 text-amber-800 border-amber-400",
    sky: "bg-sky-100 text-sky-800 border-sky-400",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-400",
    rose: "bg-rose-100 text-rose-800 border-rose-400",
  };

  return (
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      <span className="sans text-[11px] text-stone-600 font-medium">Tax Year{allYears.length > 1 ? "s" : ""}:</span>
      {allYears.map(y => {
        const isPrimary = y === primaryYear;
        return (
          <div key={y} className={`group inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border ${isPrimary ? "bg-stone-900 text-sky-400 border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-sky-400"}`}>
            <button
              onClick={() => makePrimary(y)}
              className={`mono text-xs font-bold num-tabular ${isPrimary ? "cursor-default" : "cursor-pointer"}`}
              title={isPrimary ? "Primary (active) study year" : "Click to make primary"}
            >
              {y}
            </button>
            {isPrimary && <span className="mono text-[8px] uppercase tracking-widest text-sky-300">active</span>}
            {allYears.length > 1 && (
              <button onClick={() => removeYear(y)} className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-rose-500" title="Remove year">
                <X size={10} />
              </button>
            )}
          </div>
        );
      })}
      {editing ? (
        <form onSubmit={e => { e.preventDefault(); addYear(); }} className="inline-flex items-center gap-1">
          <input
            type="number"
            value={newYear}
            onChange={e => setNewYear(e.target.value)}
            placeholder="YYYY"
            autoFocus
            className="w-16 px-1.5 py-0.5 border border-sky-500 rounded-sm mono text-xs num-tabular focus:outline-none"
            onBlur={() => { if (!newYear) setEditing(false); }}
          />
          <button type="submit" className="text-sky-600 hover:text-sky-800"><Plus size={12} /></button>
          <button type="button" onClick={() => { setEditing(false); setNewYear(""); }} className="text-stone-400 hover:text-rose-500"><X size={12} /></button>
        </form>
      ) : (
        <button onClick={() => setEditing(true)} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-dashed border-stone-400 rounded-sm text-stone-500 hover:text-sky-600 hover:border-sky-400 mono text-[10px]">
          <Plus size={10} /> add year
        </button>
      )}
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border mono text-[9px] uppercase tracking-widest font-bold ml-1 ${colorMap[stage.color]}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${stage.color === "emerald" ? "bg-emerald-500" : stage.color === "sky" ? "bg-sky-500" : stage.color === "amber" ? "bg-amber-500" : stage.color === "rose" ? "bg-rose-500 animate-pulse" : "bg-stone-400"}`}></span>
        {stage.label}
      </span>
    </div>
  );
}

// ============================================================
// ENGAGEMENT PROGRESS BANNER — persistent across all phases
// Shows lifecycle progress, current active step, timing metrics,
// and a quick-jump to the engagement phase.
// ============================================================
function EngagementProgressBanner({ engagement, scope, setPhase }) {
  const lc = engagement?.lifecycle || {};

  // The canonical 9-step engagement lifecycle (must match LifecycleOverview)
  const STEPS = [
    { id: "proposal", label: "Proposal Sent", icon: Send },
    { id: "letterDrafted", label: "Letter Drafted", icon: PenLine },
    { id: "letterSent", label: "Letter Sent", icon: Mail },
    { id: "letterSigned", label: "Letter Signed", icon: CheckCheck },
    { id: "retainerReceived", label: "Retainer Received", icon: DollarSign },
    { id: "studyStarted", label: "Study Started", icon: PlayCircle },
    { id: "fieldworkComplete", label: "Fieldwork Done", icon: ClipboardList },
    { id: "completed", label: "Ready to Deliver", icon: FileCheck },
    { id: "delivered", label: "Delivered", icon: CheckCircle2 },
  ];

  const totalSteps = STEPS.length;
  const completedSteps = STEPS.filter(s => lc[s.id]?.status === "complete").length;
  const pct = Math.round((completedSteps / totalSteps) * 100);

  // Find current active step (first not-complete)
  const currentIdx = STEPS.findIndex(s => lc[s.id]?.status !== "complete");
  const current = currentIdx >= 0 ? STEPS[currentIdx] : STEPS[STEPS.length - 1];
  const next = currentIdx >= 0 && currentIdx < STEPS.length - 1 ? STEPS[currentIdx + 1] : null;

  // Check if any step is stalled
  const stalledStep = STEPS.find(s => lc[s.id]?.status === "stalled");

  // Compute timing metrics
  const signedDate = lc.letterSigned?.date ? new Date(lc.letterSigned.date) : null;
  const startedDate = lc.studyStarted?.date ? new Date(lc.studyStarted.date) : null;
  const completedDate = lc.completed?.date ? new Date(lc.completed.date) : null;
  const deliveredDate = lc.delivered?.date ? new Date(lc.delivered.date) : null;

  const daysBetween = (a, b) => {
    if (!a || !b) return null;
    return Math.round((b.getTime() - a.getTime()) / 86400000);
  };

  const signToStart = daysBetween(signedDate, startedDate);
  const daysInProgress = (() => {
    if (startedDate && !completedDate && !deliveredDate) {
      return daysBetween(startedDate, new Date());
    }
    if (startedDate && completedDate) return daysBetween(startedDate, completedDate);
    return null;
  })();
  const totalEngagementDays = lc.proposal?.date && deliveredDate ? daysBetween(new Date(lc.proposal.date), deliveredDate) : null;

  // Stage label derivation
  const stageLabel = (() => {
    if (stalledStep) return "STALLED";
    if (lc.delivered?.status === "complete") return "DELIVERED";
    if (lc.completed?.status === "complete") return "READY TO DELIVER";
    if (lc.studyStarted?.status === "complete") return "STUDY IN PROGRESS";
    if (lc.letterSigned?.status === "complete") return "SIGNED — READY TO START";
    if (lc.letterSent?.status === "complete") return "AWAITING SIGNATURE";
    if (lc.letterDrafted?.status === "complete") return "LETTER DRAFTED";
    if (lc.proposal?.status === "complete") return "PROPOSAL SENT";
    return "NOT STARTED";
  })();

  const stageColor = (() => {
    if (stalledStep) return "rose";
    if (lc.delivered?.status === "complete") return "emerald";
    if (lc.completed?.status === "complete") return "sky";
    if (lc.studyStarted?.status === "complete") return "sky";
    if (lc.letterSigned?.status === "complete") return "emerald";
    if (lc.letterSent?.status === "complete" || lc.letterDrafted?.status === "complete" || lc.proposal?.status === "complete") return "amber";
    return "stone";
  })();

  const stageColorMap = {
    stone: { bg: "bg-stone-100", border: "border-stone-300", text: "text-stone-700", dot: "bg-stone-400", bar: "bg-stone-400", barBg: "bg-stone-200" },
    amber: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", dot: "bg-amber-500", bar: "bg-amber-500", barBg: "bg-amber-100" },
    sky: { bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-800", dot: "bg-sky-500", bar: "bg-sky-500", barBg: "bg-sky-100" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", dot: "bg-emerald-500", bar: "bg-emerald-500", barBg: "bg-emerald-100" },
    rose: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", dot: "bg-rose-500 animate-pulse", bar: "bg-rose-500", barBg: "bg-rose-100" },
  };
  const c = stageColorMap[stageColor];

  const CurrentIcon = current.icon;

  return (
    <div className={`border-b ${c.border} ${c.bg} no-print`}>
      <div className="max-w-[1400px] mx-auto px-8 py-4">
        <div className="grid grid-cols-12 gap-4 items-center">
          {/* LEFT: Client + Stage */}
          <div className="col-span-3">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${c.dot}`}></span>
              <span className={`mono text-[10px] font-bold uppercase tracking-[0.15em] ${c.text}`}>{stageLabel}</span>
            </div>
            <div className="sans text-lg font-bold text-stone-900 leading-tight truncate" title={scope.clientName || "Unnamed Engagement"}>
              {scope.clientName || <span className="text-stone-400 italic">Unnamed Engagement</span>}
            </div>
            <div className="sans text-[11px] text-stone-600 mt-0.5">
              {scope.entityType || "—"} · TY {scope.taxYear}{scope.additionalTaxYears && scope.additionalTaxYears.length > 0 ? `, ${scope.additionalTaxYears.join(", ")}` : ""}
            </div>
          </div>

          {/* CENTER: Progress bar with step dots */}
          <div className="col-span-6">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <CurrentIcon size={14} className={c.text} strokeWidth={2.5} />
                <span className={`sans text-xs font-bold ${c.text}`}>
                  {stalledStep ? `Stalled at: ${stalledStep.label}` : (completedSteps === totalSteps ? "Engagement Complete" : `Current: ${current.label}`)}
                </span>
                {next && !stalledStep && completedSteps < totalSteps && (
                  <span className="sans text-[11px] text-stone-500">· Next: {next.label}</span>
                )}
              </div>
              <div className="mono text-[11px] text-stone-600 font-bold">
                {completedSteps} / {totalSteps} steps · <span className={c.text}>{pct}%</span>
              </div>
            </div>

            {/* Step markers */}
            <div className="flex items-center gap-0.5 relative">
              {STEPS.map((s, i) => {
                const status = lc[s.id]?.status || "not_started";
                const isDone = status === "complete";
                const isStalled = status === "stalled";
                const isInProgress = status === "in_progress";
                const isPending = status === "pending";
                const isNotStarted = status === "not_started";
                const isCurrent = i === currentIdx;
                return (
                  <div key={s.id} className="flex-1 relative group">
                    <div
                      className={`h-2 rounded-sm transition-all ${
                        isDone ? "bg-emerald-500" :
                        isStalled ? "bg-rose-500" :
                        isInProgress ? "bg-sky-500" :
                        isCurrent ? `${c.bar} opacity-60` :
                        isPending ? "bg-stone-300" :
                        isNotStarted ? "bg-white border border-stone-300 border-dashed" :
                        "bg-stone-200"
                      }`}
                      title={`${s.label}: ${STATUS_CONFIG[status]?.label || status}`}
                    ></div>
                    {/* Tooltip on hover */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 bg-stone-900 text-white text-[10px] rounded-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 mono">
                      {s.label}
                      <span className="text-stone-400 ml-1">· {STATUS_CONFIG[status]?.label || status}</span>
                      {lc[s.id]?.date && <span className="text-stone-400 ml-1">· {new Date(lc[s.id].date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT: Timing metrics + jump button */}
          <div className="col-span-3 flex items-center gap-3 justify-end">
            <div className="grid grid-cols-2 gap-2 text-right">
              {signToStart !== null && (
                <div>
                  <div className="sans text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Sign→Start</div>
                  <div className="mono text-sm font-bold text-stone-900">{signToStart}d</div>
                </div>
              )}
              {daysInProgress !== null && (
                <div>
                  <div className="sans text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{completedDate ? "Duration" : "In Progress"}</div>
                  <div className={`mono text-sm font-bold ${daysInProgress > 45 ? "text-rose-700" : "text-stone-900"}`}>{daysInProgress}d</div>
                </div>
              )}
              {totalEngagementDays !== null && (
                <div className="col-span-2">
                  <div className="sans text-[9px] uppercase tracking-widest text-stone-500 font-semibold">Total Cycle</div>
                  <div className="mono text-sm font-bold text-stone-900">{totalEngagementDays}d</div>
                </div>
              )}
              {signToStart === null && daysInProgress === null && totalEngagementDays === null && (
                <div className="col-span-2">
                  <div className="sans text-[10px] text-stone-500 italic">No timing data yet</div>
                </div>
              )}
            </div>
            <button
              onClick={() => setPhase("engagement")}
              className="sans text-xs font-bold px-3 py-2 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm flex items-center gap-1.5 transition-colors shrink-0"
              title="Jump to Engagement phase to update lifecycle"
            >
              Manage <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FEEDBACK MODAL — firm-wide prototype improvement notes
// Saves to shared storage so all Linked Accounting staff see
// the same pool of feedback and improvement ideas.
// ============================================================
const FEEDBACK_CATEGORIES = [
  { id: "bug", label: "Bug / Error", icon: AlertCircle, color: "rose" },
  { id: "enhancement", label: "Enhancement", icon: Plus, color: "sky" },
  { id: "workflow", label: "Workflow", icon: GitBranch, color: "amber" },
  { id: "content", label: "Content / Citation", icon: BookOpen, color: "stone" },
  { id: "design", label: "Design / UX", icon: Pencil, color: "sky" },
];

const FEEDBACK_STATUSES = [
  { id: "open", label: "Open", color: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "reviewing", label: "Reviewing", color: "bg-sky-100 text-sky-800 border-sky-300" },
  { id: "planned", label: "Planned", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { id: "done", label: "Done", color: "bg-stone-200 text-stone-600 border-stone-300" },
  { id: "wontdo", label: "Won't Do", color: "bg-stone-100 text-stone-500 border-stone-300" },
];

const FEEDBACK_PRIORITIES = [
  { id: "low", label: "Low", color: "text-stone-500" },
  { id: "med", label: "Medium", color: "text-amber-700" },
  { id: "high", label: "High", color: "text-rose-700" },
];

function FeedbackModal({ feedback, setFeedback, onClose, phase }) {
  const [showNewForm, setShowNewForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [currentUserName, setCurrentUserName] = useState("");
  const [newEntry, setNewEntry] = useState({
    title: "",
    description: "",
    category: "enhancement",
    priority: "med",
    submitter: "",
    phase: phase || "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Auto-populate the submitter field with the logged-in user's display name
  // so feedback always carries an attribution. The user can still edit it
  // before saving (e.g. when filing on behalf of someone else).
  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("user_profiles")
        .select("display_name, email")
        .eq("id", user.id)
        .maybeSingle();
      const name =
        data?.display_name || data?.email || user.email || "";
      if (name) {
        setCurrentUserName(name);
        setNewEntry((prev) =>
          prev.submitter ? prev : { ...prev, submitter: name },
        );
      }
    })();
  }, []);

  const persistFeedback = async (next) => {
    setSaving(true);
    setSaveError("");
    try {
      await window.storage.set("rd_tool_feedback_shared", JSON.stringify(next), true);
      setFeedback(next);
    } catch (e) {
      console.error("Failed to save feedback", e);
      setSaveError("Failed to save. Your entry is preserved locally — try again.");
      setFeedback(next); // still update local state
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    if (!newEntry.title.trim()) return;
    const entry = {
      id: `fb_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: newEntry.title.trim(),
      description: newEntry.description.trim(),
      category: newEntry.category,
      priority: newEntry.priority,
      submitter:
        newEntry.submitter.trim() || currentUserName || "Anonymous",
      phase: newEntry.phase || phase || "",
      status: "open",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      responses: [],
    };
    const next = [entry, ...feedback];
    await persistFeedback(next);
    setNewEntry({
      title: "",
      description: "",
      category: "enhancement",
      priority: "med",
      submitter: currentUserName,
      phase: phase || "",
    });
    setShowNewForm(false);
  };

  const updateStatus = async (id, status) => {
    const next = feedback.map(f => f.id === id ? { ...f, status, updatedAt: new Date().toISOString() } : f);
    await persistFeedback(next);
  };

  const deleteEntry = async (id) => {
    if (!confirm("Delete this feedback item? This cannot be undone.")) return;
    const next = feedback.filter(f => f.id !== id);
    await persistFeedback(next);
  };

  const filtered = feedback.filter(f => {
    if (filterStatus !== "all" && f.status !== filterStatus) return false;
    if (filterCategory !== "all" && f.category !== filterCategory) return false;
    return true;
  });

  // Group by status for display
  const openItems = filtered.filter(f => f.status === "open" || f.status === "reviewing" || f.status === "planned");
  const closedItems = filtered.filter(f => f.status === "done" || f.status === "wontdo");

  const counts = {
    all: feedback.length,
    open: feedback.filter(f => f.status === "open").length,
    reviewing: feedback.filter(f => f.status === "reviewing").length,
    planned: feedback.filter(f => f.status === "planned").length,
    done: feedback.filter(f => f.status === "done").length,
    wontdo: feedback.filter(f => f.status === "wontdo").length,
  };

  const currentPhaseLabel = (() => {
    const p = PHASES.find(x => x.id === phase);
    return p ? `${p.short} — ${p.label}` : phase || "";
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 backdrop-blur-sm p-4 no-print" onClick={onClose}>
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-300 flex items-center justify-between bg-gradient-to-r from-stone-900 to-sky-900">
          <div className="flex items-center gap-3">
            <MessageSquare size={20} className="text-sky-400" />
            <div>
              <h2 className="sans text-lg font-bold text-white">Prototype Feedback & Improvement Notes</h2>
              <p className="sans text-[11px] text-sky-300 mt-0.5">Firm-wide · shared across all Linked Accounting staff using this tool</p>
            </div>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Summary strip */}
        <div className="px-6 py-3 border-b border-stone-200 bg-sky-50 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Info size={14} className="text-sky-700 shrink-0" />
            <p className="sans text-[12px] text-stone-700">
              This is a <strong>prototype tool</strong>. Log anything confusing, broken, or worth changing. Entries are visible to the whole firm and used to improve the workbench.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-stone-200 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterStatus("all")}
              className={`sans text-[11px] font-medium px-2.5 py-1 rounded-sm border transition-colors ${filterStatus === "all" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"}`}
            >
              All <span className="mono ml-1 text-[10px]">{counts.all}</span>
            </button>
            {FEEDBACK_STATUSES.map(s => (
              <button
                key={s.id}
                onClick={() => setFilterStatus(s.id)}
                className={`sans text-[11px] font-medium px-2.5 py-1 rounded-sm border transition-colors ${filterStatus === s.id ? "bg-stone-900 text-white border-stone-900" : `${s.color} hover:opacity-80`}`}
              >
                {s.label} <span className="mono ml-1 text-[10px]">{counts[s.id] || 0}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="sans text-[11px] px-2 py-1 border border-stone-300 bg-white rounded-sm focus:outline-none focus:border-sky-500"
            >
              <option value="all">All categories</option>
              {FEEDBACK_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <button
              onClick={() => setShowNewForm(!showNewForm)}
              className="sans text-xs font-bold px-3 py-1.5 bg-sky-600 text-white hover:bg-sky-700 rounded-sm flex items-center gap-1.5 transition-colors"
            >
              <Plus size={13} /> {showNewForm ? "Cancel" : "Add Note"}
            </button>
          </div>
        </div>

        {/* New entry form */}
        {showNewForm && (
          <div className="px-6 py-4 border-b border-stone-200 bg-sky-50/30">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12">
                <label className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1 block">Title <span className="text-rose-500">*</span></label>
                <input
                  type="text"
                  value={newEntry.title}
                  onChange={e => setNewEntry({ ...newEntry, title: e.target.value })}
                  placeholder="e.g., 'Save button should confirm before overwriting'"
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
                  autoFocus
                />
              </div>
              <div className="col-span-12">
                <label className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1 block">Description</label>
                <textarea
                  value={newEntry.description}
                  onChange={e => setNewEntry({ ...newEntry, description: e.target.value })}
                  placeholder="Describe the issue, what you expected, what happened, or the suggested improvement. Include steps to reproduce for bugs."
                  rows={4}
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 resize-none"
                />
              </div>
              <div className="col-span-4">
                <label className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1 block">Category</label>
                <select
                  value={newEntry.category}
                  onChange={e => setNewEntry({ ...newEntry, category: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
                >
                  {FEEDBACK_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-span-4">
                <label className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1 block">Priority</label>
                <select
                  value={newEntry.priority}
                  onChange={e => setNewEntry({ ...newEntry, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
                >
                  {FEEDBACK_PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div className="col-span-4">
                <label className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1 block">Your Name</label>
                <input
                  type="text"
                  value={newEntry.submitter}
                  onChange={e => setNewEntry({ ...newEntry, submitter: e.target.value })}
                  placeholder="(optional)"
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500"
                />
              </div>
              <div className="col-span-12 flex items-center justify-between pt-1">
                <div className="sans text-[11px] text-stone-500">
                  <span className="text-stone-700 font-semibold">Phase context:</span> {currentPhaseLabel}
                </div>
                <button
                  onClick={addEntry}
                  disabled={!newEntry.title.trim() || saving}
                  className="sans text-xs font-bold px-4 py-2 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {saving ? <><RefreshCw size={12} className="animate-spin" /> Saving...</> : <><Send size={12} /> Submit Note</>}
                </button>
              </div>
              {saveError && (
                <div className="col-span-12 p-2 bg-rose-50 border border-rose-300 rounded-sm">
                  <p className="sans text-[11px] text-rose-800">{saveError}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Feedback list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare size={32} className="text-stone-300 mx-auto mb-3" />
              <p className="sans text-sm text-stone-500">
                {feedback.length === 0 ? "No feedback yet. Be the first to log a note." : "No items match the current filters."}
              </p>
              {feedback.length === 0 && (
                <button
                  onClick={() => setShowNewForm(true)}
                  className="mt-3 sans text-xs font-bold px-3 py-1.5 bg-sky-600 text-white hover:bg-sky-700 rounded-sm inline-flex items-center gap-1.5"
                >
                  <Plus size={12} /> Add First Note
                </button>
              )}
            </div>
          ) : (
            <>
              {openItems.length > 0 && (
                <div className="mb-5">
                  <h3 className="sans text-[11px] font-bold uppercase tracking-widest text-stone-700 mb-2">Active ({openItems.length})</h3>
                  <div className="space-y-2">
                    {openItems.map(f => (
                      <FeedbackCard key={f.id} entry={f} updateStatus={updateStatus} deleteEntry={deleteEntry} />
                    ))}
                  </div>
                </div>
              )}
              {closedItems.length > 0 && (
                <div>
                  <h3 className="sans text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2">Closed ({closedItems.length})</h3>
                  <div className="space-y-2 opacity-70">
                    {closedItems.map(f => (
                      <FeedbackCard key={f.id} entry={f} updateStatus={updateStatus} deleteEntry={deleteEntry} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
          <p className="sans text-[11px] text-stone-500">
            {feedback.length} total note{feedback.length !== 1 ? "s" : ""} · {counts.open} open · visible firm-wide
          </p>
          <button onClick={onClose} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-white hover:bg-stone-100 rounded-sm">Close</button>
        </div>
      </div>
    </div>
  );
}

function FeedbackCard({ entry, updateStatus, deleteEntry }) {
  const [expanded, setExpanded] = useState(false);
  const cat = FEEDBACK_CATEGORIES.find(c => c.id === entry.category) || FEEDBACK_CATEGORIES[1];
  const prio = FEEDBACK_PRIORITIES.find(p => p.id === entry.priority) || FEEDBACK_PRIORITIES[1];
  const stat = FEEDBACK_STATUSES.find(s => s.id === entry.status) || FEEDBACK_STATUSES[0];
  const CatIcon = cat.icon;
  const phaseInfo = PHASES.find(p => p.id === entry.phase);

  return (
    <div className="border border-stone-300 bg-white rounded-sm hover:border-stone-400 transition-colors">
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${
            cat.color === "rose" ? "bg-rose-100 text-rose-700" :
            cat.color === "sky" ? "bg-sky-100 text-sky-700" :
            cat.color === "amber" ? "bg-amber-100 text-amber-700" :
            "bg-stone-100 text-stone-700"
          }`}>
            <CatIcon size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="sans text-sm font-semibold text-stone-900">{entry.title}</h4>
                  <span className={`sans text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${stat.color}`}>{stat.label}</span>
                  <span className={`mono text-[10px] font-bold ${prio.color}`}>{prio.label.toUpperCase()}</span>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="sans text-[10px] text-stone-500">{entry.submitter}</span>
                  <span className="text-stone-300">·</span>
                  <span className="mono text-[10px] text-stone-500">{new Date(entry.createdAt).toLocaleDateString()}</span>
                  {phaseInfo && (
                    <>
                      <span className="text-stone-300">·</span>
                      <span className="sans text-[10px] text-stone-500">from {phaseInfo.short} {phaseInfo.label}</span>
                    </>
                  )}
                  <span className="text-stone-300">·</span>
                  <span className="sans text-[10px] text-stone-500">{cat.label}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <select
                  value={entry.status}
                  onChange={e => updateStatus(entry.id, e.target.value)}
                  className="sans text-[10px] px-1.5 py-0.5 border border-stone-300 bg-white rounded-sm focus:outline-none focus:border-sky-500"
                  title="Change status"
                >
                  {FEEDBACK_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button onClick={() => deleteEntry(entry.id)} className="p-1 text-stone-400 hover:text-rose-500 transition-colors" title="Delete">
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
            {entry.description && (
              <div className="mt-2">
                <p className={`sans text-xs text-stone-700 leading-relaxed whitespace-pre-wrap ${expanded ? "" : "line-clamp-2"}`}>
                  {entry.description}
                </p>
                {entry.description.length > 120 && (
                  <button onClick={() => setExpanded(!expanded)} className="sans text-[10px] text-sky-600 hover:text-sky-800 mt-1 font-medium">
                    {expanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// FILE ATTACHMENT ZONE — used everywhere
// ============================================================
function FileAttachmentZone({ linkedTo = {}, category = "General", documents, addDocuments, deleteDocument, label = "Attach supporting documents", compact = false }) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const matchingDocs = documents.filter(d => {
    if (!d.linkedTo) return false;
    const ltKeys = Object.keys(linkedTo);
    if (ltKeys.length === 0) return false;
    return ltKeys.every(k => d.linkedTo[k] === linkedTo[k]) &&
           Object.keys(d.linkedTo).length === ltKeys.length;
  });

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    await addDocuments(Array.from(files), linkedTo, category);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className={compact ? "" : "mt-2"}>
      {!compact && (
        <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest block mb-1.5 flex items-center gap-1">
          <Paperclip size={10} /> {label}
        </div>
      )}

      <div
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-sm transition-colors ${
          isDragging ? "border-sky-500 bg-sky-50" : "border-stone-300 bg-stone-50/50"
        } ${compact ? "p-2" : "p-3"}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ""; }}
        />
        <button
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 sans text-xs text-stone-700 hover:text-stone-900 py-1"
        >
          <Upload size={12} />
          <span>{compact ? "Attach files" : "Drop files here or click to browse"}</span>
        </button>
      </div>

      {matchingDocs.length > 0 && (
        <div className="mt-2 space-y-1">
          {matchingDocs.map(doc => {
            const { icon: Icon, color } = fileIconFor(doc.name);
            return (
              <div key={doc.id} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-stone-200 rounded-sm">
                <Icon size={13} className={color} />
                <a
                  href={doc.dataUrl}
                  download={doc.name}
                  className="sans text-xs text-stone-800 hover:text-sky-700 flex-1 truncate"
                  title={doc.name}
                >
                  {doc.name}
                </a>
                <span className="mono text-[9px] text-stone-400 uppercase">{humanFileSize(doc.size)}</span>
                <button onClick={() => deleteDocument(doc.id)} className="text-stone-400 hover:text-rose-600">
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UploadButton({ onFiles, compact }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" multiple className="hidden" onChange={e => { onFiles(Array.from(e.target.files)); e.target.value = ""; }} />
      <button
        onClick={() => ref.current?.click()}
        className={`sans font-medium border border-stone-300 bg-white hover:bg-sky-50 hover:border-sky-500 rounded-sm flex items-center gap-1.5 ${
          compact ? "text-[10px] px-2 py-1" : "text-xs px-3 py-1.5"
        }`}
      >
        <Upload size={compact ? 10 : 12} /> Attach files
      </button>
    </>
  );
}

// ============================================================
// PHASE 1: SCOPE
// ============================================================
function ScopePhase({ scope, setScope }) {
  const update = (k, v) => setScope({ ...scope, [k]: v });
  return (
    <div className="p-8">
      <PhaseHeader number="01" title="Engagement Scope & Entity" description="Define the taxpayer, tax year, entities, and study type before any technical work begins." />

      <div className="grid grid-cols-2 gap-5 mt-8">
        <Field label="Client / Taxpayer Name" required>
          <input type="text" value={scope.clientName} onChange={e => update("clientName", e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="Acme Industries, Inc." />
        </Field>
        <Field label="EIN">
          <input type="text" value={scope.ein} onChange={e => update("ein", e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono text-sm focus:outline-none focus:border-stone-900" placeholder="XX-XXXXXXX" />
        </Field>
        <Field label="Tax Year">
          <select value={scope.taxYear} onChange={e => update("taxYear", e.target.value)} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
            <option>2025</option><option>2024</option><option>2023</option><option>2022</option>
          </select>
        </Field>
        <Field label="Entity Type">
          <select value={scope.entityType} onChange={e => update("entityType", e.target.value)} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
            <option>C-corp</option><option>S-corp</option><option>Partnership</option><option>LLC</option><option>Sole Prop</option>
          </select>
        </Field>
        <Field label="Study Type">
          <select value={scope.studyType} onChange={e => update("studyType", e.target.value)} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
            <option value="current-year">Current-year filing support</option>
            <option value="amended">Amended return</option>
            <option value="provision">ASC 740 / provision</option>
            <option value="estimate">Estimate only</option>
          </select>
        </Field>
        <Field label="Primary Jurisdiction">
          <input type="text" value={scope.jurisdiction} onChange={e => update("jurisdiction", e.target.value)} placeholder="e.g. Delaware / CA / TX"
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" />
        </Field>
        <Field label="Current-Year Gross Receipts" hint="For §41(h) QSB analysis">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
            <MoneyInput value={scope.grossReceiptsCurrent} onChange={e => update("grossReceiptsCurrent", e.target.value)}
              className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
          </div>
        </Field>
        <Field label="First Year with Gross Receipts" hint="For QSB 5-year test">
          <input type="number" value={scope.firstRevenueYear} onChange={e => update("firstRevenueYear", e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="2021" />
        </Field>

        <Field label="Client Contact Name" hint="For request emails">
          <input type="text" value={scope.clientContactName} onChange={e => update("clientContactName", e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="Jane Smith, CFO" />
        </Field>
        <Field label="Client Contact Email">
          <input type="email" value={scope.clientContactEmail} onChange={e => update("clientContactEmail", e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="jane@client.com" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <Toggle label="Part of a controlled group?" value={scope.controlledGroup} onChange={v => update("controlledGroup", v)} cite="41(f)(1)" />
        <Toggle label="Prior R&D study completed?" value={scope.priorStudy} onChange={v => update("priorStudy", v)} />
      </div>

      {scope.controlledGroup && (
        <div className="mt-6">
          <HumanFlag>
            <strong>Controlled group detected.</strong> A human must confirm group membership, review intercompany agreements, and ensure
            all members use the <em>same computation method</em>. <CiteBadge id="1.41-6" onClick={() => {}} />
          </HumanFlag>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHASE 2: ENGAGEMENT & FEES
// ============================================================
const LIFECYCLE_STEPS = [
  { key: "proposal", label: "Proposal Prepared", icon: FileText, description: "Initial fee quote and scope proposal drafted for prospect." },
  { key: "letterDrafted", label: "Engagement Letter Drafted", icon: Pencil, description: "Formal engagement letter prepared with agreed scope, fees, and terms." },
  { key: "letterSent", label: "Letter Sent to Client", icon: Send, description: "Engagement letter delivered to client for review and signature." },
  { key: "letterSigned", label: "Engagement Letter Signed", icon: PenLine, description: "Client signature received; engagement is formally accepted." },
  { key: "retainerReceived", label: "Retainer Received", icon: DollarSign, description: "Upfront retainer or initial payment confirmed." },
  { key: "studyStarted", label: "Study Started", icon: PlayCircle, description: "Fieldwork and data collection begun." },
  { key: "inProgress", label: "In Progress", icon: Clock, description: "Active fieldwork — interviews, QRE mapping, computation." },
  { key: "completed", label: "Analysis Completed", icon: CheckCheck, description: "All technical work finished; final report ready for review." },
  { key: "delivered", label: "Final Product Delivered", icon: Target, description: "Report and deliverables transmitted to the client." },
];

const STATUS_CONFIG = {
  not_started: { label: "Not Started", bg: "bg-white", border: "border-stone-200", text: "text-stone-400" },
  pending: { label: "Pending", bg: "bg-stone-100", border: "border-stone-300", text: "text-stone-600" },
  in_progress: { label: "In Progress", bg: "bg-sky-50", border: "border-sky-500", text: "text-sky-800" },
  complete: { label: "Complete", bg: "bg-emerald-50", border: "border-emerald-400", text: "text-emerald-800" },
  stalled: { label: "Stalled", bg: "bg-rose-50", border: "border-rose-400", text: "text-rose-800" },
};

function daysBetween(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
}

function EngagementPhase({ engagement, setEngagement, scope, finalCredit, requestLists, onCite }) {
  const [activeTab, setActiveTab] = useState("overview");

  const update = (k, v) => setEngagement({ ...engagement, [k]: v });
  const updateLifecycle = (stepKey, patch) => {
    setEngagement({
      ...engagement,
      lifecycle: { ...engagement.lifecycle, [stepKey]: { ...engagement.lifecycle[stepKey], ...patch } },
    });
  };

  const totalHours = engagement.timeEntries.reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
  const billableHours = engagement.timeEntries.filter(t => t.billable).reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
  const estimatedRevenue = useMemo(() => {
    if (engagement.feeStructure === "fixed") return parseFloat(engagement.fixedFee) || 0;
    if (engagement.feeStructure === "hourly") return billableHours * (parseFloat(engagement.hourlyRate) || 0);
    if (engagement.feeStructure === "contingent") {
      const raw = (parseFloat(engagement.contingentPct) || 0) / 100 * finalCredit;
      const cap = parseFloat(engagement.contingentCap) || 0;
      return cap > 0 ? Math.min(raw, cap) : raw;
    }
    if (engagement.feeStructure === "hybrid") {
      const fixed = parseFloat(engagement.fixedFee) || 0;
      const cont = (parseFloat(engagement.contingentPct) || 0) / 100 * finalCredit;
      return fixed + cont;
    }
    return 0;
  }, [engagement, billableHours, finalCredit]);

  const realizedRate = billableHours > 0 ? estimatedRevenue / billableHours : 0;

  const stalledLists = useMemo(() => {
    const threshold = parseInt(engagement.stallThresholdDays) || 14;
    return requestLists.filter(r => {
      const pending = r.items.filter(i => i.status === "requested").length;
      if (pending === 0) return false;
      const created = new Date(r.createdAt);
      const daysOld = Math.floor((new Date() - created) / (1000 * 60 * 60 * 24));
      return daysOld >= threshold;
    });
  }, [requestLists, engagement.stallThresholdDays]);

  const tabs = [
    { id: "overview", label: "Lifecycle Overview", icon: Target },
    { id: "fees", label: "Fee Structure", icon: DollarSign },
    { id: "letter", label: "Engagement Letter", icon: FileText },
    { id: "time", label: "Time Tracking", icon: Timer },
    { id: "stalls", label: `Stalls ${stalledLists.length > 0 ? `(${stalledLists.length})` : ""}`, icon: AlertTriangle },
  ];

  return (
    <div className="p-8">
      <PhaseHeader number="02" title="Engagement & Fees" description="The full engagement lifecycle — from proposal through signed letter, work tracking, stall detection, and final delivery." />

      <div className="flex gap-0 border border-stone-300 rounded-sm overflow-hidden mb-6 bg-white">
        {tabs.map(t => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          const isStallTab = t.id === "stalls" && stalledLists.length > 0;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-r border-stone-200 last:border-r-0 transition-colors ${
                isActive ? "bg-stone-900 text-sky-400" :
                isStallTab ? "bg-rose-50 text-rose-800 hover:bg-rose-100" :
                "bg-white text-stone-700 hover:bg-stone-50"}`}>
              <Icon size={13} />
              <span className="sans text-xs font-semibold">{t.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === "overview" && <LifecycleOverview engagement={engagement} updateLifecycle={updateLifecycle} scope={scope} estimatedRevenue={estimatedRevenue} totalHours={totalHours} finalCredit={finalCredit} />}
      {activeTab === "fees" && <FeeStructureTab engagement={engagement} update={update} finalCredit={finalCredit} estimatedRevenue={estimatedRevenue} realizedRate={realizedRate} billableHours={billableHours} />}
      {activeTab === "letter" && <EngagementLetterTab engagement={engagement} update={update} scope={scope} updateLifecycle={updateLifecycle} />}
      {activeTab === "time" && <TimeTrackingTab engagement={engagement} setEngagement={setEngagement} totalHours={totalHours} billableHours={billableHours} realizedRate={realizedRate} />}
      {activeTab === "stalls" && <StallsTab engagement={engagement} update={update} stalledLists={stalledLists} requestLists={requestLists} />}
    </div>
  );
}

function LifecycleOverview({ engagement, updateLifecycle, scope, estimatedRevenue, totalHours, finalCredit }) {
  const [expandedStep, setExpandedStep] = useState(null);

  const completedCount = LIFECYCLE_STEPS.filter(s => engagement.lifecycle[s.key]?.status === "complete").length;
  const progress = Math.round((completedCount / LIFECYCLE_STEPS.length) * 100);
  const activeStep = LIFECYCLE_STEPS.find(s => engagement.lifecycle[s.key]?.status !== "complete");

  const studyStarted = engagement.lifecycle.studyStarted?.date;
  const studyCompleted = engagement.lifecycle.completed?.date;
  const letterSigned = engagement.lifecycle.letterSigned?.date;
  const delivered = engagement.lifecycle.delivered?.date;

  const daysInProgress = studyStarted ? (studyCompleted
    ? Math.floor((new Date(studyCompleted) - new Date(studyStarted)) / (1000 * 60 * 60 * 24))
    : daysBetween(studyStarted)) : null;
  const signToStartDays = (letterSigned && studyStarted) ? Math.floor((new Date(studyStarted) - new Date(letterSigned)) / (1000 * 60 * 60 * 24)) : null;
  const completeToDeliverDays = (studyCompleted && delivered) ? Math.floor((new Date(delivered) - new Date(studyCompleted)) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="space-y-6">
      <div className="p-5 bg-stone-900 text-stone-100 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="serif text-lg font-bold text-sky-400">Engagement Progress</h4>
            <p className="sans text-xs text-stone-400 mt-1">{scope.clientName || "[Client]"} · Tax Year {scope.taxYear}</p>
          </div>
          <div className="text-right">
            <div className="serif text-4xl font-bold text-sky-400 num-tabular">{progress}%</div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone-400">{completedCount} of {LIFECYCLE_STEPS.length} complete</div>
          </div>
        </div>
        <div className="h-2 bg-stone-800 rounded-sm overflow-hidden">
          <div className="h-full bg-gradient-to-r from-sky-500 to-sky-400 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        {activeStep && (
          <div className="mt-3 flex items-center gap-2 sans text-xs text-stone-300">
            <Clock size={12} className="text-sky-400" />
            <span>Currently at: <strong className="text-sky-400">{activeStep.label}</strong></span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-4 gap-3">
        <MiniMetric label="Est. Revenue" value={`$${fmt(estimatedRevenue)}`} icon={DollarSign} />
        <MiniMetric label="Total Hours" value={totalHours.toFixed(1)} icon={Timer} />
        <MiniMetric label="Days in Progress" value={daysInProgress !== null ? daysInProgress : "\u2014"} icon={CalendarDays} />
        <MiniMetric label="Est. Credit" value={`$${fmt(finalCredit)}`} icon={Target} />
      </div>

      <div>
        <h4 className="serif text-base font-semibold text-stone-900 mb-3">Lifecycle Timeline</h4>
        <div className="space-y-0">
          {LIFECYCLE_STEPS.map((step, idx) => {
            const state = engagement.lifecycle[step.key] || { status: "not_started" };
            const cfg = STATUS_CONFIG[state.status] || STATUS_CONFIG.not_started;
            const Icon = step.icon;
            const isExpanded = expandedStep === step.key;
            const isLast = idx === LIFECYCLE_STEPS.length - 1;

            return (
              <div key={step.key} className="relative">
                {!isLast && (
                  <div className={`absolute left-5 top-12 bottom-0 w-0.5 ${state.status === "complete" ? "bg-emerald-400" : "bg-stone-300"}`} />
                )}
                <div className="flex gap-4 pb-4">
                  <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2 ${
                    state.status === "complete" ? "bg-emerald-600 border-emerald-700 text-white" :
                    state.status === "in_progress" ? "bg-sky-500 border-sky-600 text-white" :
                    state.status === "stalled" ? "bg-rose-600 border-rose-700 text-white" :
                    state.status === "pending" ? "bg-stone-200 border-stone-400 text-stone-600" :
                    "bg-white border-stone-200 text-stone-300 border-dashed"}`}>
                    {state.status === "complete" ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                  </div>
                  <div className={`flex-1 border rounded-sm ${cfg.border} ${cfg.bg}`}>
                    <button onClick={() => setExpandedStep(isExpanded ? null : step.key)} className="w-full text-left p-3 flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="mono text-[9px] font-bold uppercase tracking-widest text-stone-500">Step {idx + 1}</span>
                          <span className={`serif text-sm font-bold ${state.status === "not_started" ? "text-stone-500" : "text-stone-900"}`}>{step.label}</span>
                          <span className={`mono text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                            state.status === "complete" ? "bg-emerald-600 text-white" :
                            state.status === "in_progress" ? "bg-sky-500 text-white" :
                            state.status === "stalled" ? "bg-rose-600 text-white" :
                            state.status === "pending" ? "bg-stone-300 text-stone-700" :
                            "bg-stone-100 text-stone-400 border border-stone-300"}`}>{cfg.label}</span>
                        </div>
                        <p className={`sans text-xs leading-snug ${state.status === "not_started" ? "text-stone-400" : "text-stone-600"}`}>{step.description}</p>
                        {state.date && (
                          <div className="mono text-[10px] text-stone-500 mt-1">
                            Date: {new Date(state.date).toLocaleDateString()}
                            {state.status !== "complete" && daysBetween(state.date) !== null && <span className="ml-2">{daysBetween(state.date)} days ago</span>}
                          </div>
                        )}
                      </div>
                      <ChevronRight size={16} className={`text-stone-400 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-stone-200 pt-3 space-y-3 bg-white">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Status</div>
                            <select value={state.status} onChange={e => updateLifecycle(step.key, { status: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 bg-white rounded-sm mono text-[11px] focus:outline-none focus:border-stone-900">
                              <option value="not_started">Not Started</option>
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                              <option value="stalled">Stalled</option>
                            </select>
                          </div>
                          <div>
                            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Date</div>
                            <input type="date" value={state.date || ""} onChange={e => updateLifecycle(step.key, { date: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 bg-white rounded-sm mono text-[11px] focus:outline-none focus:border-stone-900" />
                          </div>
                        </div>
                        {step.key === "letterSigned" && state.status === "complete" && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Signed By</div>
                              <input type="text" value={state.signedBy || ""} onChange={e => updateLifecycle(step.key, { signedBy: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 bg-white rounded-sm sans text-xs focus:outline-none focus:border-stone-900" placeholder="Name and title" />
                            </div>
                            <div>
                              <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Signature Date</div>
                              <input type="date" value={state.signedDate || ""} onChange={e => updateLifecycle(step.key, { signedDate: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 bg-white rounded-sm mono text-[11px] focus:outline-none focus:border-stone-900" />
                            </div>
                          </div>
                        )}
                        {step.key === "retainerReceived" && (
                          <div>
                            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Amount Received</div>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
                              <MoneyInput value={state.amount || ""} onChange={e => updateLifecycle(step.key, { amount: e.target.value })} className="w-full pl-5 pr-2 py-1.5 border border-stone-300 bg-white rounded-sm mono num-tabular text-xs focus:outline-none focus:border-stone-900" placeholder="0" />
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Notes</div>
                          <textarea value={state.notes || ""} onChange={e => updateLifecycle(step.key, { notes: e.target.value })} className="w-full px-2 py-1.5 border border-stone-300 bg-white rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[50px]" placeholder="Any notes on this milestone..." />
                        </div>
                        {state.status === "stalled" && (
                          <div className="p-2 bg-rose-100 border border-rose-300 rounded-sm">
                            <p className="sans text-[11px] text-rose-900"><AlertTriangle size={11} className="inline mr-1" />Stalled — document the blocker in notes and consider escalating to the partner or contacting the client.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {(signToStartDays !== null || daysInProgress !== null || completeToDeliverDays !== null) && (
        <div className="p-4 bg-stone-50 border border-stone-300 rounded-sm">
          <h5 className="serif text-sm font-semibold text-stone-900 mb-2">Timing Analysis</h5>
          <div className="grid grid-cols-3 gap-3">
            {signToStartDays !== null && (
              <div className="text-center p-2 border border-stone-200 bg-white rounded-sm">
                <div className="mono text-xl font-bold text-stone-900 num-tabular">{signToStartDays}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-stone-500">Days: Sign &rarr; Start</div>
              </div>
            )}
            {daysInProgress !== null && (
              <div className="text-center p-2 border border-stone-200 bg-white rounded-sm">
                <div className="mono text-xl font-bold text-stone-900 num-tabular">{daysInProgress}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-stone-500">Days: Start &rarr; {studyCompleted ? "Complete" : "Today"}</div>
              </div>
            )}
            {completeToDeliverDays !== null && (
              <div className="text-center p-2 border border-stone-200 bg-white rounded-sm">
                <div className="mono text-xl font-bold text-stone-900 num-tabular">{completeToDeliverDays}</div>
                <div className="mono text-[9px] uppercase tracking-widest text-stone-500">Days: Complete &rarr; Deliver</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FeeStructureTab({ engagement, update, finalCredit, estimatedRevenue, realizedRate, billableHours }) {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="serif text-base font-semibold text-stone-900 mb-3">Firm Information</h4>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Firm Name">
            <input type="text" value={engagement.firmName} onChange={e => update("firmName", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" />
          </Field>
          <Field label="Partner / Engagement Lead">
            <input type="text" value={engagement.partnerName} onChange={e => update("partnerName", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="Partner name" />
          </Field>
          <Field label="Firm Address">
            <input type="text" value={engagement.firmAddress} onChange={e => update("firmAddress", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="Street, City, State ZIP" />
          </Field>
          <Field label="Firm Phone">
            <input type="text" value={engagement.firmPhone} onChange={e => update("firmPhone", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="(xxx) xxx-xxxx" />
          </Field>
          <Field label="Firm Email">
            <input type="email" value={engagement.firmEmail} onChange={e => update("firmEmail", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="contact@linkedaccounting.com" />
          </Field>
          <Field label="Partner Title">
            <input type="text" value={engagement.partnerTitle} onChange={e => update("partnerTitle", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" placeholder="Partner, CPA" />
          </Field>
        </div>
      </div>

      <div>
        <h4 className="serif text-base font-semibold text-stone-900 mb-3">Fee Structure</h4>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { v: "fixed", l: "Fixed Fee", d: "Single flat price" },
            { v: "hourly", l: "Hourly", d: "Per-hour billing" },
            { v: "contingent", l: "Contingent", d: "% of credit earned" },
            { v: "hybrid", l: "Hybrid", d: "Fixed + contingent" },
          ].map(opt => (
            <button key={opt.v} onClick={() => update("feeStructure", opt.v)}
              className={`p-3 border-2 rounded-sm text-left transition-colors ${
                engagement.feeStructure === opt.v ? "border-stone-900 bg-sky-50" : "border-stone-300 bg-white hover:border-stone-500"}`}>
              <div className="serif text-sm font-bold text-stone-900">{opt.l}</div>
              <div className="sans text-[10px] text-stone-600 mt-0.5">{opt.d}</div>
            </button>
          ))}
        </div>

        <div className="p-4 border border-stone-300 rounded-sm bg-stone-50/50 space-y-3">
          {(engagement.feeStructure === "fixed" || engagement.feeStructure === "hybrid") && (
            <Field label="Fixed Fee" hint="Flat project fee">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
                <MoneyInput value={engagement.fixedFee} onChange={e => update("fixedFee", e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
              </div>
            </Field>
          )}

          {engagement.feeStructure === "hourly" && (
            <>
              <Field label="Hourly Rate">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
                  <MoneyInput value={engagement.hourlyRate} onChange={e => update("hourlyRate", e.target.value)}
                    className="w-full pl-7 pr-12 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-xs">/hr</span>
                </div>
              </Field>
              <Field label="Estimated Hours">
                <input type="number" value={engagement.estimatedHours} onChange={e => update("estimatedHours", e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
              </Field>
            </>
          )}

          {(engagement.feeStructure === "contingent" || engagement.feeStructure === "hybrid") && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contingent %" hint="% of credit earned">
                <div className="relative">
                  <input type="number" value={engagement.contingentPct} onChange={e => update("contingentPct", e.target.value)} step="0.1"
                    className="w-full pl-3 pr-8 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="15" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">%</span>
                </div>
              </Field>
              <Field label="Contingent Cap (optional)">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
                  <MoneyInput value={engagement.contingentCap} onChange={e => update("contingentCap", e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="No cap" />
                </div>
              </Field>
            </div>
          )}
        </div>
      </div>

      <div>
        <h4 className="serif text-base font-semibold text-stone-900 mb-3">Billing & Payment</h4>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Retainer Amount">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
              <MoneyInput value={engagement.retainerAmount} onChange={e => update("retainerAmount", e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
            </div>
          </Field>
          <Field label="Retainer Due">
            <select value={engagement.retainerDue} onChange={e => update("retainerDue", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
              <option value="upon-signing">Upon signing</option>
              <option value="before-start">Before work begins</option>
              <option value="waived">No retainer</option>
            </select>
          </Field>
          <Field label="Billing Schedule">
            <select value={engagement.billingSchedule} onChange={e => update("billingSchedule", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
              <option value="upon-completion">Upon completion</option>
              <option value="monthly">Monthly as work progresses</option>
              <option value="milestone">Milestone-based</option>
              <option value="50-50">50% upfront, 50% on delivery</option>
            </select>
          </Field>
          <Field label="Payment Terms">
            <select value={engagement.paymentTerms} onChange={e => update("paymentTerms", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
              <option>Net 15</option>
              <option>Net 30</option>
              <option>Net 45</option>
              <option>Due on receipt</option>
            </select>
          </Field>
          <Field label="Expenses">
            <select value={engagement.expensesBilled} onChange={e => update("expensesBilled", e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
              <option value="actual">Billed at actual cost</option>
              <option value="flat">Flat fee for expenses</option>
              <option value="included">Included in fee</option>
            </select>
          </Field>
          {engagement.expensesBilled === "flat" && (
            <Field label="Flat Expense Amount">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
                <MoneyInput value={engagement.flatExpense} onChange={e => update("flatExpense", e.target.value)}
                  className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-stone-900" placeholder="0" />
              </div>
            </Field>
          )}
        </div>
      </div>

      <div className="p-5 bg-stone-900 text-stone-100 rounded-sm">
        <h4 className="serif text-base font-bold text-sky-400 mb-3">Estimated Revenue</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone-400 mb-1">Structure</div>
            <div className="serif text-lg font-semibold text-stone-100 capitalize">{engagement.feeStructure}</div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone-400 mb-1">Est. Revenue</div>
            <div className="serif text-2xl font-bold text-sky-400 num-tabular mono">${fmt(estimatedRevenue)}</div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone-400 mb-1">Est. Credit for Client</div>
            <div className="serif text-lg font-semibold text-stone-100 num-tabular mono">${fmt(finalCredit)}</div>
          </div>
        </div>
        {engagement.feeStructure === "hourly" && billableHours > 0 && (
          <div className="mt-3 pt-3 border-t border-stone-700 flex items-center gap-4">
            <span className="sans text-xs text-stone-400">Billable so far: <strong className="text-sky-400 mono">{billableHours.toFixed(1)} hrs</strong></span>
            <span className="sans text-xs text-stone-400">Realized rate: <strong className="text-sky-400 mono">${fmt(realizedRate)}/hr</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}

function EngagementLetterTab({ engagement, update, scope, updateLifecycle }) {
  const [copied, setCopied] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const signed = engagement.lifecycle.letterSigned?.status === "complete";

  const feeDescription = (() => {
    const fs = engagement.feeStructure;
    if (fs === "fixed" && engagement.fixedFee) {
      return `a fixed fee of $${Number(engagement.fixedFee).toLocaleString()}`;
    }
    if (fs === "hourly" && engagement.hourlyRate) {
      const est = engagement.estimatedHours ? ` Based on our preliminary scope, we estimate the engagement will require approximately ${engagement.estimatedHours} hours.` : "";
      return `our standard hourly rate of $${Number(engagement.hourlyRate).toLocaleString()} per hour, plus out-of-pocket expenses.${est}`;
    }
    if (fs === "contingent" && engagement.contingentPct) {
      const cap = engagement.contingentCap ? `, subject to a maximum fee of $${Number(engagement.contingentCap).toLocaleString()}` : "";
      return `a contingent fee equal to ${engagement.contingentPct}% of the federal research credit identified and documented in our final R&D study report${cap}. If no credit is identified, no fee will be charged except for out-of-pocket expenses actually incurred`;
    }
    if (fs === "hybrid") {
      const fixed = engagement.fixedFee ? `a fixed fee of $${Number(engagement.fixedFee).toLocaleString()}` : "";
      const pct = engagement.contingentPct ? `${engagement.contingentPct}% of the federal research credit identified` : "";
      const cap = engagement.contingentCap ? `, with the contingent portion capped at $${Number(engagement.contingentCap).toLocaleString()}` : "";
      return `${fixed}, plus a contingent fee of ${pct}${cap}`;
    }
    return "[fee structure not yet specified]";
  })();

  const expenseDescription = (() => {
    if (engagement.expensesBilled === "actual") return "Out-of-pocket expenses (travel, document reproduction, third-party research tools) will be billed separately at actual cost.";
    if (engagement.expensesBilled === "flat" && engagement.flatExpense) return `A flat fee of $${Number(engagement.flatExpense).toLocaleString()} will be charged to cover all out-of-pocket expenses.`;
    if (engagement.expensesBilled === "included") return "Out-of-pocket expenses are included in the fee stated above; no separate expense charges will apply.";
    return "";
  })();

  const retainerClause = engagement.retainerAmount && engagement.retainerDue !== "waived"
    ? `A retainer of $${Number(engagement.retainerAmount).toLocaleString()} is due ${engagement.retainerDue === "upon-signing" ? "upon execution of this agreement" : "before work on the study begins"}. The retainer will be applied to the final invoice.`
    : "";

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const copyLetter = async () => {
    try {
      const el = document.getElementById("engagement-letter-content");
      const text = el.innerText;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { alert("Copy failed."); }
  };

  const downloadLetter = () => {
    const html = document.getElementById("engagement-letter-content").outerHTML;
    const fullHtml = `<!DOCTYPE html><html><head><title>Engagement Letter</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:20px;color:#1c1917;line-height:1.6}h1{border-bottom:2px solid #1c1917;padding-bottom:8px}h2{margin-top:28px;font-size:16pt}</style></head><body>${html}</body></html>`;
    const blob = new Blob([fullHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EngagementLetter_${(scope.clientName || "Client").replace(/\s/g, "_")}_${scope.taxYear}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const markLetterSigned = () => {
    updateLifecycle("letterSigned", { status: "complete", signedDate: new Date().toISOString().slice(0, 10), date: new Date().toISOString().slice(0, 10) });
  };

  return (
    <div className="space-y-4">
      <div className={`p-4 rounded-sm border-l-4 ${signed ? "bg-emerald-50 border-emerald-500" : "bg-sky-50 border-sky-500"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {signed ? <CheckCheck size={20} className="text-emerald-700" /> : <PenLine size={20} className="text-sky-700" />}
            <div>
              <div className="serif text-base font-bold text-stone-900">
                {signed ? "Engagement Letter Signed" : "Awaiting Client Signature"}
              </div>
              <p className="sans text-xs text-stone-700 mt-0.5">
                {signed
                  ? `Signed by ${engagement.lifecycle.letterSigned.signedBy || "[name not captured]"} on ${engagement.lifecycle.letterSigned.signedDate || engagement.lifecycle.letterSigned.date || "[date not captured]"}.`
                  : "The engagement letter below must be printed, signed by the client, and returned before fieldwork can begin."}
              </p>
            </div>
          </div>
          {!signed && (
            <button onClick={markLetterSigned} className="sans text-xs font-semibold px-3 py-2 bg-stone-900 text-sky-400 rounded-sm hover:bg-stone-800 flex items-center gap-1.5">
              <CheckCheck size={13} /> Mark as Signed
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 no-print">
        <button onClick={() => setShowEmailModal(true)} className="sans text-xs font-semibold px-3 py-1.5 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm flex items-center gap-1.5">
          <Mail size={12} /> Send by Email
        </button>
        <button onClick={copyLetter} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm flex items-center gap-1.5">
          <Copy size={12} /> {copied ? "Copied!" : "Copy Text"}
        </button>
        <button onClick={downloadLetter} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm flex items-center gap-1.5">
          <Download size={12} /> Download HTML
        </button>
        <button onClick={() => window.print()} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm flex items-center gap-1.5">
          <Printer size={12} /> Print
        </button>
      </div>

      {showEmailModal && (
        <EmailLetterModal
          engagement={engagement}
          scope={scope}
          feeDescription={feeDescription}
          expenseDescription={expenseDescription}
          retainerClause={retainerClause}
          today={today}
          onClose={() => setShowEmailModal(false)}
          onMarkSent={() => {
            updateLifecycle("letterSent", {
              status: "complete",
              date: new Date().toISOString().slice(0, 10),
            });
            setShowEmailModal(false);
          }}
        />
      )}

      <div className="p-4 border border-stone-300 rounded-sm bg-stone-50">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Scope Exclusions (optional)</div>
            <textarea value={engagement.scopeExclusions || ""} onChange={e => update("scopeExclusions", e.target.value)}
              className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[60px]"
              placeholder="e.g., This engagement does not include any state R&D credit analysis; those services would be quoted separately." />
          </div>
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Special Terms (optional)</div>
            <textarea value={engagement.specialTerms || ""} onChange={e => update("specialTerms", e.target.value)}
              className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[60px]"
              placeholder="Any additional negotiated terms to include in the letter..." />
          </div>
        </div>
      </div>

      <div id="engagement-letter-content" className="bg-white border-2 border-stone-300 rounded-sm p-10" style={{ fontFamily: "Georgia, serif", lineHeight: 1.6 }}>
        <div className="border-b-2 border-stone-900 pb-4 mb-6">
          <h1 className="serif text-3xl font-bold text-stone-900 mb-1">{engagement.firmName || "[Firm Name]"}</h1>
          <div className="sans text-xs text-stone-600">
            {engagement.firmAddress && <div>{engagement.firmAddress}</div>}
            <div className="flex gap-4">
              {engagement.firmPhone && <span>{engagement.firmPhone}</span>}
              {engagement.firmEmail && <span>{engagement.firmEmail}</span>}
            </div>
          </div>
        </div>

        <div className="mb-6"><div className="sans text-sm text-stone-800">{today}</div></div>

        <div className="mb-6">
          <div className="serif text-sm text-stone-900 font-semibold">{scope.clientContactName || "[Client Contact Name]"}</div>
          <div className="serif text-sm text-stone-900">{scope.clientName || "[Client Name]"}</div>
          <div className="serif text-sm text-stone-700">[Client Address]</div>
        </div>

        <div className="mb-4">
          <div className="serif text-sm font-semibold text-stone-900">Re: Research & Development Tax Credit Study — Tax Year {scope.taxYear}</div>
        </div>

        <div className="serif text-sm text-stone-900 mb-4">Dear {scope.clientContactName ? scope.clientContactName.split(",")[0] : "[Client Name]"}:</div>

        <p className="serif text-sm text-stone-900 mb-4 leading-relaxed">
          Thank you for selecting {engagement.firmName || "[Firm Name]"} ("the Firm," "we," "us," or "our") to perform professional services for {scope.clientName || "[Client Name]"} ("the Company," "you," or "your"). This letter sets forth the terms of our engagement and describes the services we will provide, the fees you will pay, and the responsibilities of each party.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">1. Scope of Services</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-2">
          We will perform a Research & Development tax credit study (the "Study") under Internal Revenue Code Section 41 for tax year {scope.taxYear}. The Study will include:
        </p>
        <ul className="serif text-sm text-stone-900 leading-relaxed mb-3 pl-6" style={{ listStyle: "disc" }}>
          <li>Identification and qualification of research activities under IRC §41(d)(1)(A)-(C), with reference to §174A as enacted by the One Big Beautiful Bill Act (OBBBA) of 2025;</li>
          <li>Application of the statutory exclusions set forth in IRC §41(d)(4)(A)-(H);</li>
          <li>Identification and quantification of qualified research expenses ("QREs"), including qualified wages under IRC §41(b)(2)(A)(i) and Treas. Reg. §1.41-2(c)(1)-(3), qualified supplies under IRC §41(b)(2)(A)(ii), and qualified contract research under IRC §41(b)(1)(B) and Treas. Reg. §1.41-2(e)(1)-(3);</li>
          <li>Computation of the federal research credit under both the Regular Credit Method (IRC §41(a)(1)) and the Alternative Simplified Credit method (IRC §41(c)(4)), and recommendation of the method that produces the most favorable result;</li>
          <li>Analysis of the post-OBBBA §280C(c) election trade-off between claiming the full credit (with corresponding §174A deduction reduction) or the reduced credit, and the §41(h) payroll tax credit election (if applicable);</li>
          <li>For eligible small businesses (average gross receipts ≤ $31M under §448(c)), analysis of the OBBBA retroactive §174A election for tax years 2022–2024, subject to the July 6, 2026 deadline under Rev. Proc. 2025-28;</li>
          <li>Preparation of a final written Study report with technical narratives, QRE schedules, and supporting documentation index suitable for substantiation purposes.</li>
        </ul>
        {engagement.scopeExclusions && (
          <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
            <strong>Scope Exclusions.</strong> {engagement.scopeExclusions}
          </p>
        )}
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          This engagement does not include: (i) the preparation or filing of any tax returns; (ii) representation before the Internal Revenue Service or any state tax authority in the event of examination, which will be covered by a separate engagement if required; (iii) audit, review, or compilation of your financial statements; or (iv) legal advice of any kind.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">2. Fees</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          Our fee for the Services described above will be {feeDescription}.
        </p>
        {expenseDescription && (
          <p className="serif text-sm text-stone-900 leading-relaxed mb-3">{expenseDescription}</p>
        )}
        {retainerClause && (
          <p className="serif text-sm text-stone-900 leading-relaxed mb-3">{retainerClause}</p>
        )}
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          Invoices are issued {engagement.billingSchedule === "monthly" ? "monthly as work progresses" : engagement.billingSchedule === "milestone" ? "at defined project milestones" : engagement.billingSchedule === "50-50" ? "50% upon execution of this engagement letter and 50% upon delivery of the final report" : "upon completion of the Study"} and payable {engagement.paymentTerms}. Invoices not paid within thirty (30) days of the due date may accrue interest at the rate of 1.5% per month or the maximum rate permitted by law, whichever is less.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">3. Your Responsibilities</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-2">
          You agree to provide us with the information and documentation necessary to complete the Study on a timely basis, including:
        </p>
        <ul className="serif text-sm text-stone-900 leading-relaxed mb-3 pl-6" style={{ listStyle: "disc" }}>
          <li>Access to employees with knowledge of the research activities performed;</li>
          <li>Project lists, technical specifications, design documents, test plans, prototypes, and other contemporaneous records;</li>
          <li>Payroll registers, employee rosters, job descriptions, and time-tracking reports (to the extent available);</li>
          <li>General ledger detail, invoices, and purchase orders for supplies and contract research;</li>
          <li>Prior-year Forms 6765, prior R&D studies, prior QRE schedules, and gross receipts history; and</li>
          <li>Ownership documentation for controlled group analysis, if applicable.</li>
        </ul>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          You acknowledge that the quality and defensibility of the Study depends substantially on the completeness and accuracy of the information you provide. Management representations will be relied upon, and we may request written confirmation of significant representations.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">4. Our Responsibilities</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          We will perform our services in accordance with the Statements on Standards for Tax Services issued by the American Institute of Certified Public Accountants (AICPA) and applicable federal tax law. We will exercise professional judgment in evaluating the facts you provide and applying them to the law. We cannot guarantee any particular outcome or that our positions will not be challenged by the IRS or any state tax authority. Our Study represents our professional opinion based on the facts presented, existing law, and the authority cited in our final report.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">5. Confidentiality</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          All information you provide to us in connection with this engagement will be treated as confidential. We will not disclose such information to any third party without your written consent, except as required by law, regulation, or professional standard. Please note that certain communications with your tax advisor may be protected by the federal tax practitioner privilege under IRC §7525, but this privilege does not apply to criminal matters, state tax matters, or communications relating to the promotion of tax shelters.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">6. Record Retention</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          We will retain our working papers for a period of seven (7) years following the completion of the Study, in accordance with AICPA professional standards. You are responsible for retaining the original records supporting the research credit claim for as long as required by applicable tax authorities (typically, the period the tax return remains open to examination plus any applicable carryforward period).
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">7. Limitation of Liability</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          To the fullest extent permitted by law, our total liability to you arising out of or in connection with this engagement — whether in contract, tort, or otherwise — shall not exceed an amount equal to the total fees paid by you to us under this engagement. We shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including lost profits or lost tax benefits, even if advised of the possibility of such damages.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">8. Indemnification</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          You agree to indemnify and hold harmless the Firm, its partners, officers, employees, and agents from and against any claims, damages, or expenses (including reasonable attorneys' fees) arising from (i) any misrepresentation or incomplete information provided by you or on your behalf, or (ii) any use of our work product for purposes other than those described in this engagement without our prior written consent.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">9. Dispute Resolution</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          Any dispute arising out of or relating to this engagement shall first be submitted to non-binding mediation. If mediation is unsuccessful within sixty (60) days, the dispute shall be resolved by binding arbitration conducted in {engagement.firmAddress ? engagement.firmAddress.split(",").slice(-2).join(",").trim() : "[State]"} under the Commercial Arbitration Rules of the American Arbitration Association.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">10. Term and Termination</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          This engagement commences upon execution of this letter and continues until the Study is completed and delivered. Either party may terminate this engagement at any time upon written notice. In the event of termination, you agree to pay for all services performed and expenses incurred through the effective date of termination.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">11. Circular 230 Notice</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          Any written tax advice provided under this engagement is intended for your use only. It may not be used, and is not intended to be used, by any other party, and it may not be used for the purpose of avoiding any tax penalty that may be imposed on the taxpayer.
        </p>

        <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">12. Entire Agreement</h2>
        <p className="serif text-sm text-stone-900 leading-relaxed mb-3">
          This letter constitutes the entire agreement between the parties with respect to the services described and supersedes any prior oral or written communications. Any modification must be in writing and signed by both parties.
        </p>

        {engagement.specialTerms && (
          <>
            <h2 className="serif text-base font-bold text-stone-900 mt-6 mb-2">13. Additional Terms</h2>
            <p className="serif text-sm text-stone-900 leading-relaxed mb-3 whitespace-pre-wrap">{engagement.specialTerms}</p>
          </>
        )}

        <p className="serif text-sm text-stone-900 mt-6 mb-6 leading-relaxed">
          Please indicate your acceptance of the terms of this engagement by signing and dating both copies of this letter where indicated below and returning one fully executed copy to us. We appreciate the opportunity to serve you and look forward to working together.
        </p>

        <p className="serif text-sm text-stone-900 mb-8">Very truly yours,</p>

        <div className="mb-8">
          <div className="border-b border-stone-900 w-64 mb-1 pb-1 serif text-sm italic text-stone-500">
            {engagement.partnerName || "[Signature]"}
          </div>
          <div className="serif text-sm font-semibold text-stone-900">{engagement.partnerName || "[Partner Name]"}</div>
          <div className="serif text-xs text-stone-700">{engagement.partnerTitle || "Partner"}</div>
          <div className="serif text-xs text-stone-700">{engagement.firmName}</div>
        </div>

        <div className="mt-10 pt-6 border-t-2 border-stone-900">
          <h2 className="serif text-base font-bold text-stone-900 mb-3">Accepted and Agreed</h2>
          <p className="serif text-sm text-stone-900 mb-4">
            On behalf of {scope.clientName || "[Client Name]"}, I have read and understood the terms of this engagement letter and agree to be bound by them.
          </p>

          {signed ? (
            <div className="p-4 bg-emerald-50 border-2 border-emerald-500 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <CheckCheck size={18} className="text-emerald-700" />
                <span className="serif text-base font-bold text-emerald-900">SIGNED</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Signature</div>
                  <div className="border-b border-stone-900 pb-2 serif text-lg italic text-emerald-900">
                    /s/ {engagement.lifecycle.letterSigned.signedBy || "[signed]"}
                  </div>
                </div>
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Date</div>
                  <div className="border-b border-stone-900 pb-2 serif text-lg text-emerald-900">
                    {engagement.lifecycle.letterSigned.signedDate || engagement.lifecycle.letterSigned.date || ""}
                  </div>
                </div>
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Printed Name & Title</div>
                  <div className="serif text-sm font-semibold text-stone-900">{engagement.lifecycle.letterSigned.signedBy || "—"}</div>
                </div>
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Entity</div>
                  <div className="serif text-sm text-stone-900">{scope.clientName || "—"}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Signature</div>
                <div className="border-b border-stone-900 h-10"></div>
              </div>
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Date</div>
                <div className="border-b border-stone-900 h-10"></div>
              </div>
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Printed Name</div>
                <div className="border-b border-stone-900 h-10"></div>
              </div>
              <div>
                <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">Title</div>
                <div className="border-b border-stone-900 h-10"></div>
              </div>
              <div className="col-span-2">
                <div className="sans text-[10px] uppercase tracking-widest text-stone-600 mb-1">On behalf of</div>
                <div className="border-b border-stone-900 h-10 serif text-sm pt-2">{scope.clientName}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TimeTrackingTab({ engagement, setEngagement, totalHours, billableHours, realizedRate }) {
  const addEntry = () => {
    setEngagement({
      ...engagement,
      timeEntries: [
        ...engagement.timeEntries,
        {
          id: Date.now(),
          date: new Date().toISOString().slice(0, 10),
          staff: "",
          phase: "",
          hours: "",
          description: "",
          billable: true,
        },
      ],
    });
  };

  const updateEntry = (id, patch) => {
    setEngagement({
      ...engagement,
      timeEntries: engagement.timeEntries.map(e => e.id === id ? { ...e, ...patch } : e),
    });
  };

  const removeEntry = (id) => {
    setEngagement({
      ...engagement,
      timeEntries: engagement.timeEntries.filter(e => e.id !== id),
    });
  };

  const byPhase = engagement.timeEntries.reduce((acc, t) => {
    const p = t.phase || "Unassigned";
    if (!acc[p]) acc[p] = { total: 0, billable: 0 };
    acc[p].total += parseFloat(t.hours) || 0;
    if (t.billable) acc[p].billable += parseFloat(t.hours) || 0;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-4 gap-3">
        <MiniMetric label="Total Hours" value={totalHours.toFixed(1)} icon={Timer} />
        <MiniMetric label="Billable Hours" value={billableHours.toFixed(1)} icon={DollarSign} />
        <MiniMetric label="Entries" value={engagement.timeEntries.length} icon={ClipboardList} />
        <MiniMetric label="Realized Rate" value={`$${fmt(realizedRate)}/hr`} icon={Target} />
      </div>

      {Object.keys(byPhase).length > 0 && (
        <div className="p-4 border border-stone-300 rounded-sm">
          <h5 className="serif text-sm font-semibold text-stone-900 mb-2">Hours by Phase</h5>
          <div className="space-y-1.5">
            {Object.entries(byPhase).map(([phase, stats]) => (
              <div key={phase} className="flex items-center justify-between text-xs">
                <span className="sans text-stone-700">{phase}</span>
                <span className="mono num-tabular">
                  <span className="text-stone-900 font-semibold">{stats.total.toFixed(1)}</span>
                  <span className="text-stone-500 ml-2">({stats.billable.toFixed(1)} billable)</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="serif text-base font-semibold text-stone-900">Time Entries</h4>
          <button onClick={addEntry} className="sans text-xs font-medium px-3 py-1.5 bg-stone-900 text-sky-400 rounded-sm hover:bg-stone-800 flex items-center gap-1.5">
            <Plus size={13} /> Add Entry
          </button>
        </div>

        <div className="border border-stone-300 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-stone-900 text-sky-400">
              <tr>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Date</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Staff</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Phase</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Description</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">Hrs</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-center px-3 py-2.5">Bill</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {engagement.timeEntries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-stone-500 sans text-sm italic">
                    No time entries yet. Add one to start tracking hours.
                  </td>
                </tr>
              ) : (
                engagement.timeEntries.map(entry => (
                  <tr key={entry.id} className="border-t border-stone-200">
                    <td className="px-2 py-1.5">
                      <input type="date" value={entry.date} onChange={e => updateEntry(entry.id, { date: e.target.value })}
                        className="w-full px-1.5 py-1 border border-stone-200 rounded-sm mono text-[11px] focus:outline-none focus:border-stone-900" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={entry.staff} onChange={e => updateEntry(entry.id, { staff: e.target.value })} placeholder="Name"
                        className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" />
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={entry.phase} onChange={e => updateEntry(entry.id, { phase: e.target.value })}
                        className="w-full px-1.5 py-1 border border-stone-200 rounded-sm mono text-[10px] focus:outline-none focus:border-stone-900">
                        <option value="">—</option>
                        <option>Scope</option>
                        <option>Engagement</option>
                        <option>Screen</option>
                        <option>Tree</option>
                        <option>Requests</option>
                        <option>Interviews</option>
                        <option>Projects</option>
                        <option>Wages</option>
                        <option>Supplies</option>
                        <option>Contracts</option>
                        <option>Compute</option>
                        <option>Report</option>
                        <option>Review</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={entry.description} onChange={e => updateEntry(entry.id, { description: e.target.value })} placeholder="Work performed"
                        className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="number" step="0.25" value={entry.hours} onChange={e => updateEntry(entry.id, { hours: e.target.value })} placeholder="0"
                        className="w-full px-2 py-1 border border-stone-200 rounded-sm mono num-tabular text-xs text-right focus:outline-none focus:border-stone-900" />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={entry.billable} onChange={e => updateEntry(entry.id, { billable: e.target.checked })} />
                    </td>
                    <td className="px-2">
                      <button onClick={() => removeEntry(entry.id)} className="text-stone-400 hover:text-rose-600">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {engagement.timeEntries.length > 0 && (
              <tfoot className="bg-stone-100 border-t-2 border-stone-900">
                <tr>
                  <td colSpan={4} className="px-3 py-2 sans text-xs font-semibold text-stone-900 uppercase tracking-widest">Total</td>
                  <td className="px-3 py-2 text-right mono num-tabular text-sm font-bold text-stone-900">{totalHours.toFixed(1)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function StallsTab({ engagement, update, stalledLists, requestLists }) {
  return (
    <div className="space-y-5">
      <div className="p-4 bg-sky-50 border border-sky-400 rounded-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} className="text-sky-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="serif text-base font-bold text-stone-900 mb-1">Stall Detection</h4>
            <p className="sans text-xs text-stone-700 leading-relaxed">
              A request list is flagged as <strong>stalled</strong> when it has items in "requested" status that have been outstanding for more than the threshold number of days without client response. Stalled work is the single biggest cause of missed deadlines — use this view to identify what needs escalation.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <label className="sans text-xs font-semibold text-stone-700">Stall Threshold:</label>
              <input type="number" value={engagement.stallThresholdDays} onChange={e => update("stallThresholdDays", e.target.value)} min="1"
                className="w-20 px-2 py-1 border border-stone-300 bg-white rounded-sm mono num-tabular text-xs focus:outline-none focus:border-stone-900" />
              <span className="sans text-xs text-stone-600">days</span>
            </div>
          </div>
        </div>
      </div>

      {stalledLists.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-stone-300 rounded-sm">
          <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-3" />
          <p className="serif text-lg font-semibold text-stone-900">No stalls detected</p>
          <p className="sans text-sm text-stone-600 mt-1">
            {requestLists.length === 0
              ? "No active request lists to monitor."
              : `${requestLists.length} active request list${requestLists.length > 1 ? "s" : ""} — all within the ${engagement.stallThresholdDays}-day threshold.`}
          </p>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="serif text-base font-bold text-stone-900">Stalled Request Lists ({stalledLists.length})</h4>
          </div>
          <div className="space-y-3">
            {stalledLists.map(list => {
              const daysOld = Math.floor((new Date() - new Date(list.createdAt)) / (1000 * 60 * 60 * 24));
              const requested = list.items.filter(i => i.status === "requested");
              const received = list.items.filter(i => i.status === "received");
              return (
                <div key={list.id} className="border-2 border-rose-400 bg-rose-50 rounded-sm overflow-hidden">
                  <div className="p-4 bg-rose-100 border-b border-rose-300 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <AlertTriangle size={14} className="text-rose-700" />
                        <span className="mono text-[9px] font-bold uppercase tracking-widest text-rose-900 bg-rose-200 px-1.5 py-0.5 rounded-sm">STALLED {daysOld}d</span>
                      </div>
                      <div className="serif text-base font-bold text-stone-900">{list.name}</div>
                      <div className="sans text-xs text-stone-700 mt-0.5">Created {new Date(list.createdAt).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right">
                      <div className="mono text-xs text-stone-700"><strong>{requested.length}</strong> pending reply</div>
                      <div className="mono text-xs text-stone-600">{received.length} of {list.items.length} received</div>
                    </div>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="sans text-[10px] font-semibold uppercase tracking-widest text-stone-700 mb-2">Outstanding items ({requested.length})</div>
                    <ul className="space-y-1">
                      {requested.slice(0, 5).map(item => (
                        <li key={item.id} className="sans text-xs text-stone-800 flex gap-2">
                          <span className="mono text-[10px] uppercase text-stone-500 shrink-0 mt-0.5">{item.category}</span>
                          <span className="leading-snug">{item.text}</span>
                        </li>
                      ))}
                      {requested.length > 5 && (
                        <li className="sans text-xs text-stone-500 italic">...and {requested.length - 5} more</li>
                      )}
                    </ul>
                    <div className="mt-3 pt-3 border-t border-stone-200 flex items-center gap-2">
                      <AlertCircle size={12} className="text-rose-600" />
                      <span className="sans text-xs text-stone-700">
                        <strong>Recommended action:</strong> Follow up with the client contact, escalate to partner, or mark items as N/A if client is nonresponsive.
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {requestLists.length > 0 && (
        <div>
          <h4 className="serif text-base font-semibold text-stone-900 mb-3">All Request Lists</h4>
          <div className="border border-stone-300 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-100">
                <tr>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2 border-b border-stone-200">List</th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2 border-b border-stone-200">Created</th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2 border-b border-stone-200">Progress</th>
                  <th className="sans text-[10px] font-semibold uppercase tracking-widest text-center px-3 py-2 border-b border-stone-200">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {requestLists.map(list => {
                  const received = list.items.filter(i => i.status === "received").length;
                  const daysOld = Math.floor((new Date() - new Date(list.createdAt)) / (1000 * 60 * 60 * 24));
                  const isStalled = stalledLists.some(s => s.id === list.id);
                  const isComplete = list.items.length > 0 && received === list.items.length;
                  return (
                    <tr key={list.id} className="border-t border-stone-200">
                      <td className="px-3 py-2 serif text-sm text-stone-900">{list.name}</td>
                      <td className="px-3 py-2 mono text-[11px] text-stone-500">{new Date(list.createdAt).toLocaleDateString()} ({daysOld}d ago)</td>
                      <td className="px-3 py-2 text-right mono num-tabular text-xs text-stone-900">{received}/{list.items.length}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`mono text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                          isComplete ? "bg-emerald-600 text-white" :
                          isStalled ? "bg-rose-600 text-white" :
                          "bg-sky-500 text-white"
                        }`}>
                          {isComplete ? "Complete" : isStalled ? "Stalled" : "Active"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniMetric({ label, value, icon: Icon }) {
  return (
    <div className="p-3 bg-white border border-stone-300 rounded-sm">
      <div className="flex items-center justify-between mb-1">
        <span className="sans text-[9px] uppercase tracking-widest font-semibold text-stone-500">{label}</span>
        {Icon && <Icon size={12} className="text-stone-400" />}
      </div>
      <div className="serif text-xl font-bold text-stone-900 num-tabular mono">{value}</div>
    </div>
  );
}


// ============================================================
// PHASE 3: SCREEN — with notes + uploads per question + template pre-fills
// ============================================================

// TemplatePicker — dropdown that surfaces pre-written IRS-defensible
// response templates based on (industry, question, answer) triple.
// Non-technical staff pick a template; it drops into the notes textarea
// with {placeholders} they then fill in from the client interview.
function TemplatePicker({ questionKey, answer, industry, onPick, currentNote }) {
  const [open, setOpen] = useState(false);

  // Determine which answer bucket to show templates for
  const answerKey = answer === true ? "YES" : answer === false ? "NO" : null;
  const industryTemplates = SCREEN_TEMPLATES[questionKey]?.[industry] || SCREEN_TEMPLATES[questionKey]?.generic || {};
  const genericTemplates = SCREEN_TEMPLATES[questionKey]?.generic || {};

  // For "UNSURE" we don't have templates, so disable
  if (!answerKey) {
    return null;
  }

  const industryVariants = industryTemplates[answerKey] || [];
  const genericVariants = genericTemplates[answerKey] || [];

  // Combine: industry-specific first, then generic fallback (deduped)
  const variants = [
    ...industryVariants.map(t => ({ text: t, source: "industry" })),
    ...genericVariants.filter(g => !industryVariants.includes(g)).map(t => ({ text: t, source: "generic" })),
  ];

  if (variants.length === 0) {
    return null;
  }

  const handlePick = (text) => {
    if (currentNote && currentNote.trim() && !confirm("Replace existing notes with this template?")) {
      setOpen(false);
      return;
    }
    onPick(text);
    setOpen(false);
  };

  const industryLabel = SCREEN_INDUSTRIES.find(i => i.id === industry)?.label || "Generic";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="sans text-[10px] font-bold px-2 py-1 border border-sky-500 text-sky-700 bg-white hover:bg-sky-50 rounded-sm flex items-center gap-1 transition-colors"
        title={`Pre-fill from ${industryLabel} templates for ${answerKey}`}
      >
        <BookOpen size={10} />
        Pre-fill {answerKey} response
        <ChevronRight size={10} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>

      {open && (
        <>
          {/* Click-outside backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>

          <div className="absolute right-0 top-full mt-1 w-[560px] max-w-[calc(100vw-4rem)] bg-white border border-stone-300 rounded-sm shadow-xl z-50 max-h-[500px] overflow-y-auto">
            <div className="px-3 py-2 border-b border-stone-200 bg-stone-900 text-white flex items-center justify-between sticky top-0">
              <div>
                <div className="sans text-[11px] font-bold text-sky-400">{answerKey} response templates</div>
                <div className="sans text-[10px] text-stone-400">Industry: {industryLabel} · {variants.length} variant{variants.length !== 1 ? "s" : ""}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-white">
                <X size={14} />
              </button>
            </div>

            <div className="p-2 space-y-2">
              {variants.map((v, i) => (
                <button
                  key={i}
                  onClick={() => handlePick(v.text)}
                  className="w-full text-left p-3 border border-stone-200 rounded-sm hover:border-sky-500 hover:bg-sky-50/40 transition-colors group"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`mono text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${
                      v.source === "industry" ? "bg-sky-100 text-sky-800" : "bg-stone-100 text-stone-600"
                    }`}>
                      {v.source === "industry" ? industryLabel : "Generic"} · variant {i + 1}
                    </span>
                    <span className="sans text-[10px] text-sky-600 group-hover:text-sky-800 font-semibold opacity-0 group-hover:opacity-100 transition-opacity ml-auto">Use this →</span>
                  </div>
                  <p className="sans text-[11px] text-stone-700 leading-relaxed">{v.text}</p>
                </button>
              ))}
            </div>

            <div className="px-3 py-2 border-t border-stone-200 bg-stone-50">
              <div className="flex items-start gap-2">
                <Info size={11} className="text-stone-500 shrink-0 mt-0.5" />
                <p className="sans text-[10px] text-stone-600 leading-relaxed">
                  Templates contain <span className="mono font-semibold text-stone-900">{`{placeholders}`}</span> that you must replace with client-specific facts before finalizing. Template language ties to the IRS four-part test; your job is to fill in the who/what/when/where.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ScreenPhase({ screen, setScreen, documents, addDocuments, deleteDocument, onCite }) {
  const update = (k, v) => setScreen({ ...screen, [k]: v });
  const updateNote = (k, v) => setScreen({ ...screen, notes: { ...screen.notes, [k]: v } });
  const updateExclusionNote = (id, v) => setScreen({ ...screen, exclusionNotes: { ...screen.exclusionNotes, [id]: v } });

  const toggleExclusion = (id) => {
    setScreen({
      ...screen,
      exclusions: screen.exclusions.includes(id)
        ? screen.exclusions.filter(x => x !== id)
        : [...screen.exclusions, id]
    });
  };

  const yesCount = [screen.hasDevelopment, screen.hasUncertainty, screen.hasExperimentation, screen.hasTechBasis, screen.hasDocumentation].filter(v => v === true).length;
  const noCount = [screen.hasDevelopment, screen.hasUncertainty, screen.hasExperimentation, screen.hasTechBasis, screen.hasDocumentation].filter(v => v === false).length;
  const decision = yesCount >= 4 && noCount <= 1 ? "strong" : yesCount >= 3 ? "possible" : noCount >= 2 ? "weak" : null;

  const industry = screen.industry || "generic";
  const setIndustry = (id) => setScreen({ ...screen, industry: id });

  const questions = [
    { k: "hasDevelopment", q: "Does the company develop or improve products, processes, software, formulations, or techniques?", placeholder: "Example: Client develops firmware for industrial IoT sensors — verified via engineering team interview 10/15/25." },
    { k: "hasUncertainty", q: "Was there technical uncertainty at the outset of the work?", cite: "1.41-4", placeholder: "Example: Team didn't know if low-power radio could reach required range while meeting FCC limits — cited in design spec v1.0 dated Jan 2025." },
    { k: "hasExperimentation", q: "Did employees evaluate alternatives — design, engineer, test, model, iterate?", placeholder: "Example: Three different antenna topologies were prototyped and bench-tested over Q1-Q2; lab logs preserved." },
    { k: "hasTechBasis", q: "Did the work fundamentally rely on engineering, computer science, or physical/biological science?", cite: "41(d)(1)", placeholder: "Example: RF engineering and embedded systems programming (C++). Two staff hold EE degrees." },
    { k: "hasDocumentation", q: "Do contemporaneous records exist? (tickets, design docs, test reports, lab notes, git, specs)", placeholder: "Example: Jira project SENSOR-2025 contains 412 tickets, linked to git commits. Test plan docs in SharePoint." },
  ];

  return (
    <div className="p-8">
      <PhaseHeader number="04" title="Initial Qualification Screen" description="Go/no-go gating. For each question, capture notes that justify the yes/no decision and attach supporting evidence." />

      {/* Industry selector for pre-fill templates */}
      <div className="mt-6 p-4 border border-sky-300 bg-sky-50 rounded-sm">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-sky-600 text-white rounded-sm flex items-center justify-center shrink-0">
            <BookOpen size={16} />
          </div>
          <div className="flex-1">
            <div className="sans text-sm font-bold text-stone-900 mb-1">Client Industry</div>
            <p className="sans text-xs text-stone-700 mb-3">
              Select the client's industry to unlock pre-written IRS-defensible response templates. Non-technical staff can start from a template and fill in the client-specific blanks — faster, more consistent, and ties cleanly to the §41(d) four-part test.
            </p>
            <div className="flex flex-wrap gap-2">
              {SCREEN_INDUSTRIES.map(ind => (
                <button
                  key={ind.id}
                  onClick={() => setIndustry(ind.id)}
                  className={`sans text-[11px] font-medium px-2.5 py-1.5 rounded-sm border transition-colors ${
                    industry === ind.id
                      ? "bg-stone-900 text-sky-400 border-stone-900"
                      : "bg-white text-stone-700 border-stone-300 hover:border-sky-500 hover:text-sky-700"
                  }`}
                >
                  {ind.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {questions.map(({ k, q, cite, placeholder }) => (
          <div key={k} className="border border-stone-200 rounded-sm overflow-hidden">
            <div className="p-4 bg-stone-50/50">
              <div className="flex items-start justify-between gap-4 mb-3">
                <p className="sans text-sm text-stone-800 leading-snug flex-1 font-medium">{q}</p>
                {cite && <CiteBadge id={cite} onClick={onCite} />}
              </div>
              <TriToggle current={screen[k]} onChange={v => update(k, v)} />
            </div>

            {screen[k] !== null && (
              <div className="p-4 border-t border-stone-200 bg-white space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest flex items-center gap-1">
                      <MessageSquare size={10} />
                      Basis for {screen[k] === true ? "YES" : screen[k] === false ? "NO" : "UNSURE"} — facts, interviews, documents supporting this answer
                    </div>
                    <TemplatePicker
                      questionKey={k}
                      answer={screen[k]}
                      industry={industry}
                      onPick={(text) => updateNote(k, text)}
                      currentNote={screen.notes[k] || ""}
                    />
                  </div>
                  <textarea
                    value={screen.notes[k] || ""}
                    onChange={e => updateNote(k, e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-xs leading-relaxed focus:outline-none focus:border-stone-900 min-h-[70px]"
                    placeholder={placeholder}
                  />
                  {screen.notes[k] && screen.notes[k].includes("{") && (
                    <div className="mt-1 flex items-center gap-1.5">
                      <AlertTriangle size={10} className="text-amber-600" />
                      <span className="sans text-[10px] text-amber-700 italic">Template still contains <span className="mono font-semibold">{`{placeholders}`}</span> — replace with client-specific facts before finalizing.</span>
                    </div>
                  )}
                </div>
                <FileAttachmentZone
                  linkedTo={{ scope: "screen", question: k }}
                  category="Screening Evidence"
                  documents={documents}
                  addDocuments={addDocuments}
                  deleteDocument={deleteDocument}
                  label="Supporting documents for this answer"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8">
        <h4 className="serif text-base font-semibold text-stone-900 mb-2 flex items-center gap-2">
          Possible Exclusions <CiteBadge id="41(d)(4)" onClick={onCite} />
        </h4>
        <p className="sans text-xs text-stone-600 mb-4">Check any category that may apply. For each check, add notes explaining why and attach evidence.</p>
        <div className="space-y-2">
          {EXCLUSIONS.map(ex => (
            <div key={ex.id} className={`border rounded-sm transition-colors ${screen.exclusions.includes(ex.id) ? "border-rose-400" : "border-stone-200"}`}>
              <label className={`flex items-start gap-2 p-3 cursor-pointer ${screen.exclusions.includes(ex.id) ? "bg-rose-50" : "bg-white hover:bg-stone-50"}`}>
                <input type="checkbox" checked={screen.exclusions.includes(ex.id)} onChange={() => toggleExclusion(ex.id)} className="mt-0.5" />
                <span className="sans text-xs text-stone-800 leading-snug flex-1">{ex.text}</span>
                <CiteBadge id={ex.cite} onClick={onCite} />
              </label>
              {screen.exclusions.includes(ex.id) && (
                <div className="p-3 border-t border-rose-200 bg-rose-50/50 space-y-2">
                  <textarea
                    value={screen.exclusionNotes[ex.id] || ""}
                    onChange={e => updateExclusionNote(ex.id, e.target.value)}
                    className="w-full px-3 py-2 border border-rose-300 bg-white rounded-sm sans text-xs focus:outline-none focus:border-rose-600 min-h-[50px]"
                    placeholder="Describe the specific facts triggering this exclusion and how much of the work it affects..."
                  />
                  <FileAttachmentZone
                    linkedTo={{ scope: "screen", exclusion: ex.id }}
                    category="Exclusion Evidence"
                    documents={documents}
                    addDocuments={addDocuments}
                    deleteDocument={deleteDocument}
                    compact
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {decision && (
        <div className={`mt-8 p-5 border-l-4 rounded-r-sm ${
          decision === "strong" ? "border-emerald-500 bg-emerald-50" :
          decision === "possible" ? "border-sky-500 bg-sky-50" :
          "border-rose-500 bg-rose-50"
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {decision === "strong" ? <CheckCircle2 size={16} className="text-emerald-700" /> :
             decision === "possible" ? <AlertTriangle size={16} className="text-sky-700" /> :
             <AlertCircle size={16} className="text-rose-700" />}
            <h4 className="serif text-base font-bold text-stone-900">
              Go/No-Go: {decision === "strong" ? "Strong Candidate" : decision === "possible" ? "Possible — Documentation-Sensitive" : "Weak / Reconsider"}
            </h4>
          </div>
          <p className="sans text-xs text-stone-700 leading-relaxed">
            Based on screening responses ({yesCount} yes, {noCount} no).
            {decision === "strong" && " Proceed to full project qualification."}
            {decision === "possible" && " Proceed only if the client can strengthen documentation during interviews."}
            {decision === "weak" && " Recommend declining or limiting scope until more evidence is produced."}
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHASE 3: DECISION TREE REFERENCE
// ============================================================
// Data models for the decision tree - 7-step gating structure
// per IRC §41(d)(1)(A)-(C) and §41(d)(4)(A)-(H) with Treas. Reg. §1.41-4
const TREE_STEPS = [
  {
    n: 0,
    label: "Define the Activity",
    prompt: "Have you defined a specific activity rather than just a project title?",
    passLabel: "Specific activity identified",
    failLabel: "Still at project level",
    failGuidance: "Break the project into distinct activity buckets (design, prototyping, testing, rollout) before proceeding. Qualification is activity-level, not project-level.",
    cite: "41(d)(1)",
    color: "stone",
    detail: "The statute defines qualified research at the activity level. A single project can contain both qualified and nonqualified work. Split mixed phases: design alternatives, prototype builds, validation testing, commercial rollout, and maintenance should each be evaluated separately.",
  },
  {
    n: 1,
    label: "Business Component",
    prompt: "Is the activity intended to develop or improve a business component?",
    passLabel: "Yes — improves a business component",
    failLabel: "No — not tied to a component",
    failGuidance: "Not qualified. Activity must connect to an identifiable new or improved business component (product, process, software, formulation, technique, or invention).",
    cite: "41(d)(1)",
    regCite: null,
    color: "amber",
    detail: "Evidence: project charter, engineering summary, design spec, product roadmap, technical requirements document. The activity doesn't qualify just because it is time-consuming or expensive — it must connect to an identifiable business component.",
    statutoryRef: "§41(d)(1)(B)(ii)",
  },
  {
    n: 2,
    label: "Technological in Nature",
    prompt: "Does the activity rely on physical science, biological science, engineering, or computer science?",
    passLabel: "Yes — technological basis",
    failLabel: "No — nontechnical basis",
    failGuidance: "Not qualified. Market preference work, branding, layout/aesthetic redesign, pricing, user surveys, and nontechnical process preferences fail this element.",
    cite: "41(d)(1)",
    regCite: "1.41-4",
    color: "amber",
    detail: "Strong 'YES' indicators: engineering calculations, system architecture analysis, lab testing, materials/formulation analysis, computer science problem-solving, tolerance/performance/reliability testing. Strong 'NO': market research, branding, pricing analysis, UX preference surveys.",
    statutoryRef: "§41(d)(1)(B)(i); Treas. Reg. §1.41-4(a)(4)",
  },
  {
    n: 3,
    label: "Uncertainty at Outset",
    prompt: "Was there uncertainty about capability, method, or appropriate design at the start?",
    passLabel: "Yes — documented uncertainty",
    failLabel: "No — no real uncertainty",
    failGuidance: "Not qualified. If the team knew the capability, method, AND design at the outset, the activity is routine implementation rather than experimentation.",
    cite: "1.41-4",
    color: "amber",
    color: "amber",
    detail: "Three kinds of uncertainty — any one is sufficient: CAPABILITY (did we know the result could be achieved?), METHOD (did we know how to achieve it?), DESIGN (did we know which configuration would work?). Evidence: technical meeting notes, design comparison docs, experiment logs, failed test records, issue trackers showing unresolved technical questions.",
    statutoryRef: "Treas. Reg. §1.41-4(a)(3)(i)",
  },
  {
    n: 4,
    label: "Evaluate Alternatives",
    prompt: "Did the activity evaluate one or more alternatives to resolve the uncertainty?",
    passLabel: "Yes — alternatives evaluated",
    failLabel: "No — single path executed",
    failGuidance: "Not qualified. Implementing a known solution, executing a predetermined design, checklist rollout, or simple debugging without real uncertainty do not count.",
    cite: "1.41-4",
    color: "amber",
    detail: "What counts: comparing multiple designs, testing multiple formulas, trying different algorithms, modeling alternative methods, prototype iterations, failure analysis followed by redesign. What doesn't: rote confirmation testing after the design was settled, routine execution.",
    statutoryRef: "Treas. Reg. §1.41-4(a)(5)",
  },
  {
    n: 5,
    label: "Substantially All Experimentation",
    prompt: "Did substantially all of the activity consist of testing, evaluating, modeling, simulating, prototyping, or iterating?",
    passLabel: "Yes — substantially all",
    failLabel: "Mixed — only some phases",
    failGuidance: "Do NOT qualify the entire activity. Isolate the experimental subactivities and test those separately. Final documentation, rollout, and maintenance phases usually fail this step on their own.",
    cite: "41(d)(1)",
    color: "amber",
    detail: "This is where mixed activities get pruned. A project containing design alternatives + prototype testing + final rollout + maintenance should only have the experimental portions claimed. Final documentation of chosen design may support experimentation if part of the process, but not automatically standing alone.",
    statutoryRef: "§41(d)(1)(C)",
  },
  {
    n: 6,
    label: "Qualified Purpose",
    prompt: "Was the purpose to improve function, performance, reliability, or quality — rather than style, taste, cosmetic, or seasonal design?",
    passLabel: "Yes — qualified purpose",
    failLabel: "No — style/cosmetic only",
    failGuidance: "Not qualified. Research for style, taste, cosmetic, or seasonal design factors is not undertaken for a qualified purpose per Treas. Reg. §1.41-4(a)(5).",
    cite: "1.41-4",
    color: "amber",
    detail: "YES examples: better performance, improved throughput, greater reliability, improved quality, reduced defect rate, stronger technical functionality. NO examples: cosmetic redesign, branding refresh, visual re-theme, seasonal packaging, purely aesthetic UI changes without technical uncertainty.",
    statutoryRef: "Treas. Reg. §1.41-4(a)(5)",
  },
  {
    n: 7,
    label: "Exclusion Screen",
    prompt: "Does any statutory exclusion under §41(d)(4)(A)-(H) apply?",
    passLabel: "No exclusion applies",
    failLabel: "Exclusion applies",
    failGuidance: "Exclude the activity (in whole or in part) to the extent the exclusion applies. Even an otherwise experimental activity fails if it falls within a statutory exclusion.",
    cite: "41(d)(4)",
    color: "rose",
    detail: "Run every activity through all eight exclusions: post-production work, customer adaptation, duplication, surveys/studies, software special rule, foreign research, social sciences, funded research. If ANY applies, the activity is not qualified to that extent.",
    statutoryRef: "§41(d)(4)(A)-(H)",
  },
];

const TREE_EXCLUSIONS = [
  { id: "A", label: "§41(d)(4)(A)", title: "After Commercial Production", q: "Did this activity occur after commercial production began?", examples: "Post-launch bug fixes, production support, post-commercialization marketing work" },
  { id: "B", label: "§41(d)(4)(B)", title: "Customer Adaptation", q: "Was the activity just customizing an existing solution for one customer?", examples: "Configuring an existing product for a specific customer's preference without technical uncertainty" },
  { id: "C", label: "§41(d)(4)(C)", title: "Duplication", q: "Was the activity reproducing something already known from examination or public specs?", examples: "Reverse-engineering a component from public information; duplicating a competitor's solution" },
  { id: "D", label: "§41(d)(4)(D)", title: "Surveys / Studies", q: "Is this really technical research, or just market/user research?", examples: "Customer preference surveys, market research, pricing studies, focus groups" },
  { id: "E", label: "§41(d)(4)(E)", title: "Computer Software Special Rule", q: "Does the activity involve computer software that triggers the special software limitation?", examples: "Internal-use software may need separate analysis under the IUS rules" },
  { id: "F", label: "§41(d)(4)(F)", title: "Foreign Research", q: "Was the activity physically performed outside the United States?", examples: "Work performed at a foreign development center or by foreign-based engineers" },
  { id: "G", label: "§41(d)(4)(G)", title: "Social Sciences", q: "Is the activity based on social sciences rather than physical/biological sciences, engineering, or computer science?", examples: "Economics research, behavioral research, sociology, arts, or humanities" },
  { id: "H", label: "§41(d)(4)(H)", title: "Funded Research", q: "Was the activity funded by another person or governmental entity?", examples: "Customer-funded development where the customer bears risk of failure; grant-funded research without retention of rights" },
];

// Example activities to illustrate the tree
const TREE_EXAMPLES = [
  {
    id: "ex1",
    title: "IoT sensor firmware — new low-power protocol",
    outcome: "qualified",
    walkthrough: {
      0: { pass: true, note: "Isolated activity: evaluating RF protocols for battery-powered sensor." },
      1: { pass: true, note: "New sensor product (business component)." },
      2: { pass: true, note: "Relies on RF engineering and embedded computer science." },
      3: { pass: true, note: "Method uncertainty: didn't know whether Bluetooth LE, LoRa, or custom protocol could meet range and power budget." },
      4: { pass: true, note: "Three protocols prototyped and bench-tested over Q1-Q2; lab logs preserved." },
      5: { pass: true, note: "Substantially all: design, prototype, test, iterate, redesign loop." },
      6: { pass: true, note: "Purpose: improve performance (range, battery life) — a technical objective." },
      7: { pass: true, note: "No exclusions apply; work done in U.S., self-funded, pre-production." },
    },
  },
  {
    id: "ex2",
    title: "Website redesign for new branding guidelines",
    outcome: "excluded",
    walkthrough: {
      0: { pass: true, note: "Activity: UI redesign to match new brand guidelines." },
      1: { pass: true, note: "Website is a business component." },
      2: { pass: false, note: "Driven by branding preference, not engineering or computer science principles." },
      3: { pass: false, note: "No technical uncertainty — implementing a known design." },
      4: { pass: false, note: "No real alternatives tested — executing design decisions from marketing." },
      5: { pass: false, note: "Not experimental — execution of predetermined design." },
      6: { pass: false, note: "Purpose is cosmetic/style, not function/performance/reliability/quality." },
      7: { pass: false, note: "Would be caught by cosmetic-design exclusion even if earlier steps passed." },
    },
  },
  {
    id: "ex3",
    title: "Manufacturing line — improve defect rate from 3% to <1%",
    outcome: "qualified",
    walkthrough: {
      0: { pass: true, note: "Activity isolated: process improvement to reduce defect rate." },
      1: { pass: true, note: "Manufacturing process is a business component." },
      2: { pass: true, note: "Relies on mechanical engineering and materials science." },
      3: { pass: true, note: "Method uncertainty: root cause unknown; multiple hypotheses." },
      4: { pass: true, note: "Evaluated alternative fixtures, torque settings, and material suppliers." },
      5: { pass: true, note: "Design of experiments, A/B trials, statistical analysis." },
      6: { pass: true, note: "Purpose: improve quality/reliability — a qualified purpose." },
      7: { pass: true, note: "Work performed in U.S.; self-funded; pre-production improvement (not adapting for one customer)." },
    },
  },
  {
    id: "ex4",
    title: "Customer-funded prototype built to customer spec",
    outcome: "excluded",
    walkthrough: {
      0: { pass: true, note: "Activity: build prototype per customer-specified requirements." },
      1: { pass: true, note: "Prototype is a potential business component." },
      2: { pass: true, note: "Engineering work involved." },
      3: { pass: true, note: "Some design uncertainty exists." },
      4: { pass: true, note: "Several configurations tested." },
      5: { pass: true, note: "Substantially all experimental." },
      6: { pass: true, note: "Purpose: performance improvement." },
      7: { pass: false, note: "EXCLUDED under §41(d)(4)(H) — research was funded by customer who retained rights and bore economic risk. Check contract terms carefully." },
    },
  },
];

function DecisionTreePhase({ onCite }) {
  const [activeExample, setActiveExample] = useState(null);
  const [walkthrough, setWalkthrough] = useState({});
  const [expandedStep, setExpandedStep] = useState(null);

  const loadExample = (ex) => {
    setActiveExample(ex.id);
    setWalkthrough(ex.walkthrough);
    setExpandedStep(null);
  };

  const clearWalkthrough = () => {
    setActiveExample(null);
    setWalkthrough({});
    setExpandedStep(null);
  };

  const setStepResult = (stepN, pass, note) => {
    setWalkthrough(prev => ({ ...prev, [stepN]: { pass, note: note || "" } }));
    setActiveExample(null); // no longer matching example
  };

  // Determine final outcome based on walkthrough
  const outcome = (() => {
    const stepResults = TREE_STEPS.map(s => walkthrough[s.n]);
    if (stepResults.every(r => !r)) return null; // nothing filled
    // Any failure = not qualified / stop (except step 0 and step 5 which branch to split)
    for (let i = 0; i < TREE_STEPS.length; i++) {
      const r = stepResults[i];
      if (!r) return "incomplete"; // haven't reached yet
      if (r.pass === false) {
        if (TREE_STEPS[i].n === 7) return "excluded";
        if (TREE_STEPS[i].n === 5) return "partial";
        return "not_qualified";
      }
    }
    return "qualified";
  })();

  const outcomeConfig = {
    qualified: { label: "QUALIFIED RESEARCH ACTIVITY", color: "emerald", icon: CheckCircle2, desc: "Activity passes all seven gates and no exclusions apply. Document all facts in the project narrative and proceed to QRE mapping." },
    not_qualified: { label: "NOT QUALIFIED", color: "rose", icon: XCircle, desc: "A gating requirement was not met. Do not include this activity in QRE calculations." },
    excluded: { label: "EXCLUDED UNDER §41(d)(4)", color: "rose", icon: Shield, desc: "Even though the experimental elements are present, a statutory exclusion applies. Exclude to the extent the exclusion applies." },
    partial: { label: "PARTIAL — ISOLATE EXPERIMENTAL SUBACTIVITIES", color: "amber", icon: AlertTriangle, desc: "The full activity does not qualify, but experimental portions within it may. Break the activity into subactivities and re-run this tree on each." },
    incomplete: { label: "IN PROGRESS", color: "stone", icon: HelpCircle, desc: "Continue answering each step of the tree." },
  };

  const currentOutcome = outcome ? outcomeConfig[outcome] : null;

  return (
    <div className="p-8">
      <PhaseHeader number="03" title="Activity Qualification Decision Tree" description="The seven-gate test for whether an activity is qualified research under IRC §41. Reference framework for the full study — run individual activities through it to train judgment and document reasoning." />

      <div className="mb-6 p-4 bg-sky-50 border border-sky-400 rounded-sm">
        <div className="flex items-start gap-2">
          <Info size={14} className="text-sky-700 shrink-0 mt-0.5" />
          <div className="sans text-xs text-sky-900 leading-relaxed">
            <strong className="block mb-1">Reference vs. production use.</strong>
            This phase is a teaching/reference tool — the four-part test that actually drives per-project qualification lives in the Project Qualification phase. Use this tree to (1) train new staff, (2) walk tricky activities through the full statutory gating, (3) document reasoning for edge cases, and (4) anchor your discussion with clients or reviewers.
          </div>
        </div>
      </div>

      {/* EXAMPLE ACTIVITIES */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="serif text-sm font-semibold text-stone-900">Walk Through an Example Activity</h4>
          {(activeExample || Object.keys(walkthrough).length > 0) && (
            <button onClick={clearWalkthrough} className="sans text-xs font-medium px-2 py-1 border border-stone-300 rounded-sm hover:bg-stone-50">
              Clear walkthrough
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {TREE_EXAMPLES.map(ex => (
            <button
              key={ex.id}
              onClick={() => loadExample(ex)}
              className={`p-3 border rounded-sm text-left transition-all ${
                activeExample === ex.id
                  ? "border-stone-900 bg-stone-900 text-sky-400"
                  : "border-stone-300 bg-white hover:border-stone-500"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                {ex.outcome === "qualified" ? (
                  <CheckCircle2 size={13} className={activeExample === ex.id ? "text-sky-400" : "text-emerald-600"} />
                ) : (
                  <XCircle size={13} className={activeExample === ex.id ? "text-sky-400" : "text-rose-600"} />
                )}
                <span className="mono text-[9px] uppercase tracking-widest font-bold">
                  {ex.outcome === "qualified" ? "Qualifies" : "Excluded"}
                </span>
              </div>
              <div className="serif text-sm font-semibold leading-snug">{ex.title}</div>
            </button>
          ))}
        </div>
      </div>

      {/* DECISION TREE */}
      <div className="mb-6">
        <h4 className="serif text-sm font-semibold text-stone-900 mb-3">The Seven Gates</h4>
        <div className="space-y-2">
          {TREE_STEPS.map((step, idx) => {
            const result = walkthrough[step.n];
            const isExpanded = expandedStep === step.n;
            const isBlocked = idx > 0 && TREE_STEPS.slice(0, idx).some(s => walkthrough[s.n]?.pass === false && s.n !== 5);
            const stateColor = !result ? "stone" : result.pass ? "emerald" : "rose";

            return (
              <div
                key={step.n}
                className={`border rounded-sm overflow-hidden transition-all ${
                  stateColor === "emerald" ? "border-emerald-400" :
                  stateColor === "rose" ? "border-rose-400" :
                  isBlocked ? "border-stone-200 opacity-50" :
                  "border-stone-300"
                }`}
              >
                {/* Step header */}
                <button
                  onClick={() => setExpandedStep(isExpanded ? null : step.n)}
                  className={`w-full text-left p-4 flex items-start justify-between gap-3 transition-colors ${
                    stateColor === "emerald" ? "bg-emerald-50" :
                    stateColor === "rose" ? "bg-rose-50" :
                    "bg-white hover:bg-stone-50"
                  }`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mono text-[10px] font-bold ${
                      stateColor === "emerald" ? "bg-emerald-600 text-white" :
                      stateColor === "rose" ? "bg-rose-600 text-white" :
                      "bg-stone-900 text-sky-400"
                    }`}>
                      {step.n === 0 ? "0" : step.n}
                    </div>
                    <div className="flex-1 pt-0.5">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="serif text-base font-bold text-stone-900">{step.label}</span>
                        {step.cite && <CiteBadge id={step.cite} onClick={e => { e?.stopPropagation?.(); onCite(step.cite); }} />}
                        {result && (
                          <span className={`mono text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${
                            result.pass ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                          }`}>
                            {result.pass ? "✓ PASS" : "✗ STOP"}
                          </span>
                        )}
                      </div>
                      <div className="sans text-xs text-stone-700 leading-snug">{step.prompt}</div>
                      {step.statutoryRef && (
                        <div className="mono text-[10px] text-stone-500 mt-1">{step.statutoryRef}</div>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={16} className={`text-stone-400 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                </button>

                {/* Expanded step detail */}
                {isExpanded && (
                  <div className="p-4 bg-stone-50 border-t border-stone-200 space-y-3">
                    <p className="serif text-sm text-stone-800 leading-relaxed">{step.detail}</p>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setStepResult(step.n, true)}
                        className={`p-3 border-2 rounded-sm text-left transition-colors ${
                          result?.pass === true
                            ? "border-emerald-600 bg-emerald-600 text-white"
                            : "border-stone-300 bg-white hover:border-emerald-500"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 size={13} />
                          <span className="sans text-xs font-bold uppercase tracking-wider">Pass</span>
                        </div>
                        <div className="sans text-xs leading-snug">{step.passLabel}</div>
                      </button>
                      <button
                        onClick={() => setStepResult(step.n, false)}
                        className={`p-3 border-2 rounded-sm text-left transition-colors ${
                          result?.pass === false
                            ? "border-rose-600 bg-rose-600 text-white"
                            : "border-stone-300 bg-white hover:border-rose-500"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle size={13} />
                          <span className="sans text-xs font-bold uppercase tracking-wider">Stop</span>
                        </div>
                        <div className="sans text-xs leading-snug">{step.failLabel}</div>
                      </button>
                    </div>

                    {result?.pass === false && (
                      <div className="p-3 bg-rose-100 border border-rose-300 rounded-sm">
                        <div className="sans text-[10px] font-bold uppercase tracking-widest text-rose-900 mb-1">Stop guidance</div>
                        <p className="sans text-xs text-rose-900 leading-relaxed">{step.failGuidance}</p>
                      </div>
                    )}

                    {result && (
                      <div>
                        <div className="sans text-[10px] font-bold uppercase tracking-widest text-stone-700 mb-1">Note / supporting facts</div>
                        <textarea
                          value={result.note || ""}
                          onChange={e => setWalkthrough(prev => ({ ...prev, [step.n]: { ...prev[step.n], note: e.target.value } }))}
                          placeholder="Document the specific facts supporting this answer..."
                          className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[60px]"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Connector arrow (except last step) */}
                {idx < TREE_STEPS.length - 1 && (
                  <div className="flex items-center justify-center py-1 bg-stone-50 border-t border-stone-200">
                    <ChevronRight size={12} className="text-stone-400 rotate-90" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* OUTCOME BANNER */}
      {currentOutcome && (
        <div className={`mb-6 p-5 rounded-sm border-l-4 ${
          currentOutcome.color === "emerald" ? "border-emerald-500 bg-emerald-50" :
          currentOutcome.color === "rose" ? "border-rose-500 bg-rose-50" :
          currentOutcome.color === "amber" ? "border-sky-500 bg-sky-50" :
          "border-stone-500 bg-stone-50"
        }`}>
          <div className="flex items-start gap-3">
            <currentOutcome.icon size={24} className={
              currentOutcome.color === "emerald" ? "text-emerald-700" :
              currentOutcome.color === "rose" ? "text-rose-700" :
              currentOutcome.color === "amber" ? "text-sky-700" :
              "text-stone-500"
            } />
            <div className="flex-1">
              <div className="serif text-xl font-bold text-stone-900 tracking-tight">{currentOutcome.label}</div>
              <p className="sans text-sm text-stone-700 leading-relaxed mt-1">{currentOutcome.desc}</p>
            </div>
          </div>
        </div>
      )}

      {/* STEP 7 EXCLUSIONS REFERENCE */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="serif text-sm font-semibold text-stone-900">Step 7 — Statutory Exclusions Reference</h4>
          <CiteBadge id="41(d)(4)" onClick={onCite} />
        </div>
        <p className="sans text-xs text-stone-600 mb-3 leading-relaxed">
          Even if an activity passes steps 1-6, it can still be excluded under §41(d)(4)(A)-(H). Every claimed activity must be tested against all eight exclusions.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {TREE_EXCLUSIONS.map(ex => (
            <div key={ex.id} className="p-3 border border-stone-200 rounded-sm bg-white">
              <div className="flex items-center gap-2 mb-1">
                <span className="mono text-[9px] font-bold uppercase tracking-widest text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-sm">{ex.label}</span>
                <span className="serif text-sm font-semibold text-stone-900">{ex.title}</span>
              </div>
              <p className="sans text-[11px] text-stone-700 leading-snug mb-1.5">{ex.q}</p>
              <p className="sans text-[10px] text-stone-500 italic leading-snug">
                <span className="not-italic font-semibold text-stone-600">Examples:</span> {ex.examples}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* PSEUDOCODE REFERENCE */}
      <div className="mb-6">
        <h4 className="serif text-sm font-semibold text-stone-900 mb-2">Encoded Logic</h4>
        <p className="sans text-xs text-stone-600 mb-2 leading-relaxed">Machine-readable version of the tree for SOP and training use:</p>
        <div className="p-4 bg-stone-900 text-stone-100 rounded-sm overflow-x-auto">
          <pre className="mono text-[11px] text-stone-200 leading-relaxed whitespace-pre">{`IF no identifiable business component improvement
    THEN not qualified
ELSE IF not technological in nature
    THEN not qualified
ELSE IF no beginning uncertainty
       (capability, method, or design)
    THEN not qualified
ELSE IF no evaluation of alternatives
    THEN not qualified
ELSE IF substantially all of the activity
       was NOT part of the experimentation
    THEN split activity; retest experimental
         portions only
ELSE IF purpose is style/taste/cosmetic/
       seasonal rather than function/
       performance/reliability/quality
    THEN not qualified
ELSE IF activity is excluded because it is:
       after commercial production, or
       customer adaptation, or
       duplication, or
       survey/study work, or
       software under special rule, or
       foreign research, or
       social science research, or
       funded research
    THEN exclude to that extent
ELSE
    activity qualifies as federal
    qualified research`}</pre>
        </div>
      </div>

      {/* CROSS-PHASE NAVIGATION */}
      <div className="p-5 bg-stone-100 border border-stone-300 rounded-sm">
        <h5 className="serif text-base font-semibold text-stone-900 mb-2">After the tree — what happens next</h5>
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <ArrowRight size={12} className="text-stone-500 shrink-0 mt-1" />
            <p className="sans text-xs text-stone-700 leading-relaxed">
              <strong>For each project</strong>, run through this tree in the <span className="font-semibold text-stone-900">Project Qualification</span> phase (06). That phase stores the four-part test answers and narrative per project.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ArrowRight size={12} className="text-stone-500 shrink-0 mt-1" />
            <p className="sans text-xs text-stone-700 leading-relaxed">
              <strong>Once qualified</strong>, map wages under §1.41-2(c)(1)-(3) in the <span className="font-semibold text-stone-900">Wages</span> phase (07), with only actual conduct, direct supervision, and direct support counted.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <ArrowRight size={12} className="text-stone-500 shrink-0 mt-1" />
            <p className="sans text-xs text-stone-700 leading-relaxed">
              <strong>Document excluded activities too</strong> — a strong study shows both what qualifies and what was intentionally excluded and why. This substantiation goes in the final report.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PHASE 4: CLIENT REQUESTS
// ============================================================
function RequestsPhase({ requestLists, setRequestLists, scope, documents, addDocuments, onCite }) {
  const [activeListId, setActiveListId] = useState(null);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(null);

  const createFromTemplate = (templateKey) => {
    const tpl = REQUEST_TEMPLATES[templateKey];
    const newList = {
      id: Date.now(),
      name: tpl.name,
      description: tpl.description,
      createdAt: new Date().toISOString(),
      status: "draft",
      items: tpl.items.map((it, i) => ({
        id: Date.now() + i + Math.random(),
        category: it.category,
        text: it.text,
        cite: it.cite,
        status: "pending",
        clientResponse: "",
        linkedDocs: [],
      })),
    };
    setRequestLists([...requestLists, newList]);
    setActiveListId(newList.id);
    setShowNewMenu(false);
  };

  const createBlank = () => {
    const newList = {
      id: Date.now(),
      name: "Custom Request List",
      description: "",
      createdAt: new Date().toISOString(),
      status: "draft",
      items: [],
    };
    setRequestLists([...requestLists, newList]);
    setActiveListId(newList.id);
    setShowNewMenu(false);
  };

  const updateList = (id, patch) => setRequestLists(requestLists.map(r => r.id === id ? { ...r, ...patch } : r));
  const deleteList = (id) => {
    if (!confirm("Delete this request list?")) return;
    setRequestLists(requestLists.filter(r => r.id !== id));
    if (activeListId === id) setActiveListId(null);
  };

  const updateItem = (listId, itemId, patch) => {
    setRequestLists(requestLists.map(r => r.id === listId
      ? { ...r, items: r.items.map(it => it.id === itemId ? { ...it, ...patch } : it) }
      : r));
  };

  const addItem = (listId) => {
    setRequestLists(requestLists.map(r => r.id === listId
      ? { ...r, items: [...r.items, { id: Date.now() + Math.random(), category: "General", text: "", cite: null, status: "pending", clientResponse: "", linkedDocs: [] }] }
      : r));
  };

  const removeItem = (listId, itemId) => {
    setRequestLists(requestLists.map(r => r.id === listId
      ? { ...r, items: r.items.filter(it => it.id !== itemId) }
      : r));
  };

  const attachDocsToItem = async (listId, itemId, files) => {
    const newDocs = await addDocuments(Array.from(files), { scope: "request", listId, itemId }, "Client Response");
    const docIds = newDocs.map(d => d.id);
    setRequestLists(prev => prev.map(r => r.id === listId
      ? { ...r, items: r.items.map(it => it.id === itemId ? { ...it, linkedDocs: [...it.linkedDocs, ...docIds], status: "received" } : it) }
      : r));
  };

  const activeList = requestLists.find(r => r.id === activeListId);

  return (
    <div className="p-8">
      <PhaseHeader number="05" title="Client Request Lists" description="Build, track, and send document requests to the client. Templates built from the SOP IDR checklist are available." />

      <div className="mb-6 relative">
        <button
          onClick={() => setShowNewMenu(!showNewMenu)}
          className="sans text-sm font-semibold px-4 py-2.5 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm flex items-center gap-2"
        >
          <Plus size={14} /> Create new request list
        </button>
        {showNewMenu && (
          <div className="absolute top-full mt-2 left-0 w-[460px] bg-white border border-stone-300 rounded-sm shadow-xl z-10">
            <div className="px-3 py-2 border-b border-stone-200 mono text-[10px] uppercase tracking-widest text-stone-500 font-bold">Templates</div>
            {Object.entries(REQUEST_TEMPLATES).map(([k, tpl]) => (
              <button
                key={k}
                onClick={() => createFromTemplate(k)}
                className="block w-full text-left px-4 py-3 hover:bg-sky-50 border-b border-stone-100 last:border-b-0"
              >
                <div className="serif text-sm font-semibold text-stone-900">{tpl.name}</div>
                <div className="sans text-xs text-stone-600 mt-0.5">{tpl.description}</div>
                <div className="mono text-[10px] text-stone-500 mt-1">{tpl.items.length} items</div>
              </button>
            ))}
            <button onClick={createBlank} className="block w-full text-left px-4 py-3 hover:bg-stone-50 border-t border-stone-200">
              <div className="serif text-sm font-semibold text-stone-900">+ Blank list</div>
              <div className="sans text-xs text-stone-600 mt-0.5">Build your own from scratch</div>
            </button>
          </div>
        )}
      </div>

      {requestLists.length > 0 && (
        <div className="mb-6 flex gap-2 flex-wrap">
          {requestLists.map(r => {
            const done = r.items.filter(i => i.status === "received").length;
            const isActive = activeListId === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setActiveListId(r.id)}
                className={`px-3 py-2 border rounded-sm text-left transition-colors ${
                  isActive ? "border-stone-900 bg-stone-900 text-sky-400" : "border-stone-300 bg-white hover:border-stone-500"
                }`}
              >
                <div className="sans text-xs font-semibold">{r.name}</div>
                <div className={`mono text-[10px] mt-0.5 ${isActive ? "text-sky-400/70" : "text-stone-500"}`}>
                  {done}/{r.items.length} received
                </div>
              </button>
            );
          })}
        </div>
      )}

      {activeList ? (
        <div className="border-2 border-stone-900 rounded-sm">
          <div className="bg-stone-900 text-stone-100 p-4 flex items-center justify-between">
            <div className="flex-1 mr-4">
              <input
                type="text"
                value={activeList.name}
                onChange={e => updateList(activeList.id, { name: e.target.value })}
                className="w-full bg-transparent serif text-xl font-bold text-sky-400 focus:outline-none"
              />
              <input
                type="text"
                value={activeList.description}
                onChange={e => updateList(activeList.id, { description: e.target.value })}
                className="w-full bg-transparent sans text-xs text-stone-300 focus:outline-none mt-1"
                placeholder="Description"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEmailModal(activeList)}
                className="sans text-xs font-semibold px-3 py-2 bg-sky-400 text-stone-900 hover:bg-sky-500 rounded-sm flex items-center gap-1.5"
              >
                <Send size={12} /> Compose Email
              </button>
              <button onClick={() => deleteList(activeList.id)} className="text-stone-400 hover:text-rose-400 p-2">
                <Trash2 size={13} />
              </button>
            </div>
          </div>

          <div className="bg-white p-4">
            <div className="grid grid-cols-4 gap-3 mb-4">
              <StatCard label="Pending" count={activeList.items.filter(i => i.status === "pending").length} color="amber" />
              <StatCard label="Requested" count={activeList.items.filter(i => i.status === "requested").length} color="amber" />
              <StatCard label="Received" count={activeList.items.filter(i => i.status === "received").length} color="emerald" />
              <StatCard label="N/A" count={activeList.items.filter(i => i.status === "na").length} color="rose" />
            </div>

            <div className="space-y-2">
              {activeList.items.map((item, idx) => {
                const itemDocs = documents.filter(d => item.linkedDocs.includes(d.id));
                return (
                  <div key={item.id} className={`border rounded-sm ${
                    item.status === "received" ? "border-emerald-300 bg-emerald-50/30" :
                    item.status === "na" ? "border-stone-200 bg-stone-50 opacity-60" :
                    "border-stone-200 bg-white"
                  }`}>
                    <div className="flex items-start gap-2 p-3">
                      <span className="mono text-[10px] font-bold text-stone-400 uppercase tracking-widest w-6 shrink-0 mt-1">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-start gap-2">
                          <input
                            type="text"
                            value={item.category}
                            onChange={e => updateItem(activeList.id, item.id, { category: e.target.value })}
                            className="mono text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 bg-stone-100 border border-stone-300 rounded-sm w-24 text-stone-700 focus:outline-none focus:border-stone-900"
                          />
                          <textarea
                            value={item.text}
                            onChange={e => updateItem(activeList.id, item.id, { text: e.target.value })}
                            className="flex-1 px-2 py-1 border border-stone-200 rounded-sm sans text-sm focus:outline-none focus:border-stone-900"
                            rows={Math.max(1, Math.ceil((item.text || "").length / 85))}
                          />
                          {item.cite && <CiteBadge id={item.cite} onClick={onCite} />}
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <select
                            value={item.status}
                            onChange={e => updateItem(activeList.id, item.id, { status: e.target.value })}
                            className={`mono text-[10px] uppercase font-bold px-2 py-1 border rounded-sm ${
                              item.status === "received" ? "bg-emerald-600 text-white border-emerald-700" :
                              item.status === "requested" ? "bg-sky-500 text-white border-sky-600" :
                              item.status === "na" ? "bg-stone-400 text-white border-stone-500" :
                              "bg-stone-200 text-stone-700 border-stone-300"
                            }`}
                          >
                            <option value="pending">Pending</option>
                            <option value="requested">Requested</option>
                            <option value="received">Received</option>
                            <option value="na">N/A</option>
                          </select>
                          <UploadButton onFiles={files => attachDocsToItem(activeList.id, item.id, files)} compact />
                          {itemDocs.length > 0 && (
                            <span className="mono text-[10px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-sm">
                              {itemDocs.length} file{itemDocs.length > 1 ? "s" : ""} attached
                            </span>
                          )}
                        </div>

                        {itemDocs.length > 0 && (
                          <div className="space-y-1">
                            {itemDocs.map(doc => {
                              const { icon: Icon, color } = fileIconFor(doc.name);
                              return (
                                <div key={doc.id} className="flex items-center gap-2 px-2 py-1 bg-white border border-stone-200 rounded-sm">
                                  <Icon size={12} className={color} />
                                  <a href={doc.dataUrl} download={doc.name} className="sans text-xs text-stone-800 hover:text-sky-700 flex-1 truncate">
                                    {doc.name}
                                  </a>
                                  <span className="mono text-[9px] text-stone-400">{humanFileSize(doc.size)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <input
                          type="text"
                          value={item.clientResponse}
                          onChange={e => updateItem(activeList.id, item.id, { clientResponse: e.target.value })}
                          placeholder="Client notes / response summary"
                          className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs italic focus:outline-none focus:border-stone-900"
                        />
                      </div>
                      <button onClick={() => removeItem(activeList.id, item.id)} className="text-stone-400 hover:text-rose-600 shrink-0">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => addItem(activeList.id)}
              className="mt-3 px-4 py-2 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm sans text-xs font-medium flex items-center gap-2"
            >
              <Plus size={13} /> Add request item
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 border-2 border-dashed border-stone-300 rounded-sm">
          <Inbox size={32} className="text-stone-400 mx-auto mb-3" />
          <p className="sans text-sm text-stone-600">No request list selected.</p>
          <p className="sans text-xs text-stone-500 mt-1">Create a new list from a template or start from scratch above.</p>
        </div>
      )}

      {showEmailModal && (
        <EmailModal
          list={showEmailModal}
          scope={scope}
          onClose={() => setShowEmailModal(null)}
          onMarkRequested={() => {
            updateList(showEmailModal.id, {
              status: "sent",
              items: showEmailModal.items.map(it => it.status === "pending" ? { ...it, status: "requested" } : it)
            });
            setShowEmailModal(null);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// ENGAGEMENT LETTER EMAIL MODAL — mirrors EmailModal but builds a
// formal letter body from engagement state. Uses mailto: like the
// request list does so it works with the user's email client.
// ============================================================
function EmailLetterModal({
  engagement,
  scope,
  feeDescription,
  expenseDescription,
  retainerClause,
  today,
  onClose,
  onMarkSent,
}) {
  const clientName = scope.clientName || "[Client Name]";
  const clientContact = scope.clientContactName || "[Client Contact]";
  const taxYear = scope.taxYear || "[Year]";
  const firmName = engagement.firmName || "[Firm Name]";
  const partnerName = engagement.partnerName || "[Partner Name]";
  const partnerTitle = engagement.partnerTitle || "Partner";

  const subject = `Engagement Letter — R&D Tax Credit Study — ${clientName} TY${taxYear}`;

  const bodyLines = [
    today,
    ``,
    clientContact,
    clientName,
    ``,
    `Re: Engagement Letter — R&D Tax Credit Study — Tax Year ${taxYear}`,
    ``,
    `Dear ${clientContact.replace(/^\[|\]$/g, "")},`,
    ``,
    `This letter confirms the agreement between ${firmName} and ${clientName} for ${firmName}'s preparation of an Internal Revenue Code §41 research and development tax credit study for the tax year ending ${taxYear}.`,
    ``,
    `SCOPE OF SERVICES`,
    `We will perform the following services:`,
    `  • Identify and evaluate qualified research activities under IRC §41(d)`,
    `  • Compute and document qualified research expenses (wages, supplies, contract research)`,
    `  • Compute the credit under both the Regular Credit and Alternative Simplified Credit (ASC) methods`,
    `  • Produce a final study report with supporting documentation, citations, and an authority index`,
    engagement.scopeExclusions
      ? `\nSCOPE EXCLUSIONS\n${engagement.scopeExclusions}`
      : ``,
    ``,
    `FEES`,
    `Our fees for this engagement are based on ${feeDescription}.`,
    expenseDescription ? expenseDescription : ``,
    retainerClause ? retainerClause : ``,
    ``,
    engagement.specialTerms
      ? `SPECIAL TERMS\n${engagement.specialTerms}\n`
      : ``,
    `ACCEPTANCE`,
    `Please indicate your acceptance of these terms by signing and returning the formal engagement letter (attached or available on request).`,
    ``,
    `Please let me know if you have any questions or would like to discuss any of the terms.`,
    ``,
    `Sincerely,`,
    ``,
    partnerName,
    `${partnerTitle}, ${firmName}`,
  ].filter((line) => line !== null && line !== undefined);

  const body = bodyLines.join("\n");

  const [editTo, setEditTo] = useState(scope.clientContactEmail || "");
  const [editSubject, setEditSubject] = useState(subject);
  const [editBody, setEditBody] = useState(body);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(
        `To: ${editTo}\nSubject: ${editSubject}\n\n${editBody}`,
      );
      alert("Copied to clipboard");
    } catch (e) {
      alert("Copy failed — select and copy manually.");
    }
  };

  const mailto = `mailto:${encodeURIComponent(editTo)}?subject=${encodeURIComponent(editSubject)}&body=${encodeURIComponent(editBody)}`;

  return (
    <div className="fixed inset-0 bg-stone-900/70 z-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-stone-300 flex items-center justify-between">
          <h3 className="serif text-xl font-bold text-stone-900">
            Compose Engagement Letter Email
          </h3>
          <button
            onClick={onClose}
            className="text-stone-500 hover:text-stone-900"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">
              To
            </div>
            <input
              type="email"
              value={editTo}
              onChange={(e) => setEditTo(e.target.value)}
              placeholder="client@example.com"
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900"
            />
            {!editTo && (
              <p className="sans text-[11px] text-stone-500 mt-1">
                Tip: set a default recipient on the Scope &amp; Entity phase
                (Client Contact Email).
              </p>
            )}
          </div>
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">
              Subject
            </div>
            <input
              type="text"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm font-semibold focus:outline-none focus:border-stone-900"
            />
          </div>
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">
              Body
            </div>
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              rows={22}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono text-xs leading-relaxed focus:outline-none focus:border-stone-900"
            />
            <p className="sans text-[11px] text-stone-500 mt-1">
              The full formatted letter is also available via Download HTML or
              Print on the letter tab — recommended to attach for client
              records.
            </p>
          </div>
        </div>
        <div className="p-5 border-t border-stone-300 flex items-center justify-between gap-3">
          <button
            onClick={copyAll}
            className="sans text-sm font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-stone-100 rounded-sm flex items-center gap-1.5"
          >
            <Copy size={13} /> Copy to clipboard
          </button>
          <div className="flex items-center gap-2">
            <a
              href={mailto}
              className="sans text-sm font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-stone-100 rounded-sm flex items-center gap-1.5"
            >
              <Mail size={13} /> Open in Mail
            </a>
            <button
              onClick={onMarkSent}
              className="sans text-sm font-semibold px-4 py-2 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm flex items-center gap-1.5"
            >
              <CheckCircle2 size={13} /> Mark as Sent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmailModal({ list, scope, onClose, onMarkRequested }) {
  const subject = `R&D Study — Information Request: ${list.name} — ${scope.clientName || "[Client]"} TY${scope.taxYear}`;

  const grouped = list.items
    .filter(it => it.status !== "na" && it.status !== "received")
    .reduce((acc, it) => {
      if (!acc[it.category]) acc[it.category] = [];
      acc[it.category].push(it);
      return acc;
    }, {});

  const bodyLines = [
    `Hi ${scope.clientContactName || "[Client Contact]"},`,
    ``,
    `As part of the R&D tax credit study for ${scope.clientName || "[Client]"} for tax year ${scope.taxYear}, please provide the following documentation and information.`,
    ``,
    list.description ? list.description : "",
    list.description ? `` : null,
  ].filter(l => l !== null);

  Object.entries(grouped).forEach(([cat, items]) => {
    bodyLines.push(cat.toUpperCase());
    items.forEach((it, i) => bodyLines.push(`  ${i + 1}. ${it.text}`));
    bodyLines.push(``);
  });

  bodyLines.push(
    `Please let me know if any of these items are unclear or unavailable — we can discuss substitutes. Target date for first round of responses is [DATE].`,
    ``,
    `Thanks,`,
    `[Your name]`
  );

  const body = bodyLines.join("\n");

  const [editSubject, setEditSubject] = useState(subject);
  const [editBody, setEditBody] = useState(body);
  const [editTo, setEditTo] = useState(scope.clientContactEmail || "");

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(`To: ${editTo}\nSubject: ${editSubject}\n\n${editBody}`);
      alert("Copied to clipboard");
    } catch (e) { alert("Copy failed — select and copy manually."); }
  };

  const mailto = `mailto:${encodeURIComponent(editTo)}?subject=${encodeURIComponent(editSubject)}&body=${encodeURIComponent(editBody)}`;

  return (
    <div className="fixed inset-0 bg-stone-900/70 z-50 flex items-center justify-center p-8">
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-stone-300 flex items-center justify-between">
          <h3 className="serif text-xl font-bold text-stone-900">Compose Request Email</h3>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">To</div>
            <input type="email" value={editTo} onChange={e => setEditTo(e.target.value)} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Subject</div>
            <input type="text" value={editSubject} onChange={e => setEditSubject(e.target.value)} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm font-semibold focus:outline-none focus:border-stone-900" />
          </div>
          <div>
            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Body</div>
            <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={22} className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono text-xs leading-relaxed focus:outline-none focus:border-stone-900" />
          </div>
        </div>
        <div className="p-5 border-t border-stone-300 flex items-center justify-between gap-3">
          <button onClick={copyAll} className="sans text-sm font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-stone-100 rounded-sm flex items-center gap-1.5">
            <Copy size={13} /> Copy to clipboard
          </button>
          <div className="flex items-center gap-2">
            <a href={mailto} className="sans text-sm font-medium px-3 py-2 border border-stone-300 bg-white hover:bg-stone-100 rounded-sm flex items-center gap-1.5">
              <Mail size={13} /> Open in Mail
            </a>
            <button onClick={onMarkRequested} className="sans text-sm font-semibold px-4 py-2 bg-stone-900 text-sky-400 hover:bg-stone-800 rounded-sm flex items-center gap-1.5">
              <CheckCircle2 size={13} /> Mark as Sent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PHASE 5: DOCUMENT VAULT
// ============================================================
function DocumentVaultPhase({ documents, addDocuments, deleteDocument, updateDocument, projects, employees, supplies, contractors }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const categories = ["all", ...Array.from(new Set(documents.map(d => d.category)))];

  const filtered = documents.filter(d => {
    if (filter !== "all" && d.category !== filter) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const linkDescriptionFor = (doc) => {
    if (!doc.linkedTo || Object.keys(doc.linkedTo).length === 0) return "Unlinked";
    const lt = doc.linkedTo;
    if (lt.scope === "screen" && lt.question) return `Screening Q: ${lt.question}`;
    if (lt.scope === "screen" && lt.exclusion) return `Exclusion: ${lt.exclusion}`;
    if (lt.scope === "project" && lt.projectId) {
      const p = projects.find(x => x.id === lt.projectId);
      return p ? `Project: ${p.name}` : "Project (deleted)";
    }
    if (lt.scope === "employee" && lt.employeeId) {
      const e = employees.find(x => x.id === lt.employeeId);
      return e ? `Employee: ${e.name || "unnamed"}` : "Employee (deleted)";
    }
    if (lt.scope === "supply" && lt.supplyId) {
      const s = supplies.find(x => x.id === lt.supplyId);
      return s ? `Supply: ${s.description || "unnamed"}` : "Supply (deleted)";
    }
    if (lt.scope === "contractor" && lt.contractorId) {
      const c = contractors.find(x => x.id === lt.contractorId);
      return c ? `Contractor: ${c.vendor || "unnamed"}` : "Contractor (deleted)";
    }
    if (lt.scope === "request") return `Client Request Response`;
    if (lt.scope === "wages") return `Payroll Master (study-level)`;
    if (lt.scope === "supplies") return `GL Master (study-level)`;
    if (lt.general) return `General — Study-level`;
    return "Unlinked";
  };

  return (
    <div className="p-8">
      <PhaseHeader number="06" title="Document Vault" description="Centralized view of every file attached during the study. Drop in new files, tag them, and review where they're linked." />

      <div className="mb-6">
        <div className="border-2 border-dashed border-stone-300 bg-stone-50/50 rounded-sm p-6">
          <input ref={inputRef} type="file" multiple className="hidden" onChange={e => { addDocuments(Array.from(e.target.files), { general: true }, "Unfiled"); e.target.value = ""; }} />
          <button onClick={() => inputRef.current?.click()} className="w-full flex flex-col items-center gap-2 sans text-sm text-stone-700 hover:text-stone-900">
            <Upload size={24} className="text-stone-400" />
            <span>Upload unfiled documents here (link them later)</span>
            <span className="sans text-xs text-stone-500">PDF, Excel, Word, images — drag & drop also works</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by filename..."
          className="flex-1 px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900"
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900">
          {categories.map(c => <option key={c} value={c}>{c === "all" ? "All categories" : c}</option>)}
        </select>
      </div>

      <div className="border border-stone-300 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-900 text-sky-400">
            <tr>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">File</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Category</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Linked To</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">Size</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Uploaded</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-10 text-stone-500 sans text-sm italic">
                  {documents.length === 0 ? "No documents uploaded yet." : "No documents match your filters."}
                </td>
              </tr>
            ) : (
              filtered.map(doc => {
                const { icon: Icon, color } = fileIconFor(doc.name);
                return (
                  <tr key={doc.id} className="border-t border-stone-200 hover:bg-stone-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={color} />
                        <a href={doc.dataUrl} download={doc.name} className="sans text-xs text-stone-800 hover:text-sky-700 truncate">
                          {doc.name}
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={doc.category}
                        onChange={e => updateDocument(doc.id, { category: e.target.value })}
                        className="mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 border border-stone-200 rounded-sm w-28 focus:outline-none focus:border-stone-900"
                      />
                    </td>
                    <td className="px-3 py-2 mono text-[10px] text-stone-600">{linkDescriptionFor(doc)}</td>
                    <td className="px-3 py-2 text-right mono text-[10px] text-stone-500">{humanFileSize(doc.size)}</td>
                    <td className="px-3 py-2 mono text-[10px] text-stone-500">{new Date(doc.uploadedAt).toLocaleDateString()}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => deleteDocument(doc.id)} className="text-stone-400 hover:text-rose-600">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {documents.length > 0 && (
        <div className="mt-6 p-4 bg-stone-50 border border-stone-200 rounded-sm">
          <h5 className="serif text-sm font-semibold text-stone-900 mb-2">Vault Summary</h5>
          <div className="grid grid-cols-4 gap-3">
            <MiniStat label="Total files" count={documents.length} />
            <MiniStat label="Categories" count={new Set(documents.map(d => d.category)).size} />
            <MiniStat label="Linked" count={documents.filter(d => d.linkedTo && Object.keys(d.linkedTo).length > 0 && !d.linkedTo.general).length} />
            <MiniStat label="Unlinked" count={documents.filter(d => !d.linkedTo || Object.keys(d.linkedTo).length === 0 || d.linkedTo.general).length} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHASE 6: PROJECTS — with notes + uploads
// ============================================================
function ProjectsPhase({ projects, setProjects, documents, addDocuments, deleteDocument, onCite }) {
  const [newProj, setNewProj] = useState({ name: "", description: "" });

  const addProject = () => {
    if (!newProj.name.trim()) return;
    setProjects([...projects, {
      id: Date.now(),
      name: newProj.name,
      description: newProj.description,
      fourPart: { purpose: null, uncertainty: null, experimentation: null, technological: null },
      fourPartNotes: { purpose: "", uncertainty: "", experimentation: "", technological: "" },
      exclusionFlags: [],
      narrative: "",
      qualification: "pending",
    }]);
    setNewProj({ name: "", description: "" });
  };

  const updateProject = (id, field, value) => setProjects(projects.map(p => p.id === id ? { ...p, [field]: value } : p));
  const updateFourPart = (id, test, value) => setProjects(projects.map(p => p.id === id ? { ...p, fourPart: { ...p.fourPart, [test]: value } } : p));
  const updateFourPartNote = (id, test, value) => setProjects(projects.map(p => p.id === id ? { ...p, fourPartNotes: { ...(p.fourPartNotes || {}), [test]: value } } : p));
  const removeProject = (id) => setProjects(projects.filter(p => p.id !== id));

  const autoQualify = (p) => {
    const scores = Object.values(p.fourPart);
    if (scores.every(s => s === true)) return "qualified";
    if (scores.some(s => s === false)) return "excluded";
    if (scores.filter(s => s === true).length >= 2) return "partial";
    return "pending";
  };

  return (
    <div className="p-8">
      <PhaseHeader number="07" title="Project-Level Qualification" description="Apply the four-part test from §41(d)(1) to each project. Document the basis for each answer and attach supporting evidence." />

      <AIBadge>AI can draft narratives from approved interview notes and organize the qualification matrix. <strong className="block mt-1">A human must approve each project's qualification conclusion.</strong></AIBadge>

      <div className="mt-6 p-4 border-2 border-dashed border-stone-300 rounded-sm">
        <div className="grid grid-cols-[1fr_2fr_auto] gap-3">
          <input type="text" value={newProj.name} onChange={e => setNewProj({ ...newProj, name: e.target.value })} placeholder="Project name"
            className="px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" />
          <input type="text" value={newProj.description} onChange={e => setNewProj({ ...newProj, description: e.target.value })} placeholder="Short description / business component"
            className="px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-stone-900" />
          <button onClick={addProject} className="px-4 py-2 bg-stone-900 text-sky-400 sans text-sm font-semibold rounded-sm hover:bg-stone-800 flex items-center gap-2">
            <Plus size={14} /> Add
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {projects.length === 0 && (
          <div className="text-center py-12 text-stone-500 sans text-sm italic">No projects yet. Add a project to begin qualification.</div>
        )}
        {projects.map((p, idx) => {
          const currentQual = autoQualify(p);
          return (
            <div key={p.id} className="border border-stone-300 rounded-sm overflow-hidden">
              <div className={`px-4 py-3 flex items-center justify-between ${
                currentQual === "qualified" ? "bg-emerald-50 border-b border-emerald-200" :
                currentQual === "excluded" ? "bg-rose-50 border-b border-rose-200" :
                currentQual === "partial" ? "bg-sky-50 border-b border-sky-200" :
                "bg-stone-50 border-b border-stone-200"
              }`}>
                <div className="flex items-center gap-3">
                  <span className="mono text-[10px] font-bold text-stone-500 uppercase tracking-widest">P{String(idx + 1).padStart(2, "0")}</span>
                  <div>
                    <h5 className="serif text-base font-semibold text-stone-900">{p.name}</h5>
                    {p.description && <p className="sans text-xs text-stone-600">{p.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select value={p.qualification} onChange={e => updateProject(p.id, "qualification", e.target.value)}
                    className={`mono text-[10px] uppercase font-bold px-2 py-1 border rounded-sm ${
                      p.qualification === "qualified" ? "bg-emerald-600 text-white border-emerald-700" :
                      p.qualification === "excluded" ? "bg-rose-600 text-white border-rose-700" :
                      p.qualification === "partial" ? "bg-sky-500 text-white border-sky-600" :
                      "bg-stone-200 text-stone-700 border-stone-300"
                    }`}>
                    <option value="pending">Pending</option>
                    <option value="qualified">Qualified</option>
                    <option value="partial">Partial</option>
                    <option value="excluded">Excluded</option>
                  </select>
                  <button onClick={() => removeProject(p.id)} className="text-stone-400 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div className="space-y-3">
                  {FOUR_PART_TEST.map(t => (
                    <div key={t.id} className="p-3 border border-stone-200 rounded-sm bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="sans text-[11px] font-semibold text-stone-900 uppercase tracking-wide">{t.title}</span>
                        <CiteBadge id={t.cite} onClick={onCite} />
                      </div>
                      <p className="sans text-xs text-stone-700 leading-snug mb-2">{t.q}</p>
                      <TriToggle current={p.fourPart[t.id]} onChange={v => updateFourPart(p.id, t.id, v)} compact />

                      {p.fourPart[t.id] !== null && (
                        <div className="mt-2">
                          <textarea
                            value={p.fourPartNotes?.[t.id] || ""}
                            onChange={e => updateFourPartNote(p.id, t.id, e.target.value)}
                            className="w-full px-2 py-1.5 border border-stone-200 rounded-sm sans text-xs leading-relaxed focus:outline-none focus:border-stone-900 min-h-[50px]"
                            placeholder={`Basis for ${p.fourPart[t.id] === true ? "YES" : p.fourPart[t.id] === false ? "NO" : "UNSURE"} — cite specific facts, interviews, documents...`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1.5">Project Narrative</div>
                  <textarea value={p.narrative} onChange={e => updateProject(p.id, "narrative", e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-xs leading-relaxed focus:outline-none focus:border-stone-900 min-h-[80px]"
                    placeholder="Business objective → technical uncertainty → alternatives tested → experimentation → technological basis → key employees → supporting documents" />
                </div>

                <FileAttachmentZone
                  linkedTo={{ scope: "project", projectId: p.id }}
                  category="Project Evidence"
                  documents={documents}
                  addDocuments={addDocuments}
                  deleteDocument={deleteDocument}
                  label="Project supporting documents (specs, tickets, test results, design docs)"
                />
              </div>
            </div>
          );
        })}
      </div>

      {projects.length > 0 && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          <StatCard label="Qualified" count={projects.filter(p => p.qualification === "qualified").length} color="emerald" />
          <StatCard label="Partial" count={projects.filter(p => p.qualification === "partial").length} color="amber" />
          <StatCard label="Excluded" count={projects.filter(p => p.qualification === "excluded").length} color="rose" />
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHASE 7: WAGES — with notes + uploads per employee
// ============================================================
function WagesPhase({ employees, setEmployees, wageQRE, documents, addDocuments, deleteDocument, onCite }) {
  const [expandedId, setExpandedId] = useState(null);

  const addEmployee = () => {
    const id = Date.now();
    setEmployees([...employees, { id, name: "", title: "", department: "", wages: "", qualifiedPct: "", category: "conduct", notes: "" }]);
    setExpandedId(id);
  };

  const updateEmp = (id, field, value) => setEmployees(employees.map(e => e.id === id ? { ...e, [field]: value } : e));
  const removeEmp = (id) => setEmployees(employees.filter(e => e.id !== id));

  return (
    <div className="p-8">
      <PhaseHeader number="08" title="Wage QREs — Employee-Level Mapping" description="Map each employee's wages to a qualified service category with documented basis." />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CiteBadge id="41(b)(2)" onClick={onCite} />
        <CiteBadge id="1.41-2(c)" onClick={onCite} />
        <span className="sans text-xs text-stone-500">Qualified services = actual conduct, direct supervision, direct support.</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border border-stone-300 rounded-sm">
          <thead className="bg-stone-900 text-sky-400">
            <tr>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Employee</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Title</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Category</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">Wages</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">Qual %</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">QRE</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {employees.map(e => {
              const wages = parseFloat(e.wages) || 0;
              const pct = parseFloat(e.qualifiedPct) || 0;
              const qre = wages * (pct / 100);
              const isExpanded = expandedId === e.id;
              const empDocs = documents.filter(d => d.linkedTo?.scope === "employee" && d.linkedTo?.employeeId === e.id);
              return (
                <React.Fragment key={e.id}>
                  <tr className="border-t border-stone-200">
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setExpandedId(isExpanded ? null : e.id)} className="text-stone-400 hover:text-stone-900">
                          <ChevronRight size={13} className={isExpanded ? "rotate-90 transition-transform" : "transition-transform"} />
                        </button>
                        <input value={e.name} onChange={ev => updateEmp(e.id, "name", ev.target.value)} placeholder="Name"
                          className="flex-1 px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" />
                        {empDocs.length > 0 && <span className="mono text-[9px] text-emerald-700 bg-emerald-100 px-1 rounded-sm">{empDocs.length}📎</span>}
                      </div>
                    </td>
                    <td className="px-2 py-1.5"><input value={e.title} onChange={ev => updateEmp(e.id, "title", ev.target.value)} placeholder="Title" className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5">
                      <select value={e.category} onChange={ev => updateEmp(e.id, "category", ev.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded-sm mono text-[10px] focus:outline-none focus:border-stone-900">
                        <option value="conduct">Actual Conduct</option>
                        <option value="supervision">Direct Supervision</option>
                        <option value="support">Direct Support</option>
                        <option value="nonqualified">Non-qualified</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5"><MoneyInput value={e.wages} onChange={ev => updateEmp(e.id, "wages", ev.target.value)} placeholder="0" className="w-full px-2 py-1 border border-stone-200 rounded-sm mono num-tabular text-xs text-right focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5"><input type="number" value={e.qualifiedPct} onChange={ev => updateEmp(e.id, "qualifiedPct", ev.target.value)} placeholder="0" className="w-full px-2 py-1 border border-stone-200 rounded-sm mono num-tabular text-xs text-right focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-3 py-1.5 text-right mono num-tabular text-xs font-semibold text-stone-900">${fmt(qre)}</td>
                    <td className="px-2"><button onClick={() => removeEmp(e.id)} className="text-stone-400 hover:text-rose-600"><Trash2 size={13} /></button></td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={7} className="bg-stone-50 p-4 border-t border-stone-200">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Basis for qualified %</div>
                            <textarea value={e.notes || ""} onChange={ev => updateEmp(e.id, "notes", ev.target.value)}
                              placeholder="Time-tracking data, manager interview, project allocation report, etc."
                              className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[80px]" />
                          </div>
                          <div>
                            <FileAttachmentZone
                              linkedTo={{ scope: "employee", employeeId: e.id }}
                              category="Wage Support"
                              documents={documents}
                              addDocuments={addDocuments}
                              deleteDocument={deleteDocument}
                              label="Time-tracking, interviews, job descriptions"
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {employees.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-stone-500 sans text-xs italic">No employees yet.</td></tr>
            )}
          </tbody>
          <tfoot className="bg-stone-100 border-t-2 border-stone-900">
            <tr>
              <td colSpan={5} className="px-3 py-2.5 sans text-xs font-semibold text-stone-900 uppercase tracking-widest">Total Qualified Wages</td>
              <td className="px-3 py-2.5 text-right mono num-tabular text-sm font-bold text-stone-900">${fmt(wageQRE)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <button onClick={addEmployee} className="mt-3 px-4 py-2 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm sans text-xs font-medium flex items-center gap-2">
        <Plus size={13} /> Add employee
      </button>

      <div className="mt-6">
        <HumanFlag>
          Any employee marked <strong>Direct Support</strong> or <strong>Direct Supervision</strong> should be reviewed — higher-level management and general admin do not qualify per §1.41-2(c)(2).
        </HumanFlag>
      </div>

      <div className="mt-6">
        <FileAttachmentZone
          linkedTo={{ scope: "wages", general: true }}
          category="Payroll Master"
          documents={documents}
          addDocuments={addDocuments}
          deleteDocument={deleteDocument}
          label="Master payroll register & org chart (study-level)"
        />
      </div>
    </div>
  );
}

// ============================================================
// PHASE 8: SUPPLIES — with notes + uploads
// ============================================================
function SuppliesPhase({ supplies, setSupplies, supplyQRE, documents, addDocuments, deleteDocument, onCite }) {
  const [expandedId, setExpandedId] = useState(null);

  const addSupply = () => {
    const id = Date.now();
    setSupplies([...supplies, { id, description: "", vendor: "", account: "", amount: "", project: "", qualified: true, notes: "" }]);
    setExpandedId(id);
  };
  const update = (id, f, v) => setSupplies(supplies.map(s => s.id === id ? { ...s, [f]: v } : s));
  const remove = (id) => setSupplies(supplies.filter(s => s.id !== id));

  return (
    <div className="p-8">
      <PhaseHeader number="09" title="Supply QREs" description="Supplies used in the conduct of qualified research. Attach invoices and GL detail to each entry." />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CiteBadge id="41(b)(2)" onClick={onCite} />
        <span className="sans text-xs text-stone-500">§41(b)(2)(A)(ii) — supplies used in the conduct of qualified research.</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border border-stone-300 rounded-sm">
          <thead className="bg-stone-900 text-sky-400">
            <tr>
              <th></th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Description</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Vendor</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">GL Acct</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-3 py-2.5">Project</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-3 py-2.5">Amount</th>
              <th className="sans text-[10px] font-semibold uppercase tracking-widest text-center px-3 py-2.5">Qual?</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {supplies.map(s => {
              const isExpanded = expandedId === s.id;
              const supDocs = documents.filter(d => d.linkedTo?.scope === "supply" && d.linkedTo?.supplyId === s.id);
              return (
                <React.Fragment key={s.id}>
                  <tr className="border-t border-stone-200">
                    <td className="px-2">
                      <button onClick={() => setExpandedId(isExpanded ? null : s.id)} className="text-stone-400 hover:text-stone-900">
                        <ChevronRight size={13} className={isExpanded ? "rotate-90 transition-transform" : "transition-transform"} />
                      </button>
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <input value={s.description} onChange={e => update(s.id, "description", e.target.value)} placeholder="Prototype material" className="flex-1 px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" />
                        {supDocs.length > 0 && <span className="mono text-[9px] text-emerald-700 bg-emerald-100 px-1 rounded-sm">{supDocs.length}📎</span>}
                      </div>
                    </td>
                    <td className="px-2 py-1.5"><input value={s.vendor} onChange={e => update(s.id, "vendor", e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5"><input value={s.account} onChange={e => update(s.id, "account", e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded-sm mono text-xs focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5"><input value={s.project} onChange={e => update(s.id, "project", e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded-sm sans text-xs focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5"><MoneyInput value={s.amount} onChange={e => update(s.id, "amount", e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded-sm mono num-tabular text-xs text-right focus:outline-none focus:border-stone-900" /></td>
                    <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={s.qualified} onChange={e => update(s.id, "qualified", e.target.checked)} /></td>
                    <td className="px-2"><button onClick={() => remove(s.id)} className="text-stone-400 hover:text-rose-600"><Trash2 size={13} /></button></td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} className="bg-stone-50 p-4 border-t border-stone-200">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Basis for qualification / tie to project</div>
                            <textarea value={s.notes || ""} onChange={e => update(s.id, "notes", e.target.value)}
                              placeholder="How this material was used in qualified research..."
                              className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[80px]" />
                          </div>
                          <FileAttachmentZone
                            linkedTo={{ scope: "supply", supplyId: s.id }}
                            category="Supply Support"
                            documents={documents}
                            addDocuments={addDocuments}
                            deleteDocument={deleteDocument}
                            label="Invoices, POs, GL detail"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {supplies.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-stone-500 sans text-xs italic">No supply entries yet.</td></tr>}
          </tbody>
          <tfoot className="bg-stone-100 border-t-2 border-stone-900">
            <tr>
              <td colSpan={5} className="px-3 py-2.5 sans text-xs font-semibold text-stone-900 uppercase tracking-widest">Total Qualified Supplies</td>
              <td className="px-3 py-2.5 text-right mono num-tabular text-sm font-bold text-stone-900">${fmt(supplyQRE)}</td>
              <td colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <button onClick={addSupply} className="mt-3 px-4 py-2 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm sans text-xs font-medium flex items-center gap-2">
        <Plus size={13} /> Add supply line
      </button>

      <div className="mt-6">
        <FileAttachmentZone
          linkedTo={{ scope: "supplies", general: true }}
          category="GL Detail"
          documents={documents}
          addDocuments={addDocuments}
          deleteDocument={deleteDocument}
          label="Master GL / AP detail files (study-level)"
        />
      </div>
    </div>
  );
}

// ============================================================
// PHASE 9: CONTRACTS — with notes + uploads + per-invoice breakout
// ============================================================
function ContractsPhase({ contractors, setContractors, contractQRE, documents, addDocuments, deleteDocument, onCite }) {
  const add = () => setContractors([...contractors, {
    id: Date.now(), vendor: "", sowRef: "", amount: "",
    priorAgreement: false, onBehalfOf: false, bearsCost: false, rightsToResults: false,
    qualified: false, notes: "", invoices: [], useInvoiceBreakout: false,
  }]);
  const update = (id, f, v) => setContractors(contractors.map(c => c.id === id ? { ...c, [f]: v } : c));
  const remove = (id) => setContractors(contractors.filter(c => c.id !== id));
  const autoCheckQual = (c) => c.priorAgreement && c.onBehalfOf && c.bearsCost && c.rightsToResults;

  // Invoice helpers
  const addInvoice = (contractorId) => {
    const c = contractors.find(x => x.id === contractorId);
    const invoices = Array.isArray(c.invoices) ? c.invoices : [];
    const newInvoice = {
      id: Date.now() + Math.random(),
      invoiceNo: "",
      date: "",
      description: "",
      amount: "",
      qualifiedPct: "100",
      notes: "",
    };
    update(contractorId, "invoices", [...invoices, newInvoice]);
  };

  const updateInvoice = (contractorId, invoiceId, field, value) => {
    const c = contractors.find(x => x.id === contractorId);
    const invoices = (c.invoices || []).map(inv =>
      inv.id === invoiceId ? { ...inv, [field]: value } : inv
    );
    update(contractorId, "invoices", invoices);
  };

  const removeInvoice = (contractorId, invoiceId) => {
    const c = contractors.find(x => x.id === contractorId);
    const invoices = (c.invoices || []).filter(inv => inv.id !== invoiceId);
    update(contractorId, "invoices", invoices);
  };

  const toggleBreakout = (contractorId) => {
    const c = contractors.find(x => x.id === contractorId);
    const turningOn = !c.useInvoiceBreakout;
    if (turningOn && (!c.invoices || c.invoices.length === 0)) {
      // Seed with one invoice prefilled from the total amount
      const seedAmt = parseFloat(c.amount) || 0;
      update(contractorId, "invoices", [{
        id: Date.now() + Math.random(),
        invoiceNo: "",
        date: "",
        description: "",
        amount: seedAmt > 0 ? String(seedAmt) : "",
        qualifiedPct: "100",
        notes: "",
      }]);
    }
    update(contractorId, "useInvoiceBreakout", turningOn);
  };

  // Per-invoice quick-fill common percentages
  const QUICK_PCTS = [100, 75, 50, 25, 10];

  return (
    <div className="p-8">
      <PhaseHeader number="10" title="Contract Research QREs" description="Outside vendors count at 65% if all four tests are met. For mixed-use vendors, toggle the per-invoice breakout to assign a qualified % to each invoice line." />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CiteBadge id="1.41-2(e)" onClick={onCite} />
        <span className="sans text-xs text-stone-500">All four tests must be met for 65% inclusion. The 65% rule applies AFTER the qualified-% breakout.</span>
      </div>

      <HumanFlag>
        Contract research is the <strong>most documentation-sensitive bucket</strong>. A human must review SOWs and contracts to confirm
        agreement existed before work began, taxpayer bore economic risk, and taxpayer retained rights to results.
        <span className="block mt-1">For vendors whose work is <em>partially</em> qualified research, use the per-invoice breakout to document exactly which invoices (and what portion of each) relate to qualified activities.</span>
      </HumanFlag>

      <div className="mt-6 space-y-4">
        {contractors.map((c, i) => {
          const passes = autoCheckQual(c);
          const amt = parseFloat(c.amount) || 0;
          const useBreakout = c.useInvoiceBreakout && Array.isArray(c.invoices) && c.invoices.length > 0;

          // Compute invoice-level totals
          const invoices = c.invoices || [];
          const invoiceRawTotal = invoices.reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0);
          const invoiceQualifiedTotal = invoices.reduce((s, inv) => {
            const a = parseFloat(inv.amount) || 0;
            const p = Math.min(100, Math.max(0, parseFloat(inv.qualifiedPct) || 0));
            return s + (a * p / 100);
          }, 0);
          const weightedQualifiedPct = invoiceRawTotal > 0 ? (invoiceQualifiedTotal / invoiceRawTotal) * 100 : 0;

          // The amount that ultimately gets the 65% applied
          const baseQualifiedAmount = useBreakout ? invoiceQualifiedTotal : amt;
          const finalQRE = c.qualified ? baseQualifiedAmount * 0.65 : 0;

          return (
            <div key={c.id} className={`border rounded-sm ${c.qualified && passes ? "border-emerald-400" : "border-stone-300"}`}>
              <div className="bg-stone-50 px-4 py-3 border-b border-stone-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="mono text-[10px] font-bold text-stone-500 uppercase">C{String(i + 1).padStart(2, "0")}</span>
                  <input value={c.vendor} onChange={e => update(c.id, "vendor", e.target.value)} placeholder="Vendor name" className="px-2 py-1 border border-stone-300 bg-white rounded-sm sans text-sm font-semibold w-64 focus:outline-none focus:border-stone-900" />
                  <input value={c.sowRef} onChange={e => update(c.id, "sowRef", e.target.value)} placeholder="SOW ref" className="px-2 py-1 border border-stone-300 bg-white rounded-sm mono text-xs w-32 focus:outline-none focus:border-stone-900" />
                </div>
                <div className="flex items-center gap-3">
                  {!useBreakout ? (
                    <>
                      <span className="mono text-[10px] text-stone-500 uppercase">Total amount paid</span>
                      <MoneyInput value={c.amount} onChange={e => update(c.id, "amount", e.target.value)} placeholder="0" className="px-2 py-1 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm w-32 text-right focus:outline-none focus:border-stone-900" />
                    </>
                  ) : (
                    <>
                      <span className="mono text-[10px] text-stone-500 uppercase">Invoice total</span>
                      <span className="mono num-tabular text-sm font-bold text-stone-900 px-2 py-1 bg-stone-100 rounded-sm w-32 text-right" title="Sum of all invoices below">
                        ${fmt(invoiceRawTotal)}
                      </span>
                    </>
                  )}
                  <button onClick={() => remove(c.id)} className="text-stone-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <CheckRow label="Agreement entered before work began" checked={c.priorAgreement} onChange={v => update(c.id, "priorAgreement", v)} />
                  <CheckRow label="Work performed on behalf of taxpayer" checked={c.onBehalfOf} onChange={v => update(c.id, "onBehalfOf", v)} />
                  <CheckRow label="Taxpayer bears cost if unsuccessful" checked={c.bearsCost} onChange={v => update(c.id, "bearsCost", v)} />
                  <CheckRow label="Taxpayer has rights to results" checked={c.rightsToResults} onChange={v => update(c.id, "rightsToResults", v)} />
                </div>

                {/* Invoice breakout toggle */}
                <div className="border-t border-stone-200 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!c.useInvoiceBreakout}
                        onChange={() => toggleBreakout(c.id)}
                        className="shrink-0"
                      />
                      <div>
                        <div className="sans text-xs font-bold text-stone-900">Use per-invoice breakout</div>
                        <div className="sans text-[11px] text-stone-500">Break out individual invoices with a qualified % on each (for mixed-use vendors)</div>
                      </div>
                    </label>
                    {useBreakout && (
                      <div className="text-right">
                        <div className="mono text-[10px] text-stone-500 uppercase tracking-widest">Weighted qualified %</div>
                        <div className={`mono num-tabular text-sm font-bold ${weightedQualifiedPct >= 90 ? "text-emerald-700" : weightedQualifiedPct >= 50 ? "text-sky-700" : "text-amber-700"}`}>
                          {weightedQualifiedPct.toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>

                  {useBreakout && (
                    <div className="mt-3 border border-stone-300 rounded-sm overflow-hidden bg-white">
                      <table className="w-full">
                        <thead className="bg-stone-900">
                          <tr>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-left px-2 py-2 w-24">Invoice #</th>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-left px-2 py-2 w-28">Date</th>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-left px-2 py-2">Description / Activity</th>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-right px-2 py-2 w-28">Total Amount</th>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-center px-2 py-2 w-48">Qualified %</th>
                            <th className="sans text-[9px] font-semibold uppercase tracking-widest text-sky-400 text-right px-2 py-2 w-28">Qualified $</th>
                            <th className="px-2 py-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv, invIdx) => {
                            const invAmt = parseFloat(inv.amount) || 0;
                            const invPct = Math.min(100, Math.max(0, parseFloat(inv.qualifiedPct) || 0));
                            const invQualDollars = invAmt * invPct / 100;
                            return (
                              <tr key={inv.id} className="border-t border-stone-200 hover:bg-sky-50/30">
                                <td className="px-2 py-1.5">
                                  <input
                                    type="text"
                                    value={inv.invoiceNo || ""}
                                    onChange={e => updateInvoice(c.id, inv.id, "invoiceNo", e.target.value)}
                                    placeholder={`INV-${String(invIdx + 1).padStart(3, "0")}`}
                                    className="w-full px-1.5 py-1 border border-stone-200 bg-white rounded-sm mono text-[11px] focus:outline-none focus:border-sky-500"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="date"
                                    value={inv.date || ""}
                                    onChange={e => updateInvoice(c.id, inv.id, "date", e.target.value)}
                                    className="w-full px-1.5 py-1 border border-stone-200 bg-white rounded-sm mono text-[11px] focus:outline-none focus:border-sky-500"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="text"
                                    value={inv.description || ""}
                                    onChange={e => updateInvoice(c.id, inv.id, "description", e.target.value)}
                                    placeholder="e.g., 'Algorithm prototype — weeks 3-4'"
                                    className="w-full px-1.5 py-1 border border-stone-200 bg-white rounded-sm sans text-[11px] focus:outline-none focus:border-sky-500"
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="relative">
                                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 mono text-[10px] text-stone-400">$</span>
                                    <MoneyInput
                                      value={inv.amount || ""}
                                      onChange={e => updateInvoice(c.id, inv.id, "amount", e.target.value)}
                                      placeholder="0"
                                      className="w-full pl-4 pr-1.5 py-1 border border-stone-200 bg-white rounded-sm mono num-tabular text-[11px] text-right focus:outline-none focus:border-sky-500"
                                    />
                                  </div>
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="flex items-center gap-1">
                                    <div className="relative flex-1">
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={inv.qualifiedPct || ""}
                                        onChange={e => updateInvoice(c.id, inv.id, "qualifiedPct", e.target.value)}
                                        placeholder="0"
                                        className="w-full pl-1.5 pr-5 py-1 border border-stone-200 bg-white rounded-sm mono num-tabular text-[11px] text-right focus:outline-none focus:border-sky-500"
                                      />
                                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 mono text-[10px] text-stone-400">%</span>
                                    </div>
                                    <div className="flex gap-0.5">
                                      {QUICK_PCTS.map(p => (
                                        <button
                                          key={p}
                                          onClick={() => updateInvoice(c.id, inv.id, "qualifiedPct", String(p))}
                                          className={`mono text-[9px] font-bold px-1 py-0.5 rounded-sm border transition-colors ${
                                            String(p) === String(inv.qualifiedPct)
                                              ? "bg-sky-600 text-white border-sky-600"
                                              : "bg-white text-stone-500 border-stone-300 hover:border-sky-400 hover:text-sky-700"
                                          }`}
                                          title={`Set to ${p}%`}
                                        >
                                          {p}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <span className="mono num-tabular text-[11px] font-bold text-stone-900">
                                    ${fmt(invQualDollars)}
                                  </span>
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <button
                                    onClick={() => removeInvoice(c.id, inv.id)}
                                    className="text-stone-400 hover:text-rose-600"
                                    title="Remove invoice"
                                  >
                                    <X size={11} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                          {/* Totals row */}
                          <tr className="border-t-2 border-stone-900 bg-stone-100">
                            <td colSpan="3" className="px-2 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => addInvoice(c.id)}
                                  className="sans text-[10px] font-bold px-2 py-0.5 border border-sky-500 text-sky-700 bg-white hover:bg-sky-50 rounded-sm flex items-center gap-1"
                                >
                                  <Plus size={10} /> Add Invoice Line
                                </button>
                                <span className="sans text-[10px] text-stone-500 italic">{invoices.length} invoice{invoices.length !== 1 ? "s" : ""} logged</span>
                              </div>
                            </td>
                            <td className="px-2 py-2 text-right mono num-tabular text-[11px] font-bold text-stone-900">
                              ${fmt(invoiceRawTotal)}
                            </td>
                            <td className="px-2 py-2 text-center mono num-tabular text-[11px] font-bold text-stone-700">
                              {weightedQualifiedPct.toFixed(1)}% avg
                            </td>
                            <td className="px-2 py-2 text-right mono num-tabular text-[11px] font-bold text-sky-700">
                              ${fmt(invoiceQualifiedTotal)}
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div>
                  <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Notes on contract terms</div>
                  <textarea value={c.notes || ""} onChange={e => update(c.id, "notes", e.target.value)}
                    placeholder="Cite specific contract sections supporting each test. For mixed-use vendors, document how qualified percentages were determined (e.g., SOW references, time tracking, manager estimates)."
                    className="w-full px-2 py-1.5 border border-stone-300 rounded-sm sans text-xs focus:outline-none focus:border-stone-900 min-h-[60px]" />
                </div>

                <FileAttachmentZone
                  linkedTo={{ scope: "contractor", contractorId: c.id }}
                  category="Contract Support"
                  documents={documents}
                  addDocuments={addDocuments}
                  deleteDocument={deleteDocument}
                  label="MSA, SOW, amendments, invoices, deliverables"
                />
              </div>

              <div className="px-4 py-3 bg-stone-50 border-t border-stone-200 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={c.qualified} onChange={e => update(c.id, "qualified", e.target.checked)} />
                    <span className="sans text-xs font-semibold text-stone-900">Qualified (human sign-off)</span>
                  </label>
                  {passes && !c.qualified && <span className="mono text-[10px] text-sky-700 bg-sky-100 px-2 py-0.5 rounded-sm">All 4 tests pass — ready for human approval</span>}
                  {!passes && c.qualified && <span className="mono text-[10px] text-rose-700 bg-rose-100 px-2 py-0.5 rounded-sm">⚠ Not all tests pass</span>}
                </div>
                <div className="mono num-tabular text-sm flex items-center gap-3">
                  {useBreakout ? (
                    <span className="text-stone-500 text-[10px]">
                      ${fmt(invoiceQualifiedTotal)} qual × 65% =
                    </span>
                  ) : (
                    <span className="text-stone-500 text-[10px]">
                      ${fmt(amt)} × 65% =
                    </span>
                  )}
                  <span className="font-bold text-stone-900 text-base">${fmt(finalQRE)}</span>
                </div>
              </div>
            </div>
          );
        })}

        {contractors.length === 0 && (
          <div className="text-center py-10 text-stone-500 sans text-sm italic border-2 border-dashed border-stone-200 rounded-sm">No contractors yet.</div>
        )}
      </div>

      <button onClick={add} className="mt-3 px-4 py-2 border border-stone-300 bg-white hover:bg-stone-50 rounded-sm sans text-xs font-medium flex items-center gap-2">
        <Plus size={13} /> Add contractor
      </button>

      <div className="mt-6 p-4 bg-stone-900 text-stone-100 rounded-sm flex items-center justify-between">
        <div>
          <div className="sans text-[10px] uppercase tracking-widest text-stone-400">Total Contract Research QRE (65% rule applied)</div>
          <div className="serif text-2xl font-bold text-sky-400 mono num-tabular mt-1">${fmt(contractQRE)}</div>
        </div>
        <FileCheck size={36} className="text-stone-700" />
      </div>
    </div>
  );
}

// ============================================================
// PHASE 10: COMPUTE
// ============================================================
function ComputePhase({ compute, setCompute, totalQRE, wageQRE, supplyQRE, contractQRE, ascResult, regularResult, scope, qsbEligible, onCite }) {
  const update = (k, v) => setCompute({ ...compute, [k]: v });
  const updatePrior = (idx, v) => {
    const next = [...compute.priorQRE];
    next[idx] = { ...next[idx], qre: v };
    setCompute({ ...compute, priorQRE: next });
  };

  const chosenMethod = compute.chosenMethod || compute.method || "ASC";

  // Gross credits per method (pre-280C)
  const ascGross = ascResult.gross;
  const ascReduced = ascResult.reduced;
  const regGross = regularResult.gross;
  const regReduced = regularResult.reduced;

  // Final credit under each scenario
  const ascFinal = compute.reducedCreditElection ? ascReduced : ascGross;
  const regFinal = compute.reducedCreditElection ? regReduced : regGross;
  const chosenFinal = chosenMethod === "REGULAR" ? regFinal : ascFinal;

  // Delta
  const delta = ascFinal - regFinal;
  const better = ascFinal >= regFinal ? "ASC" : "REGULAR";

  // QSB payroll preview
  const payrollEligibleAmount = Math.min(chosenFinal, 500000);

  // Prior QRE diagnostics for ASC
  const priorArr = compute.priorQRE.map(p => parseFloat(p.qre) || 0);
  const priorHasZero = priorArr.some(v => v === 0);
  const priorAllZero = priorArr.every(v => v === 0);
  const priorSum = priorArr.reduce((a, b) => a + b, 0);
  const prior3Avg = priorSum / 3;
  const asc50Base = prior3Avg * 0.5;
  const ascLine22 = priorSum / 6; // Form 6765 line 22 mechanic

  // Regular method diagnostics
  const fbp = Math.min(parseFloat(compute.fixedBasePct) || 0, 16) / 100;
  const avgGR = parseFloat(compute.avgGrossReceipts) || 0;
  const regComputedBase = fbp * avgGR;
  const regFloor = totalQRE * 0.5;
  const regFinalBase = Math.max(regComputedBase, regFloor);
  const regExcess = Math.max(0, totalQRE - regFinalBase);

  return (
    <div className="p-8">
      <PhaseHeader number="11" title="Credit Computation — Compare All Methods" description="All federal calculation methods computed side-by-side from the Steps for Calculations R&D reference. Enter inputs for each method, review outputs, then select the filing method." />

      <AIBadge>
        <strong>AI computes all methods simultaneously from approved QRE inputs.</strong> The staff member reviews outputs and selects the filing method. Final choice requires human sign-off and documented rationale.
      </AIBadge>

      {/* QRE SUMMARY */}
      <div className="mt-6 mb-6 p-5 border-2 border-stone-900 rounded-sm bg-stone-50">
        <h4 className="serif text-sm font-bold text-stone-900 uppercase tracking-widest mb-3">QRE Summary — Current Year</h4>
        <div className="grid grid-cols-4 gap-4">
          <QRECard label="Wages" value={wageQRE} cite="1.41-2(c)" onCite={onCite} />
          <QRECard label="Supplies" value={supplyQRE} cite="41(b)(2)" onCite={onCite} />
          <QRECard label="Contract (65%)" value={contractQRE} cite="1.41-2(e)" onCite={onCite} />
          <QRECard label="Total QRE" value={totalQRE} bold />
        </div>
      </div>

      {/* METHOD COMPARISON BANNER */}
      <div className="mb-6 p-4 bg-gradient-to-r from-stone-900 to-stone-800 text-stone-100 rounded-sm">
        <div className="grid grid-cols-3 gap-4 items-center">
          <div className="text-center">
            <div className="sans text-[10px] uppercase tracking-widest text-stone-400">ASC Final</div>
            <div className={`serif text-2xl font-bold num-tabular mono ${better === "ASC" ? "text-sky-400" : "text-stone-100"}`}>${fmt(ascFinal)}</div>
            {better === "ASC" && totalQRE > 0 && <div className="mono text-[9px] text-sky-400 mt-1 uppercase tracking-widest">▲ Higher</div>}
          </div>
          <div className="text-center border-x border-stone-700 px-3">
            <div className="sans text-[10px] uppercase tracking-widest text-stone-400">Delta (ASC − Regular)</div>
            <div className={`serif text-xl font-bold num-tabular mono ${delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-400" : "text-stone-300"}`}>
              {delta >= 0 ? "+" : ""}${fmt(delta)}
            </div>
            <div className="mono text-[9px] text-stone-500 mt-1 uppercase tracking-widest">
              {compute.reducedCreditElection ? "§280C Reduced" : "Gross"}
            </div>
          </div>
          <div className="text-center">
            <div className="sans text-[10px] uppercase tracking-widest text-stone-400">Regular Final</div>
            <div className={`serif text-2xl font-bold num-tabular mono ${better === "REGULAR" ? "text-sky-400" : "text-stone-100"}`}>${fmt(regFinal)}</div>
            {better === "REGULAR" && regFinal > 0 && <div className="mono text-[9px] text-sky-400 mt-1 uppercase tracking-widest">▲ Higher</div>}
          </div>
        </div>
      </div>

      {/* SIDE-BY-SIDE METHOD CARDS */}
      <div className="grid grid-cols-2 gap-5 mb-6">

        {/* ASC COLUMN */}
        <div className={`border-2 rounded-sm overflow-hidden ${chosenMethod === "ASC" ? "border-stone-900" : "border-stone-300"}`}>
          <div className={`px-4 py-3 flex items-center justify-between ${chosenMethod === "ASC" ? "bg-stone-900 text-sky-400" : "bg-stone-100 text-stone-700"}`}>
            <div>
              <h4 className="serif text-base font-bold">Alternative Simplified Credit</h4>
              <div className="mono text-[10px] uppercase tracking-widest opacity-75 mt-0.5">Method 2 · Form 6765 §B</div>
            </div>
            <CiteBadge id="41(c)(4)" onClick={onCite} />
          </div>

          <div className="p-4 space-y-4 bg-white">
            <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm">
              <div className="sans text-[10px] font-bold uppercase tracking-widest text-stone-700 mb-1">Formula</div>
              <div className="mono text-[11px] text-stone-800 leading-relaxed">
                ASC = 14% × (Current QRE − 50% × 3-yr avg prior QRE)
                <br />
                <span className="text-stone-500">Fallback if no QREs in any prior yr: 6% × Current QRE</span>
                <br />
                <span className="text-stone-500 italic">per §41(c)(4)(A)-(B)(ii)</span>
              </div>
            </div>

            <div>
              <div className="sans text-[10px] font-bold uppercase tracking-widest text-stone-700 mb-2">Inputs — Prior 3 Years QREs</div>
              <div className="grid grid-cols-3 gap-2">
                {compute.priorQRE.map((p, i) => (
                  <div key={i}>
                    <div className="mono text-[10px] text-stone-500 mb-1">Year {p.year}</div>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 sans text-xs">$</span>
                      <MoneyInput
                        value={p.qre}
                        onChange={e => updatePrior(i, e.target.value)}
                        className="w-full pl-5 pr-2 py-1.5 border border-stone-300 bg-white rounded-sm mono num-tabular text-xs focus:outline-none focus:border-stone-900"
                        placeholder="0"
                      />
                    </div>
                  </div>
                ))}
              </div>
              {priorHasZero && !priorAllZero && (
                <div className="mt-2 p-2 bg-sky-50 border border-sky-400 rounded-sm">
                  <p className="sans text-[10px] text-sky-900 leading-snug">
                    <AlertTriangle size={10} className="inline mr-1" /> One prior year = $0 → 6% fallback applies per §41(c)(4)(B)(ii).
                  </p>
                </div>
              )}
              {priorAllZero && (
                <div className="mt-2 p-2 bg-sky-50 border border-sky-400 rounded-sm">
                  <p className="sans text-[10px] text-sky-900 leading-snug">
                    <AlertTriangle size={10} className="inline mr-1" /> All prior years = $0 → 6% startup rule applies.
                  </p>
                </div>
              )}
            </div>

            <div className="p-3 bg-stone-900 text-stone-100 rounded-sm">
              <div className="mono text-[10px] uppercase tracking-widest text-stone-400 mb-2">Step-by-Step Computation</div>
              <div className="space-y-1 mono num-tabular text-[11px]">
                <CalcRow label="Prior 3-yr sum" value={priorSum} />
                <CalcRow label="Prior 3-yr average" value={prior3Avg} />
                <CalcRow label="50% of avg (base)" value={asc50Base} cite="Line 22 ÷ 6.0" />
                <CalcRow label="Current QRE" value={totalQRE} />
                <CalcRow label="Excess (Line 23)" value={Math.max(0, totalQRE - asc50Base)} />
                {(priorHasZero || priorAllZero) ? (
                  <>
                    <CalcRow label="Rate (fallback)" value="6.00%" isText />
                    <div className="pt-2 mt-2 border-t border-stone-700">
                      <CalcRow label="ASC Gross (6% × QRE)" value={ascGross} highlight />
                    </div>
                  </>
                ) : (
                  <>
                    <CalcRow label="Rate (Line 24)" value="14.00%" isText />
                    <div className="pt-2 mt-2 border-t border-stone-700">
                      <CalcRow label="ASC Gross" value={ascGross} highlight />
                    </div>
                  </>
                )}
                <CalcRow label="§280C Reduced (× 79%)" value={ascReduced} dim />
              </div>
            </div>

            <MethodFinalBanner
              label="ASC Final Credit"
              value={ascFinal}
              reduced={compute.reducedCreditElection}
              selected={chosenMethod === "ASC"}
              onSelect={() => update("chosenMethod", "ASC")}
            />
          </div>
        </div>

        {/* REGULAR COLUMN */}
        <div className={`border-2 rounded-sm overflow-hidden ${chosenMethod === "REGULAR" ? "border-stone-900" : "border-stone-300"}`}>
          <div className={`px-4 py-3 flex items-center justify-between ${chosenMethod === "REGULAR" ? "bg-stone-900 text-sky-400" : "bg-stone-100 text-stone-700"}`}>
            <div>
              <h4 className="serif text-base font-bold">Regular Credit Method</h4>
              <div className="mono text-[10px] uppercase tracking-widest opacity-75 mt-0.5">Method 1 · Form 6765 §A</div>
            </div>
            <CiteBadge id="41(a)(1)" onClick={onCite} />
          </div>

          <div className="p-4 space-y-4 bg-white">
            <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm">
              <div className="sans text-[10px] font-bold uppercase tracking-widest text-stone-700 mb-1">Formula</div>
              <div className="mono text-[11px] text-stone-800 leading-relaxed">
                Regular = 20% × (Current QRE − Base Amount)
                <br />
                <span className="text-stone-500">Base = Fixed-base % × Avg annual GR; floor = 50% of current QRE</span>
                <br />
                <span className="text-stone-500 italic">per §41(a)(1)(A)-(B); Form 6765 lines 6-13</span>
              </div>
            </div>

            <div>
              <div className="sans text-[10px] font-bold uppercase tracking-widest text-stone-700 mb-2">Inputs</div>
              <div className="space-y-2">
                <div>
                  <div className="mono text-[10px] text-stone-500 mb-1">Fixed-Base % (cap 16%) · Line 6</div>
                  <div className="relative">
                    <input
                      type="number"
                      value={compute.fixedBasePct}
                      onChange={e => update("fixedBasePct", e.target.value)}
                      max="16"
                      step="0.01"
                      className="w-full pl-2 pr-6 py-1.5 border border-stone-300 bg-white rounded-sm mono num-tabular text-xs focus:outline-none focus:border-stone-900"
                      placeholder="3.00"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 sans text-xs">%</span>
                  </div>
                  {(parseFloat(compute.fixedBasePct) || 0) > 16 && (
                    <div className="mt-1 mono text-[10px] text-rose-600">⚠ Capped to 16% per §41(c)(3)(C)(i)</div>
                  )}
                </div>
                <div>
                  <div className="mono text-[10px] text-stone-500 mb-1">Avg Annual Gross Receipts (prior 4 yrs) · Line 7</div>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 sans text-xs">$</span>
                    <MoneyInput
                      value={compute.avgGrossReceipts}
                      onChange={e => update("avgGrossReceipts", e.target.value)}
                      className="w-full pl-5 pr-2 py-1.5 border border-stone-300 bg-white rounded-sm mono num-tabular text-xs focus:outline-none focus:border-stone-900"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {(!compute.fixedBasePct || !compute.avgGrossReceipts) && (
                <div className="mt-2 p-2 bg-rose-50 border border-rose-300 rounded-sm">
                  <p className="sans text-[10px] text-rose-900 leading-snug">
                    <AlertTriangle size={10} className="inline mr-1" /> Regular method requires both inputs. Without defensible base data, use ASC.
                  </p>
                </div>
              )}
            </div>

            <div className="p-3 bg-stone-900 text-stone-100 rounded-sm">
              <div className="mono text-[10px] uppercase tracking-widest text-stone-400 mb-2">Step-by-Step Computation</div>
              <div className="space-y-1 mono num-tabular text-[11px]">
                <CalcRow label="Fixed-base % × Avg GR" value={regComputedBase} />
                <CalcRow label="50% QRE floor (Line 10)" value={regFloor} />
                <CalcRow label="Base Amount (greater)" value={regFinalBase} cite="§41(c)(5)" />
                <CalcRow label="Current QRE" value={totalQRE} />
                <CalcRow label="Excess" value={regExcess} />
                <CalcRow label="Rate (Line 13)" value="20.00%" isText />
                <div className="pt-2 mt-2 border-t border-stone-700">
                  <CalcRow label="Regular Gross" value={regGross} highlight />
                </div>
                <CalcRow label="§280C Reduced (× 79%)" value={regReduced} dim />
              </div>
            </div>

            <MethodFinalBanner
              label="Regular Final Credit"
              value={regFinal}
              reduced={compute.reducedCreditElection}
              selected={chosenMethod === "REGULAR"}
              onSelect={() => update("chosenMethod", "REGULAR")}
            />
          </div>
        </div>
      </div>

      {/* §280C OVERLAY */}
      <div className="mb-6 p-5 bg-sky-50 border border-sky-400 rounded-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h5 className="serif text-base font-semibold text-stone-900">§280C Reduced Credit Election</h5>
              <CiteBadge id="280C" onClick={onCite} />
            </div>
            <p className="sans text-xs text-stone-700 leading-relaxed mb-3">
              Reduces gross credit to <strong>79% of gross</strong> (regular multiplied by 15.8% instead of 20%; ASC multiplied by 79%). Most filers elect this to avoid reducing the §174A deduction. Applied to both method calcs above.
            </p>
            <label className="flex items-center gap-2 cursor-pointer p-2 bg-white border border-sky-400 rounded-sm">
              <input type="checkbox" checked={compute.reducedCreditElection} onChange={e => update("reducedCreditElection", e.target.checked)} />
              <span className="sans text-sm font-semibold text-stone-900">Elect §280C reduced credit (recommended)</span>
            </label>
          </div>
          <div className="p-3 bg-white border border-sky-400 rounded-sm min-w-[180px]">
            <div className="sans text-[9px] uppercase tracking-widest text-stone-500 mb-1">Toggle Impact</div>
            <div className="mono text-[10px] text-stone-600">Gross: <span className="font-bold num-tabular text-stone-900">${fmt(chosenMethod === "REGULAR" ? regGross : ascGross)}</span></div>
            <div className="mono text-[10px] text-stone-600">×79%: <span className="font-bold num-tabular text-sky-700">${fmt(chosenMethod === "REGULAR" ? regReduced : ascReduced)}</span></div>
            <div className="mt-1 pt-1 border-t border-stone-200">
              <div className="mono text-[9px] text-stone-500">Diff: ${fmt((chosenMethod === "REGULAR" ? regGross - regReduced : ascGross - ascReduced))}</div>
            </div>
          </div>
        </div>
      </div>

      {/* PAYROLL ELECTION PREVIEW */}
      <div className="mb-6 p-5 border border-stone-300 rounded-sm bg-white">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h5 className="serif text-base font-semibold text-stone-900">§41(h) Payroll Tax Credit Election</h5>
              <CiteBadge id="41(h)(1)" onClick={onCite} />
              <CiteBadge id="41(h)(4)" onClick={onCite} />
            </div>
            <p className="sans text-xs text-stone-700 leading-relaxed">
              Monetization layer — not a separate computation. QSB taxpayers may elect up to <strong>$500,000</strong> of the research credit against payroll taxes.
              {" "}{scope && qsbEligible !== undefined
                ? (qsbEligible ? <span className="text-emerald-700 font-semibold">Taxpayer appears QSB-eligible.</span> : <span className="text-rose-700 font-semibold">Taxpayer does not appear QSB-eligible (see Phase 10).</span>)
                : ""}
              {" "}Full QSB eligibility & election occur in Phase 10.
            </p>
          </div>
          <div className="p-3 bg-stone-50 border border-stone-200 rounded-sm min-w-[200px]">
            <div className="sans text-[9px] uppercase tracking-widest text-stone-500 mb-1">If elected (preview)</div>
            <div className="mono num-tabular text-lg font-bold text-stone-900">${fmt(payrollEligibleAmount)}</div>
            <div className="mono text-[9px] text-stone-500 mt-1">capped at $500,000 per §41(h)(4)(B)</div>
          </div>
        </div>
      </div>

      {/* CONTROLLED GROUP OVERLAY */}
      {scope && scope.controlledGroup && (
        <div className="mb-6 p-5 border border-stone-300 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <h5 className="serif text-base font-semibold text-stone-900">Controlled Group Aggregation</h5>
            <CiteBadge id="41(f)(1)" onClick={onCite} />
            <CiteBadge id="1.41-6" onClick={onCite} />
          </div>
          <HumanFlag>
            Controlled group flagged in Phase 1. A human must compute the credit on an <strong>aggregate group-level basis</strong> using the <em>same method</em> across all members, then allocate the group credit proportionately per §1.41-6(c). The side-by-side computation above reflects this entity only.
          </HumanFlag>
        </div>
      )}

      {/* METHOD SELECTION & RATIONALE */}
      <div className="p-5 bg-stone-900 text-stone-100 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h5 className="serif text-lg font-bold text-sky-400">Filing Method Selection</h5>
            <p className="sans text-xs text-stone-400 mt-0.5">Currently selected: <strong className="text-sky-400">{chosenMethod === "REGULAR" ? "Regular Credit" : "Alternative Simplified Credit (ASC)"}</strong></p>
          </div>
          <div className="text-right">
            <div className="mono text-[10px] uppercase tracking-widest text-stone-400">Chosen final credit</div>
            <div className="serif text-3xl font-bold text-sky-400 num-tabular mono">${fmt(chosenFinal)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            onClick={() => update("chosenMethod", "ASC")}
            className={`px-4 py-3 rounded-sm text-left border transition-all ${
              chosenMethod === "ASC"
                ? "bg-sky-400 text-stone-900 border-sky-400"
                : "bg-stone-800 text-stone-300 border-stone-700 hover:border-sky-500"
            }`}
          >
            <div className="flex items-center gap-2">
              {chosenMethod === "ASC" ? <CheckCircle2 size={14} /> : <Square size={14} />}
              <span className="sans text-sm font-semibold">Use ASC</span>
            </div>
            <div className="mono text-[10px] mt-1 opacity-75">${fmt(ascFinal)}</div>
          </button>
          <button
            onClick={() => update("chosenMethod", "REGULAR")}
            className={`px-4 py-3 rounded-sm text-left border transition-all ${
              chosenMethod === "REGULAR"
                ? "bg-sky-400 text-stone-900 border-sky-400"
                : "bg-stone-800 text-stone-300 border-stone-700 hover:border-sky-500"
            }`}
          >
            <div className="flex items-center gap-2">
              {chosenMethod === "REGULAR" ? <CheckCircle2 size={14} /> : <Square size={14} />}
              <span className="sans text-sm font-semibold">Use Regular</span>
            </div>
            <div className="mono text-[10px] mt-1 opacity-75">${fmt(regFinal)}</div>
          </button>
        </div>

        <div>
          <div className="sans text-[10px] font-semibold text-stone-400 uppercase tracking-widest mb-1">Rationale for method selection (human sign-off)</div>
          <textarea
            value={compute.methodSelectionNotes || ""}
            onChange={e => update("methodSelectionNotes", e.target.value)}
            className="w-full px-3 py-2 bg-stone-800 border border-stone-700 rounded-sm sans text-xs text-stone-100 focus:outline-none focus:border-sky-500 min-h-[70px]"
            placeholder="e.g., ASC produces a higher credit by $X and avoids the need to reconstruct 1984-88 fixed-base data. Prior year filings used ASC; maintaining consistency per §41(c)(4)(C)..."
          />
        </div>
      </div>
    </div>
  );
}

function CalcRow({ label, value, isText, highlight, dim, cite }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${dim ? "text-stone-500" : highlight ? "text-stone-100 font-semibold" : "text-stone-300"}`}>
        {label}
        {cite && <span className="text-stone-600 text-[9px] ml-1">[{cite}]</span>}
      </span>
      <span className={`num-tabular ${highlight ? "text-sky-400 font-bold text-[13px]" : dim ? "text-stone-400" : "text-sky-200"}`}>
        {isText ? value : `$${fmt(value)}`}
      </span>
    </div>
  );
}

function MethodFinalBanner({ label, value, reduced, selected, onSelect }) {
  return (
    <button
      onClick={onSelect}
      className={`w-full p-3 rounded-sm border-2 text-left transition-all ${
        selected
          ? "bg-sky-400 border-sky-400 text-stone-900"
          : "bg-white border-stone-300 text-stone-700 hover:border-sky-500"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {selected ? <CheckCircle2 size={15} /> : <Square size={15} />}
          <div>
            <div className="sans text-[10px] uppercase tracking-widest font-bold">{label}</div>
            <div className="mono text-[9px] opacity-70">{reduced ? "§280C reduced" : "gross"}</div>
          </div>
        </div>
        <div className="serif text-xl font-bold num-tabular mono">${fmt(value)}</div>
      </div>
    </button>
  );
}

// ============================================================
// PHASE 11: ELECTIONS
// ============================================================
function ElectionsPhase({ compute, setCompute, scope, qsbEligible, finalCredit, payrollCreditAmount, onCite }) {
  const update = (k, v) => setCompute({ ...compute, [k]: v });
  return (
    <div className="p-8">
      <PhaseHeader number="12" title="Special Elections & Controlled Group" description="Payroll tax election for QSBs, controlled group aggregation, and final method decision." />

      <div className="mb-6 p-5 border border-stone-300 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="serif text-base font-bold text-stone-900">§41(h) Payroll Tax Credit Election</h4>
          <div className="flex gap-1.5">
            <CiteBadge id="41(h)(1)" onClick={onCite} />
            <CiteBadge id="41(h)(3)" onClick={onCite} />
            <CiteBadge id="41(h)(4)" onClick={onCite} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <TestBox label="Gross receipts < $5M" passed={parseFloat(scope.grossReceiptsCurrent) > 0 && parseFloat(scope.grossReceiptsCurrent) < 5000000} detail={scope.grossReceiptsCurrent ? `$${fmt(parseFloat(scope.grossReceiptsCurrent))}` : "Not entered"} />
          <TestBox label="Within 5 yrs of first revenue" passed={scope.firstRevenueYear && (parseInt(scope.taxYear) - parseInt(scope.firstRevenueYear)) < 5} detail={scope.firstRevenueYear ? `First: ${scope.firstRevenueYear}` : "Not entered"} />
          <TestBox label="QSB eligible overall" passed={qsbEligible} detail={qsbEligible ? "Pass all tests" : "Fail"} />
        </div>

        {qsbEligible ? (
          <div>
            <label className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-300 rounded-sm cursor-pointer">
              <input type="checkbox" checked={compute.payrollTaxElection} onChange={e => update("payrollTaxElection", e.target.checked)} />
              <span className="sans text-sm text-stone-900">Elect payroll tax credit (up to <strong>$500,000</strong>)</span>
            </label>
            {compute.payrollTaxElection && (
              <div className="mt-3 p-4 bg-stone-900 text-stone-100 rounded-sm flex items-center justify-between">
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone-400">Elected payroll tax credit</div>
                  <div className="serif text-2xl font-bold text-sky-400 mono num-tabular">${fmt(payrollCreditAmount)}</div>
                </div>
                <div className="text-right sans text-xs text-stone-400">
                  <div>Final credit: ${fmt(finalCredit)}</div>
                  <div>Cap: $500,000</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <HumanFlag>
            Taxpayer does <strong>not</strong> currently appear to meet QSB criteria. A human must verify gross receipts history, first-revenue-year, and predecessor events.
          </HumanFlag>
        )}
      </div>

      <div className="mb-6 p-5 border border-stone-300 rounded-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="serif text-base font-bold text-stone-900">Controlled Group Aggregation</h4>
          <div className="flex gap-1.5"><CiteBadge id="41(f)(1)" onClick={onCite} /><CiteBadge id="1.41-6" onClick={onCite} /></div>
        </div>
        {scope.controlledGroup ? (
          <HumanFlag>
            <strong>Controlled group flagged in Phase 1.</strong> A human must confirm membership, ensure same computation method across members,
            aggregate QREs, allocate credit proportionately, and review intercompany arrangements.
          </HumanFlag>
        ) : (
          <p className="sans text-sm text-stone-600">No controlled group flagged.</p>
        )}
      </div>

      <div className="p-6 bg-stone-900 text-stone-100 rounded-sm">
        <div className="sans text-[10px] uppercase tracking-widest text-stone-400 mb-2">Final Recommended Federal Credit</div>
        <div className="serif text-5xl font-bold text-sky-400 mono num-tabular mb-3">${fmt(finalCredit)}</div>
        <div className="flex items-center gap-4 mono text-[11px] text-stone-300">
          <span>Method: {(compute.chosenMethod || compute.method) === "REGULAR" ? "Regular" : "ASC"}</span>
          <span>§280C: {compute.reducedCreditElection ? "Reduced" : "Gross"}</span>
          {compute.payrollTaxElection && <span className="text-sky-400">Payroll election: ${fmt(payrollCreditAmount)}</span>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PHASE 13: CLIENT IMPACT — tax dollars saved
// ============================================================
// State-by-state R&D credit reference for common states (simplified, 2025)
const STATE_RD_CREDIT_REFERENCE = {
  "CA": { name: "California", rate: "15% / 3% ASC", note: "Not refundable; $5M business credit limitation 2024-2027" },
  "UT": { name: "Utah", rate: "5% + 7.5% basic", note: "Carryforward 14 years" },
  "NY": { name: "New York (Excelsior)", rate: "50% of federal × NY portion, up to 6-8%", note: "Refundable for program participants" },
  "MA": { name: "Massachusetts", rate: "10%", note: "Not refundable; carryforward 15 years" },
  "TX": { name: "Texas", rate: "8.722% (new 2026) / 5% (legacy)", note: "Refundable for no-tax-due entities" },
  "AZ": { name: "Arizona", rate: "24% / 15%", note: "Partially refundable in some years" },
  "GA": { name: "Georgia", rate: "10%", note: "Offsets 50% of income tax; carryforward 10 years" },
  "IL": { name: "Illinois", rate: "6.5%", note: "Carryforward 5 years" },
  "MN": { name: "Minnesota", rate: "10% / 4%", note: "Refundable portion available" },
  "OH": { name: "Ohio", rate: "7% of QREs above 3-yr avg", note: "Nonrefundable CAT credit" },
  "PA": { name: "Pennsylvania", rate: "10% / 20% small business", note: "Tradable; carryforward 15 years" },
};

function ImpactPhase({ scope, engagement, setEngagement, compute, totalQRE, finalCredit, onCite }) {
  const update = (k, v) => setEngagement({ ...engagement, [k]: v });

  // Federal tax rate — default 21% for C-corp, else prompt custom
  const fedRate = (parseFloat(engagement.impactFedRate) || 0) / 100;
  const stateRate = (parseFloat(engagement.impactStateRate) || 0) / 100;
  const customRate = (parseFloat(engagement.impactCustomRate) || 0) / 100;
  const stateCredit = parseFloat(engagement.impactStateCredit) || 0;
  const years = Math.max(1, parseInt(engagement.impactYearsOfBenefit) || 1);

  // A research credit is a DOLLAR-FOR-DOLLAR reduction in tax.
  // The "tax savings" from the credit itself = the credit amount.
  // However, if §280C reduced credit NOT elected, the §174A deduction is also reduced,
  // causing a secondary tax increase. We model both scenarios.
  const credit280cElected = compute.reducedCreditElection;

  // Scenario: Federal credit (dollar-for-dollar)
  const federalCreditSavings = finalCredit;

  // State credit (if separately computed)
  const stateCreditSavings = stateCredit;

  // Total direct credit savings
  const totalCreditSavings = federalCreditSavings + stateCreditSavings;

  // Effective cost of the credit based on §280C election:
  // If §280C elected → you get less credit (79% of gross) BUT keep full deduction. No offset.
  // If §280C NOT elected → you get full credit BUT must reduce §174A deduction by credit amount
  //   → offsetting tax increase of (credit × fedRate)
  const s280cOffset = credit280cElected ? 0 : finalCredit * fedRate;
  const netFederalBenefit = federalCreditSavings - s280cOffset;

  // Total net benefit
  const netTotalBenefit = netFederalBenefit + stateCreditSavings;

  // ROI calculation (vs fee)
  const fee = (() => {
    if (engagement.feeStructure === "fixed") return parseFloat(engagement.fixedFee) || 0;
    if (engagement.feeStructure === "contingent") {
      const raw = (parseFloat(engagement.contingentPct) || 0) / 100 * finalCredit;
      const cap = parseFloat(engagement.contingentCap) || 0;
      return cap > 0 ? Math.min(raw, cap) : raw;
    }
    if (engagement.feeStructure === "hybrid") {
      const fixed = parseFloat(engagement.fixedFee) || 0;
      const cont = (parseFloat(engagement.contingentPct) || 0) / 100 * finalCredit;
      return fixed + cont;
    }
    return 0;
  })();

  const netAfterFee = netTotalBenefit - fee;
  const roi = fee > 0 ? ((netAfterFee / fee) * 100) : null;

  // Multi-year projection (if rolled forward or annually recurring)
  const multiYearBenefit = netTotalBenefit * years;

  // Impact ratio — how many cents of benefit per dollar of QRE
  const impactRatio = totalQRE > 0 ? netTotalBenefit / totalQRE : 0;

  // Copy the summary email
  const [copied, setCopied] = useState(false);
  const copyClientSummary = async () => {
    const summary = buildClientSummaryText({
      scope, engagement, finalCredit, totalCreditSavings, netTotalBenefit, fee,
      netAfterFee, stateCredit, fedRate, stateRate, years, multiYearBenefit,
    });
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { alert("Copy failed."); }
  };

  const downloadImpactOnePager = () => {
    const html = document.getElementById("client-impact-onepager")?.outerHTML || "";
    const fullHtml = `<!DOCTYPE html><html><head><title>Tax Impact Summary - ${scope.clientName || "Client"}</title><style>body{font-family:Inter,system-ui,sans-serif;max-width:800px;margin:40px auto;padding:20px;color:#0c0c0c;line-height:1.6}h1{color:#0284c7;border-bottom:3px solid #0284c7;padding-bottom:8px;font-size:28pt}h2{color:#0c0c0c;font-size:16pt;margin-top:28px}table{width:100%;border-collapse:collapse;margin:12px 0}td,th{border:1px solid #d6d3d1;padding:8px 12px;text-align:left}th{background:#f0f9ff;color:#075985}.big-number{font-size:32pt;color:#0284c7;font-weight:700}.footer-note{margin-top:40px;padding-top:20px;border-top:1px solid #d6d3d1;color:#6b7280;font-size:10pt}</style></head><body>${html}</body></html>`;
    const blob = new Blob([fullHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `TaxImpact_${(scope.clientName || "Client").replace(/\s/g, "_")}_${scope.taxYear}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8">
      <PhaseHeader number="13" title="Client Impact — Tax Dollars Saved" description="Translate the R&D credit into real dollars saved. Model federal and state rates, §280C trade-offs, fee-adjusted ROI, and multi-year impact — exactly the numbers your client wants to see." />

      {/* HERO IMPACT BANNER */}
      <div className="mb-6 p-6 bg-gradient-to-br from-stone-900 via-stone-900 to-sky-900 text-stone-100 rounded-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 opacity-5">
          <svg viewBox="0 0 200 200" fill="none">
            <polygon points="100,20 180,60 180,140 100,180 20,140 20,60" stroke="#38BDF8" strokeWidth="3" fill="none" />
            <polygon points="100,60 140,80 140,120 100,140 60,120 60,80" stroke="#38BDF8" strokeWidth="3" fill="none" />
          </svg>
        </div>
        <div className="relative">
          <div className="sans text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-2 font-semibold">Total Estimated Tax Benefit</div>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="serif text-7xl font-bold text-sky-400 num-tabular mono">${fmt(netTotalBenefit)}</span>
            {years > 1 && <span className="sans text-lg text-stone-400">/ year × {years} = <span className="text-sky-400 font-bold">${fmt(multiYearBenefit)}</span> over {years} years</span>}
          </div>
          <div className="grid grid-cols-4 gap-4 mt-6 pt-5 border-t border-stone-700">
            <ImpactHeroMetric label="Federal Credit" value={federalCreditSavings} color="sky" />
            <ImpactHeroMetric label="State Credit" value={stateCreditSavings} color="sky" />
            <ImpactHeroMetric label="§280C Offset" value={-s280cOffset} color={s280cOffset > 0 ? "rose" : "stone"} />
            <ImpactHeroMetric label="Total Qualified Expenses" value={totalQRE} color="stone" />
          </div>
        </div>
      </div>

      {/* IMPACT INPUTS */}
      <div className="mb-6">
        <h4 className="sans text-sm font-bold text-stone-900 uppercase tracking-widest mb-3">Impact Assumptions</h4>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <button
            onClick={() => { update("impactScenario", "corp"); update("impactFedRate", "21"); }}
            className={`p-3 border-2 rounded-sm text-left transition-colors ${engagement.impactScenario === "corp" ? "border-sky-500 bg-sky-50" : "border-stone-300 bg-white hover:border-sky-400"}`}>
            <div className="sans text-sm font-bold text-stone-900">C-Corporation</div>
            <div className="sans text-xs text-stone-600 mt-0.5">Federal rate: 21% flat</div>
          </button>
          <button
            onClick={() => { update("impactScenario", "passthrough"); update("impactFedRate", "37"); }}
            className={`p-3 border-2 rounded-sm text-left transition-colors ${engagement.impactScenario === "passthrough" ? "border-sky-500 bg-sky-50" : "border-stone-300 bg-white hover:border-sky-400"}`}>
            <div className="sans text-sm font-bold text-stone-900">Pass-Through / Individual</div>
            <div className="sans text-xs text-stone-600 mt-0.5">Top fed rate: 37% (2025)</div>
          </button>
          <button
            onClick={() => update("impactScenario", "custom")}
            className={`p-3 border-2 rounded-sm text-left transition-colors ${engagement.impactScenario === "custom" ? "border-sky-500 bg-sky-50" : "border-stone-300 bg-white hover:border-sky-400"}`}>
            <div className="sans text-sm font-bold text-stone-900">Custom Blended</div>
            <div className="sans text-xs text-stone-600 mt-0.5">Specify your own rate</div>
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Field label="Federal Tax Rate" hint="For §280C offset modeling">
            <div className="relative">
              <input type="number" value={engagement.impactFedRate} onChange={e => update("impactFedRate", e.target.value)} step="0.1"
                className="w-full pl-3 pr-8 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500" placeholder="21" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">%</span>
            </div>
          </Field>
          <Field label="State" hint="For labeling">
            <select value={engagement.impactStateName} onChange={e => {
              const stateKey = e.target.value;
              update("impactStateName", stateKey);
            }}
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500">
              <option value="">— Select state —</option>
              {Object.entries(STATE_RD_CREDIT_REFERENCE).map(([k, v]) => (
                <option key={k} value={k}>{v.name}</option>
              ))}
              <option value="other">Other / Not listed</option>
            </select>
          </Field>
          <Field label="State Tax Rate (income)" hint="Optional; for total effective rate">
            <div className="relative">
              <input type="number" value={engagement.impactStateRate} onChange={e => update("impactStateRate", e.target.value)} step="0.01"
                className="w-full pl-3 pr-8 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500" placeholder="0" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">%</span>
            </div>
          </Field>
          <Field label="State R&D Credit Amount" hint="From state study, if separate">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 sans text-sm">$</span>
              <MoneyInput value={engagement.impactStateCredit} onChange={e => update("impactStateCredit", e.target.value)}
                className="w-full pl-7 pr-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500" placeholder="0" />
            </div>
          </Field>
        </div>

        {engagement.impactStateName && STATE_RD_CREDIT_REFERENCE[engagement.impactStateName] && (
          <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded-sm">
            <div className="flex items-start gap-2">
              <Info size={12} className="text-sky-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="sans text-xs text-stone-900">
                  <strong>{STATE_RD_CREDIT_REFERENCE[engagement.impactStateName].name} R&D Credit:</strong> {STATE_RD_CREDIT_REFERENCE[engagement.impactStateName].rate}
                </div>
                <div className="sans text-[11px] text-stone-600 mt-0.5">{STATE_RD_CREDIT_REFERENCE[engagement.impactStateName].note}</div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 gap-4">
          <Field label="Multi-Year Projection" hint="If benefit recurs annually">
            <input type="number" value={engagement.impactYearsOfBenefit} onChange={e => update("impactYearsOfBenefit", e.target.value)} min="1" max="20"
              className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm mono num-tabular text-sm focus:outline-none focus:border-sky-500" placeholder="1" />
          </Field>
          <Field label="§280C Reduced Credit Election" hint="From Phase 11">
            <div className="px-3 py-2 border border-stone-300 bg-stone-50 rounded-sm sans text-sm">
              {credit280cElected ? (
                <span className="text-emerald-700 font-semibold"><CheckCircle2 size={12} className="inline mr-1" /> Elected — No deduction offset</span>
              ) : (
                <span className="text-amber-700 font-semibold"><AlertTriangle size={12} className="inline mr-1" /> Not Elected — {fmt(s280cOffset)} offset applies</span>
              )}
            </div>
          </Field>
        </div>
      </div>

      {/* DOLLAR BREAKDOWN */}
      <div className="mb-6">
        <h4 className="sans text-sm font-bold text-stone-900 uppercase tracking-widest mb-3">Dollar-by-Dollar Breakdown</h4>
        <div className="border border-stone-300 rounded-sm overflow-hidden bg-white">
          <table className="w-full">
            <thead className="bg-stone-900 text-sky-400">
              <tr>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-4 py-3">Component</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-left px-4 py-3">Mechanism</th>
                <th className="sans text-[10px] font-semibold uppercase tracking-widest text-right px-4 py-3">Impact</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-stone-200">
                <td className="px-4 py-3 sans text-sm font-semibold text-stone-900">Federal R&D Credit</td>
                <td className="px-4 py-3 sans text-xs text-stone-600">Dollar-for-dollar reduction in federal income tax liability per IRC §41(a)</td>
                <td className="px-4 py-3 text-right mono num-tabular text-base font-bold text-emerald-700">+${fmt(federalCreditSavings)}</td>
              </tr>
              {stateCreditSavings > 0 && (
                <tr className="border-t border-stone-200">
                  <td className="px-4 py-3 sans text-sm font-semibold text-stone-900">State R&D Credit</td>
                  <td className="px-4 py-3 sans text-xs text-stone-600">State-specific credit{engagement.impactStateName && STATE_RD_CREDIT_REFERENCE[engagement.impactStateName] ? ` (${STATE_RD_CREDIT_REFERENCE[engagement.impactStateName].name})` : ""}</td>
                  <td className="px-4 py-3 text-right mono num-tabular text-base font-bold text-emerald-700">+${fmt(stateCreditSavings)}</td>
                </tr>
              )}
              {s280cOffset > 0 && (
                <tr className="border-t border-stone-200 bg-rose-50/50">
                  <td className="px-4 py-3 sans text-sm font-semibold text-stone-900">§280C Deduction Reduction</td>
                  <td className="px-4 py-3 sans text-xs text-stone-600">§280C: Without reduced credit election, §174A deduction is reduced by credit amount, creating offsetting tax at {(fedRate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3 text-right mono num-tabular text-base font-bold text-rose-700">−${fmt(s280cOffset)}</td>
                </tr>
              )}
              <tr className="border-t-2 border-stone-900 bg-sky-50">
                <td className="px-4 py-3 sans text-base font-bold text-stone-900">Net Tax Savings</td>
                <td className="px-4 py-3 sans text-xs text-stone-700 italic">Combined federal + state benefit after §280C</td>
                <td className="px-4 py-3 text-right mono num-tabular text-xl font-bold text-sky-700">${fmt(netTotalBenefit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ROI BOX */}
      {fee > 0 && (
        <div className="mb-6 p-5 border-2 border-sky-500 bg-gradient-to-br from-sky-50 to-white rounded-sm">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <h4 className="sans text-sm font-bold text-stone-900 uppercase tracking-widest mb-3">Return on Investment</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-500 mb-1">Linked Accounting Fee</div>
                  <div className="mono num-tabular text-xl font-bold text-stone-900">${fmt(fee)}</div>
                </div>
                <div>
                  <div className="sans text-[10px] uppercase tracking-widest text-stone-500 mb-1">Net Tax Savings After Fee</div>
                  <div className="mono num-tabular text-xl font-bold text-emerald-700">${fmt(netAfterFee)}</div>
                </div>
              </div>
            </div>
            <div className="text-center px-6 py-4 bg-stone-900 rounded-sm min-w-[180px]">
              <div className="sans text-[9px] uppercase tracking-[0.2em] text-sky-300 font-semibold mb-1">Client ROI</div>
              {roi !== null ? (
                <>
                  <div className="serif text-3xl font-bold text-sky-400 num-tabular mono">{roi >= 0 ? "+" : ""}{roi.toFixed(0)}%</div>
                  <div className="mono text-[9px] text-stone-400 mt-1">every $1 spent returns ${(netAfterFee / fee + 1).toFixed(2)}</div>
                </>
              ) : (
                <div className="serif text-2xl font-bold text-stone-400">—</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* IMPACT METRICS STRIP */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <ImpactMetric label="Benefit per $1 QRE" value={`$${impactRatio.toFixed(3)}`} sublabel="cents of savings" />
        <ImpactMetric label="Total Qualified Expenses" value={`$${fmt(totalQRE)}`} sublabel="current year" />
        <ImpactMetric label="Effective Benefit Rate" value={`${totalQRE > 0 ? ((netTotalBenefit / totalQRE) * 100).toFixed(2) : "0"}%`} sublabel="of QRE" />
        <ImpactMetric label={years > 1 ? `${years}-Year Total` : "Annual Benefit"} value={`$${fmt(multiYearBenefit)}`} sublabel={years > 1 ? "projected" : "single year"} />
      </div>

      {/* CLIENT-FACING ONE-PAGER */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="sans text-sm font-bold text-stone-900 uppercase tracking-widest">Client-Facing One-Pager</h4>
          <div className="flex items-center gap-2 no-print">
            <button onClick={copyClientSummary} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 bg-white hover:bg-sky-50 hover:border-sky-400 rounded-sm flex items-center gap-1.5">
              <Copy size={12} /> {copied ? "Copied!" : "Copy Email"}
            </button>
            <button onClick={downloadImpactOnePager} className="sans text-xs font-medium px-3 py-1.5 bg-stone-900 text-sky-400 rounded-sm hover:bg-stone-800 flex items-center gap-1.5">
              <Download size={12} /> Download One-Pager
            </button>
          </div>
        </div>

        <div id="client-impact-onepager" className="p-10 bg-white border-2 border-stone-300 rounded-sm" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
          <div className="border-b-4 border-sky-500 pb-4 mb-6">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-sky-600 mb-1 font-bold">Tax Impact Summary</div>
            <h1 className="text-4xl font-bold text-stone-900 tracking-tight">{scope.clientName || "[Client]"}</h1>
            <div className="text-sm text-stone-600 mt-1">Tax Year {scope.taxYear} · Research & Development Tax Credit</div>
          </div>

          <h2 className="text-lg font-bold text-stone-900 mb-2">What This Credit Means for You</h2>
          <p className="text-sm text-stone-800 leading-relaxed mb-4">
            Based on our Study of your {scope.taxYear} research activities, {scope.clientName || "your company"} has qualified for a federal R&D tax credit of <strong>${fmt(finalCredit)}</strong>. After accounting for {credit280cElected ? "the §280C reduced credit election" : `the §280C deduction reduction at your ${(fedRate * 100).toFixed(1)}% federal rate`}{stateCreditSavings > 0 ? ` and a separately computed state credit of $${fmt(stateCreditSavings)}` : ""}, we estimate your <strong>total net tax savings at ${fmt(netTotalBenefit)}</strong> for this tax year.
          </p>

          <div className="my-6 p-6 bg-gradient-to-r from-sky-100 to-sky-50 border-l-4 border-sky-500 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sky-700 font-bold mb-1">Total Estimated Tax Savings</div>
            <div className="text-5xl font-bold text-sky-700 num-tabular">${fmt(netTotalBenefit)}</div>
            {years > 1 && (
              <div className="text-sm text-stone-700 mt-2">
                Projected over {years} years: <strong className="text-sky-700">${fmt(multiYearBenefit)}</strong>
              </div>
            )}
          </div>

          <h2 className="text-lg font-bold text-stone-900 mb-2 mt-6">How We Got There</h2>
          <table className="w-full text-sm mb-4">
            <tbody>
              <tr>
                <td className="py-2 border-b border-stone-200 text-stone-700">Total Qualified Research Expenses (QREs)</td>
                <td className="py-2 border-b border-stone-200 text-right mono num-tabular font-semibold">${fmt(totalQRE)}</td>
              </tr>
              <tr>
                <td className="py-2 border-b border-stone-200 text-stone-700">Federal Research Credit ({(compute.chosenMethod || compute.method) === "REGULAR" ? "Regular Method" : "ASC"})</td>
                <td className="py-2 border-b border-stone-200 text-right mono num-tabular font-semibold text-emerald-700">+${fmt(federalCreditSavings)}</td>
              </tr>
              {stateCreditSavings > 0 && (
                <tr>
                  <td className="py-2 border-b border-stone-200 text-stone-700">State R&D Credit{engagement.impactStateName && STATE_RD_CREDIT_REFERENCE[engagement.impactStateName] ? ` (${STATE_RD_CREDIT_REFERENCE[engagement.impactStateName].name})` : ""}</td>
                  <td className="py-2 border-b border-stone-200 text-right mono num-tabular font-semibold text-emerald-700">+${fmt(stateCreditSavings)}</td>
                </tr>
              )}
              {s280cOffset > 0 && (
                <tr>
                  <td className="py-2 border-b border-stone-200 text-stone-700">Less: §280C Deduction Offset at {(fedRate * 100).toFixed(1)}%</td>
                  <td className="py-2 border-b border-stone-200 text-right mono num-tabular font-semibold text-rose-700">−${fmt(s280cOffset)}</td>
                </tr>
              )}
              <tr className="bg-sky-50">
                <td className="py-3 border-t-2 border-sky-500 text-stone-900 font-bold text-base">Net Tax Savings</td>
                <td className="py-3 border-t-2 border-sky-500 text-right mono num-tabular font-bold text-base text-sky-700">${fmt(netTotalBenefit)}</td>
              </tr>
            </tbody>
          </table>

          {fee > 0 && (
            <>
              <h2 className="text-lg font-bold text-stone-900 mb-2 mt-6">Your Return on Investment</h2>
              <table className="w-full text-sm mb-4">
                <tbody>
                  <tr>
                    <td className="py-2 border-b border-stone-200 text-stone-700">Linked Accounting Fee</td>
                    <td className="py-2 border-b border-stone-200 text-right mono num-tabular">${fmt(fee)}</td>
                  </tr>
                  <tr>
                    <td className="py-2 border-b border-stone-200 text-stone-700">Net Savings After Fee</td>
                    <td className="py-2 border-b border-stone-200 text-right mono num-tabular font-semibold text-emerald-700">${fmt(netAfterFee)}</td>
                  </tr>
                  {roi !== null && (
                    <tr>
                      <td className="py-2 text-stone-700"><strong>ROI</strong></td>
                      <td className="py-2 text-right mono num-tabular font-bold text-sky-700">{roi >= 0 ? "+" : ""}{roi.toFixed(0)}%</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}

          <h2 className="text-lg font-bold text-stone-900 mb-2 mt-6">Key Assumptions</h2>
          <ul className="text-sm text-stone-700 leading-relaxed pl-5" style={{ listStyle: "disc" }}>
            <li>Federal tax rate applied: {(fedRate * 100).toFixed(1)}% ({engagement.impactScenario === "corp" ? "C-Corporation statutory rate" : engagement.impactScenario === "passthrough" ? "top individual rate for pass-through owners" : "custom blended rate"})</li>
            {stateRate > 0 && <li>State income tax rate: {(stateRate * 100).toFixed(2)}%</li>}
            <li>§280C reduced credit election: <strong>{credit280cElected ? "Elected" : "Not elected"}</strong></li>
            <li>Credit computation method: <strong>{(compute.chosenMethod || compute.method) === "REGULAR" ? "Regular Credit (§41(a)(1))" : "Alternative Simplified Credit (§41(c)(4))"}</strong></li>
            {years > 1 && <li>Multi-year projection assumes substantially similar R&D activity recurring for {years} years</li>}
          </ul>

          {engagement.impactCustomNote && (
            <>
              <h2 className="text-lg font-bold text-stone-900 mb-2 mt-6">Additional Notes</h2>
              <p className="text-sm text-stone-800 leading-relaxed whitespace-pre-wrap">{engagement.impactCustomNote}</p>
            </>
          )}

          <div className="mt-10 pt-4 border-t border-stone-300 text-xs text-stone-500 italic">
            This summary is prepared by Linked Accounting LLP as part of the R&D Tax Credit Study engagement for {scope.clientName || "[Client]"}. Amounts shown are estimates based on professional judgment and the facts provided. The final credit position is supported by the complete R&D Study report and underlying workpapers. This summary is not a substitute for the detailed Study report or professional tax advice. {scope.taxYear} figures reflect current law as of the date of this summary.
          </div>
        </div>
      </div>

      {/* CUSTOM NOTE */}
      <div className="mb-6 p-4 border border-stone-300 rounded-sm bg-stone-50">
        <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1">Additional Notes for Client Summary (optional)</div>
        <textarea value={engagement.impactCustomNote || ""} onChange={e => update("impactCustomNote", e.target.value)}
          className="w-full px-3 py-2 border border-stone-300 bg-white rounded-sm sans text-sm focus:outline-none focus:border-sky-500 min-h-[80px]"
          placeholder="e.g., We recommend maintaining contemporaneous project records going forward to support ongoing annual credits. Based on sustained R&D spending, we project comparable benefits in future tax years." />
      </div>
    </div>
  );
}

function buildClientSummaryText({ scope, engagement, finalCredit, totalCreditSavings, netTotalBenefit, fee, netAfterFee, stateCredit, fedRate, stateRate, years, multiYearBenefit }) {
  const clientName = scope.clientName || "[Client]";
  const contact = scope.clientContactName || "[Contact]";
  const firstName = contact.split(",")[0].split(" ")[0];
  const stateCreditLine = stateCredit > 0 ? `\nState R&D credit: $${Number(stateCredit).toLocaleString()}` : "";
  const multiYearLine = years > 1 ? `\n\nProjected over ${years} years of substantially similar R&D activity, this benefit would total approximately $${Math.round(multiYearBenefit).toLocaleString()}.` : "";
  const roiLine = fee > 0 ? `\n\nAfter our fee of $${Math.round(fee).toLocaleString()}, your net benefit is $${Math.round(netAfterFee).toLocaleString()} — a return of ${((netAfterFee / fee) * 100).toFixed(0)}% on your investment in the study.` : "";

  return `Hi ${firstName},

I'm pleased to share the results of our R&D tax credit study for ${clientName} for tax year ${scope.taxYear}.

SUMMARY:
Federal research credit: $${Math.round(finalCredit).toLocaleString()}${stateCreditLine}
Net estimated tax savings (after §280C): $${Math.round(netTotalBenefit).toLocaleString()}${multiYearLine}${roiLine}

These savings represent real dollars that reduce your ${scope.taxYear} tax liability. The credit is supported by our full study report, which includes technical narratives for each qualified activity, detailed QRE schedules for wages, supplies, and contract research, and a complete authority index citing the Internal Revenue Code and Treasury Regulations that support each position taken.

A detailed tax impact one-pager is attached. The complete R&D Study report will follow separately.

Please reach out with any questions — we're happy to walk through any part of the analysis.

Best,
${engagement.partnerName || "[Partner Name]"}
${engagement.partnerTitle || "Partner"}
${engagement.firmName || "Linked Accounting LLP"}
${engagement.firmEmail || ""}
${engagement.firmPhone || ""}`;
}

function ImpactHeroMetric({ label, value, color }) {
  const colorMap = {
    sky: "text-sky-400",
    rose: "text-rose-400",
    emerald: "text-emerald-400",
    stone: "text-stone-300",
  };
  return (
    <div>
      <div className="sans text-[9px] uppercase tracking-widest text-stone-400 font-semibold mb-1">{label}</div>
      <div className={`mono num-tabular text-lg font-bold ${colorMap[color] || "text-stone-300"}`}>
        {value < 0 ? "−" : ""}${fmt(Math.abs(value))}
      </div>
    </div>
  );
}

function ImpactMetric({ label, value, sublabel }) {
  return (
    <div className="p-4 bg-white border border-stone-300 rounded-sm">
      <div className="sans text-[9px] uppercase tracking-widest font-semibold text-stone-500 mb-1">{label}</div>
      <div className="serif text-2xl font-bold text-stone-900 num-tabular mono">{value}</div>
      <div className="sans text-[10px] text-stone-500 mt-1">{sublabel}</div>
    </div>
  );
}

// ============================================================
// PHASE 14: REPORT — with document index
// ============================================================
function ReportPhase({ scope, screen, projects, employees, supplies, contractors, compute, totalQRE, wageQRE, supplyQRE, contractQRE, finalCredit, selectedResult, ascResult, regularResult, documents }) {
  const [reportView, setReportView] = useState("summary");
  const printReport = () => window.print();

  const downloadHTML = () => {
    const html = document.getElementById("rd-final-report").outerHTML;
    const blob = new Blob([`<!DOCTYPE html><html><head><title>RD Study - ${scope.clientName || 'Client'} ${scope.taxYear}</title><style>body{font-family:Georgia,serif;padding:40px;max-width:900px;margin:auto;color:#1c1917}table{border-collapse:collapse;width:100%;margin:12px 0}td,th{border:1px solid #d6d3d1;padding:6px 10px;text-align:left;font-size:11pt}th{background:#1c1917;color:#38bdf8;font-size:10pt;text-transform:uppercase}h1,h2,h3{color:#1c1917}h1{border-bottom:3px solid #1c1917;padding-bottom:8px}h2{border-bottom:1px solid #1c1917;padding-bottom:4px;margin-top:30px}.cite{font-family:monospace;font-size:9pt;background:#fef3c7;padding:1px 5px;border:1px solid #f59e0b;border-radius:2px;color:#92400e}</style></head><body>${html}</body></html>`], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `RD_Study_${(scope.clientName || 'Client').replace(/\s/g,'_')}_${scope.taxYear}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const qualProjects = projects.filter(p => p.qualification === "qualified");
  const excludedProjects = projects.filter(p => p.qualification === "excluded");

  return (
    <div className="p-8">
      <PhaseHeader number="14" title="Final R&D Study Report" description="The deliverable. Facts → method → conclusion → numbers, with full legal citations and document index." />

      <div className="flex gap-2 mb-6 no-print">
        <button onClick={() => setReportView("summary")} className={`sans text-xs font-medium px-3 py-1.5 border rounded-sm ${reportView === "summary" ? "bg-stone-900 text-sky-400 border-stone-900" : "bg-white border-stone-300"}`}>Summary</button>
        <button onClick={() => setReportView("full")} className={`sans text-xs font-medium px-3 py-1.5 border rounded-sm ${reportView === "full" ? "bg-stone-900 text-sky-400 border-stone-900" : "bg-white border-stone-300"}`}>Full Report</button>
        <div className="flex-1"></div>
        <button onClick={printReport} className="sans text-xs font-medium px-3 py-1.5 border border-stone-300 rounded-sm flex items-center gap-1.5 hover:bg-stone-50"><Printer size={12} /> Print</button>
        <button onClick={downloadHTML} className="sans text-xs font-medium px-3 py-1.5 bg-stone-900 text-sky-400 rounded-sm flex items-center gap-1.5 hover:bg-stone-800"><Download size={12} /> Download HTML</button>
      </div>

      <div id="rd-final-report" className="bg-white border border-stone-300 p-10 rounded-sm">
        <div className="border-b-4 border-stone-900 pb-5 mb-8">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">IRC §41 Research Credit Study</div>
          <h1 className="serif text-4xl font-bold text-stone-900 leading-tight">{scope.clientName || "[Client Name]"}</h1>
          <div className="flex items-center gap-6 mt-3 sans text-sm text-stone-700">
            <span>Tax Year: <strong>{scope.taxYear}</strong></span>
            <span>Entity: <strong>{scope.entityType}</strong></span>
            {scope.ein && <span>EIN: <strong className="mono">{scope.ein}</strong></span>}
          </div>
        </div>

        <section className="mb-8">
          <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">A. Executive Summary</h2>
          <div className="grid grid-cols-4 gap-4 my-5">
            <MetricCard label="Total QRE" value={`$${fmt(totalQRE)}`} />
            <MetricCard label="Federal Credit" value={`$${fmt(finalCredit)}`} highlight />
            <MetricCard label="Method" value={(compute.chosenMethod || compute.method) === "REGULAR" ? "Regular" : "ASC"} />
            <MetricCard label="§280C" value={compute.reducedCreditElection ? "Reduced" : "Gross"} />
          </div>
          <p className="serif text-sm text-stone-800 leading-relaxed">
            Based on our analysis of {qualProjects.length} qualified project{qualProjects.length !== 1 ? "s" : ""}, {employees.length} in-scope employee{employees.length !== 1 ? "s" : ""}, and related supplies and contract research,
            {" "}{scope.clientName || "the Company"} has <strong>${fmt(totalQRE)}</strong> in qualified research expenses for tax year {scope.taxYear}.
            The recommended federal research credit under the {(compute.chosenMethod || compute.method) === "REGULAR" ? "Regular Credit method (§41(a)(1))" : "Alternative Simplified Credit method (§41(c)(4)(A))"} is <strong>${fmt(finalCredit)}</strong>
            {compute.reducedCreditElection ? ", reflecting the §280C reduced-credit election" : ""}.
          </p>
        </section>

        {reportView === "full" && (
          <>
            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">B. Scope of Study</h2>
              <table className="w-full">
                <tbody>
                  <tr><td className="sans text-xs text-stone-600 py-1.5 w-48">Tax Year</td><td className="serif text-sm text-stone-900 py-1.5">{scope.taxYear}</td></tr>
                  <tr><td className="sans text-xs text-stone-600 py-1.5">Entity</td><td className="serif text-sm text-stone-900 py-1.5">{scope.clientName} ({scope.entityType}) {scope.ein && `— EIN ${scope.ein}`}</td></tr>
                  <tr><td className="sans text-xs text-stone-600 py-1.5">Jurisdiction</td><td className="serif text-sm text-stone-900 py-1.5">{scope.jurisdiction || "Federal"}</td></tr>
                  <tr><td className="sans text-xs text-stone-600 py-1.5">Study Type</td><td className="serif text-sm text-stone-900 py-1.5">{scope.studyType}</td></tr>
                  <tr><td className="sans text-xs text-stone-600 py-1.5">Controlled Group</td><td className="serif text-sm text-stone-900 py-1.5">{scope.controlledGroup ? "Yes — see §41(f)(1) aggregation analysis" : "No"}</td></tr>
                </tbody>
              </table>
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">C. Legal & Technical Framework</h2>
              <p className="serif text-sm text-stone-800 leading-relaxed mb-3">
                Under IRC §41(d)(1)(A)-(C), qualified research must satisfy the four-part test: permitted purpose, technical uncertainty, process of experimentation, and technological nature.
                Qualified research expenses consist of in-house research expenses (wages, supplies, and certain computer-use amounts) under §41(b)(2)(A)(i)-(iii) and contract research expenses under §41(b)(1)(B),
                generally counted at 65% per Treas. Reg. §1.41-2(e)(1).
              </p>
              <p className="serif text-sm text-stone-800 leading-relaxed">
                Qualified wage services are limited to (1) actual conduct of qualified research, (2) direct supervision, and (3) direct support, as defined in Treas. Reg. §1.41-2(c)(1)-(3).
                Exclusions under §41(d)(4) were evaluated and applied where applicable.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">D. Qualified Research Activities</h2>
              {qualProjects.length === 0 ? (
                <p className="serif text-sm text-stone-600 italic">No qualified projects identified yet.</p>
              ) : (
                <div className="space-y-4">
                  {qualProjects.map((p, i) => {
                    const projDocs = documents.filter(d => d.linkedTo?.scope === "project" && d.linkedTo?.projectId === p.id);
                    return (
                      <div key={p.id} className="border-l-4 border-emerald-500 pl-4 py-1">
                        <div className="serif text-base font-bold text-stone-900">Project {i + 1}: {p.name}</div>
                        {p.description && <div className="sans text-xs text-stone-600 italic mt-0.5">{p.description}</div>}
                        {p.narrative && <p className="serif text-sm text-stone-800 leading-relaxed mt-2">{p.narrative}</p>}

                        {p.fourPartNotes && Object.values(p.fourPartNotes).some(n => n) && (
                          <div className="mt-3 space-y-1">
                            {FOUR_PART_TEST.map(t => p.fourPartNotes[t.id] && (
                              <div key={t.id} className="serif text-xs text-stone-700">
                                <strong>{t.title}:</strong> {p.fourPartNotes[t.id]}
                              </div>
                            ))}
                          </div>
                        )}

                        {projDocs.length > 0 && (
                          <div className="mt-2 sans text-[10px] text-stone-600">
                            <strong>Supporting docs:</strong> {projDocs.map(d => d.name).join(", ")}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {excludedProjects.length > 0 && (
                <div className="mt-6">
                  <h3 className="serif text-base font-semibold text-stone-900 mb-2">Excluded Activities</h3>
                  <ul className="space-y-1 serif text-sm text-stone-700">
                    {excludedProjects.map(p => (<li key={p.id}>▸ <strong>{p.name}</strong>{p.description && ` — ${p.description}`}</li>))}
                  </ul>
                </div>
              )}
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">E. Qualified Research Expenses</h2>

              <h3 className="serif text-base font-semibold text-stone-900 mt-4 mb-2">Wages</h3>
              <table className="w-full text-sm">
                <thead><tr className="bg-stone-100"><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Employee</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Title</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Category</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">Wages</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">%</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">QRE</th></tr></thead>
                <tbody>
                  {employees.map(e => {
                    const qre = (parseFloat(e.wages) || 0) * (parseFloat(e.qualifiedPct) || 0) / 100;
                    return (<tr key={e.id}><td className="serif text-xs px-2 py-1 border border-stone-300">{e.name || "—"}</td><td className="serif text-xs px-2 py-1 border border-stone-300">{e.title}</td><td className="mono text-[10px] px-2 py-1 border border-stone-300 capitalize">{e.category}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(parseFloat(e.wages) || 0)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">{e.qualifiedPct || 0}%</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right font-semibold">${fmt(qre)}</td></tr>);
                  })}
                  <tr className="bg-stone-900 text-sky-400"><td colSpan={5} className="sans text-xs uppercase tracking-wider font-bold px-2 py-1.5 border border-stone-900">Total Qualified Wages — §41(b)(2)(A)(i)</td><td className="mono num-tabular text-sm font-bold px-2 py-1.5 border border-stone-900 text-right">${fmt(wageQRE)}</td></tr>
                </tbody>
              </table>

              {supplies.length > 0 && (<>
                <h3 className="serif text-base font-semibold text-stone-900 mt-6 mb-2">Supplies</h3>
                <table className="w-full text-sm"><thead><tr className="bg-stone-100"><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Description</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Vendor</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Project</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">Amount</th></tr></thead><tbody>{supplies.filter(s => s.qualified).map(s => (<tr key={s.id}><td className="serif text-xs px-2 py-1 border border-stone-300">{s.description}</td><td className="serif text-xs px-2 py-1 border border-stone-300">{s.vendor}</td><td className="serif text-xs px-2 py-1 border border-stone-300">{s.project}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(parseFloat(s.amount) || 0)}</td></tr>))}<tr className="bg-stone-900 text-sky-400"><td colSpan={3} className="sans text-xs uppercase tracking-wider font-bold px-2 py-1.5 border border-stone-900">Total Qualified Supplies — §41(b)(2)(A)(ii)</td><td className="mono num-tabular text-sm font-bold px-2 py-1.5 border border-stone-900 text-right">${fmt(supplyQRE)}</td></tr></tbody></table>
              </>)}

              {contractors.filter(c => c.qualified).length > 0 && (<>
                <h3 className="serif text-base font-semibold text-stone-900 mt-6 mb-2">Contract Research (65% per Treas. Reg. §1.41-2(e)(1))</h3>
                <table className="w-full text-sm"><thead><tr className="bg-stone-100"><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Vendor</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">SOW</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">Paid</th><th className="sans text-[10px] uppercase tracking-wider text-center px-2 py-1.5 border border-stone-300">Qual %</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">Qual $</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">× 65%</th></tr></thead><tbody>{contractors.filter(c => c.qualified).map(c => {
                  const useBreakout = c.useInvoiceBreakout && Array.isArray(c.invoices) && c.invoices.length > 0;
                  const invoices = c.invoices || [];
                  const rawTotal = useBreakout ? invoices.reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0) : (parseFloat(c.amount) || 0);
                  const qualTotal = useBreakout ? invoices.reduce((s, inv) => {
                    const a = parseFloat(inv.amount) || 0;
                    const p = Math.min(100, Math.max(0, parseFloat(inv.qualifiedPct) || 0));
                    return s + (a * p / 100);
                  }, 0) : rawTotal;
                  const pct = rawTotal > 0 ? (qualTotal / rawTotal) * 100 : 100;
                  return (<tr key={c.id}><td className="serif text-xs px-2 py-1 border border-stone-300">{c.vendor}{useBreakout && <span className="mono text-[9px] text-stone-500 ml-1">({invoices.length} inv)</span>}</td><td className="mono text-[10px] px-2 py-1 border border-stone-300">{c.sowRef}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(rawTotal)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-center">{pct.toFixed(0)}%</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(qualTotal)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right font-semibold">${fmt(qualTotal * 0.65)}</td></tr>);})}<tr className="bg-stone-900 text-sky-400"><td colSpan={5} className="sans text-xs uppercase tracking-wider font-bold px-2 py-1.5 border border-stone-900">Total Contract Research — §41(b)(1)(B)</td><td className="mono num-tabular text-sm font-bold px-2 py-1.5 border border-stone-900 text-right">${fmt(contractQRE)}</td></tr></tbody></table>
              </>)}

              <div className="mt-6 p-4 bg-stone-900 text-stone-100 rounded-sm flex items-center justify-between">
                <span className="serif text-base font-bold text-sky-400">TOTAL QRE</span>
                <span className="mono num-tabular serif text-2xl font-bold text-sky-400">${fmt(totalQRE)}</span>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">F. Credit Computation</h2>

              <h3 className="serif text-base font-semibold text-stone-900 mb-2">Method Comparison</h3>
              <table className="w-full text-sm mb-4">
                <thead><tr className="bg-stone-100"><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Step</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">ASC (§41(c)(4))</th><th className="sans text-[10px] uppercase tracking-wider text-right px-2 py-1.5 border border-stone-300">Regular (§41(a)(1))</th></tr></thead>
                <tbody>
                  <tr><td className="serif text-xs px-2 py-1 border border-stone-300">Current-year QREs</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(totalQRE)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(totalQRE)}</td></tr>
                  <tr><td className="serif text-xs px-2 py-1 border border-stone-300">Base amount</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(ascResult.base || 0)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(regularResult.baseAmount)}</td></tr>
                  <tr><td className="serif text-xs px-2 py-1 border border-stone-300">Excess (incremental QRE)</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(ascResult.excess || 0)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(regularResult.excess)}</td></tr>
                  <tr><td className="serif text-xs px-2 py-1 border border-stone-300">Rate</td><td className="mono text-xs px-2 py-1 border border-stone-300 text-right">{ascResult.method.includes("Fallback") ? "6%" : "14%"}</td><td className="mono text-xs px-2 py-1 border border-stone-300 text-right">20%</td></tr>
                  <tr><td className="serif text-xs px-2 py-1 border border-stone-300 font-semibold">Gross credit</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right font-semibold">${fmt(ascResult.gross)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right font-semibold">${fmt(regularResult.gross)}</td></tr>
                  {compute.reducedCreditElection && (
                    <tr><td className="serif text-xs px-2 py-1 border border-stone-300">§280C reduced (× 79%)</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(ascResult.reduced)}</td><td className="mono num-tabular text-xs px-2 py-1 border border-stone-300 text-right">${fmt(regularResult.reduced)}</td></tr>
                  )}
                </tbody>
              </table>

              <div className="p-4 bg-stone-50 border border-stone-300 rounded-sm mb-4">
                <div className="serif text-sm font-semibold text-stone-900 mb-2">Selected Method: {selectedResult.method}</div>
                <div className="mono text-xs text-stone-700 leading-relaxed">
                  {(compute.chosenMethod || compute.method) === "REGULAR" ? (
                    <>Per IRC §41(a)(1): Credit = 20% × (Current-Year QREs − Base Amount)<br />= 20% × (${fmt(totalQRE)} − ${fmt(regularResult.baseAmount)})<br />= 20% × ${fmt(regularResult.excess)}<br />= <strong>${fmt(regularResult.gross)}</strong> (gross regular)</>
                  ) : (
                    <>Per IRC §41(c)(4)(A): Credit = 14% × (Current-Year QREs − 50% × Avg Prior 3-Year QREs)<br />= 14% × (${fmt(totalQRE)} − ${fmt(ascResult.base || 0)})<br />= 14% × ${fmt(ascResult.excess || 0)}<br />= <strong>${fmt(ascResult.gross)}</strong> (gross ASC)</>
                  )}
                </div>
                {compute.reducedCreditElection && (<div className="mono text-xs text-stone-700 mt-2 pt-2 border-t border-stone-300">§280C reduced credit election: ${fmt(selectedResult.gross)} × 79% = <strong>${fmt(selectedResult.reduced)}</strong></div>)}
                {compute.methodSelectionNotes && (
                  <div className="mt-3 pt-3 border-t border-stone-300">
                    <div className="sans text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Rationale for method selection</div>
                    <div className="serif text-sm text-stone-800">{compute.methodSelectionNotes}</div>
                  </div>
                )}
              </div>
              <div className="p-4 bg-stone-900 text-stone-100 rounded-sm flex items-center justify-between">
                <span className="serif text-base font-bold text-sky-400">FINAL FEDERAL CREDIT</span>
                <span className="mono num-tabular serif text-3xl font-bold text-sky-400">${fmt(finalCredit)}</span>
              </div>
              {compute.payrollTaxElection && (<div className="mt-3 p-3 border border-stone-300 rounded-sm"><span className="sans text-xs text-stone-700"><strong>Payroll Tax Credit Election (§41(h)(1)):</strong> Elected amount: <strong className="mono">${fmt(Math.min(finalCredit, 500000))}</strong>.</span></div>)}
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">G. Document Index (Evidence File)</h2>
              {documents.length === 0 ? (
                <p className="serif text-sm text-stone-600 italic">No supporting documents uploaded.</p>
              ) : (
                <>
                  <p className="serif text-sm text-stone-700 mb-3">The following {documents.length} document{documents.length > 1 ? "s" : ""} constitute the evidence file supporting this study:</p>
                  <table className="w-full text-sm">
                    <thead><tr className="bg-stone-100"><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">#</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">File</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Category</th><th className="sans text-[10px] uppercase tracking-wider text-left px-2 py-1.5 border border-stone-300">Size</th></tr></thead>
                    <tbody>
                      {documents.map((d, i) => (
                        <tr key={d.id}><td className="mono text-[10px] px-2 py-1 border border-stone-300">{String(i+1).padStart(3,"0")}</td><td className="serif text-xs px-2 py-1 border border-stone-300">{d.name}</td><td className="mono text-[10px] px-2 py-1 border border-stone-300">{d.category}</td><td className="mono text-[10px] px-2 py-1 border border-stone-300">{humanFileSize(d.size)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">H. Limitations & Assumptions</h2>
              <ul className="serif text-sm text-stone-800 space-y-1.5 list-disc list-inside">
                <li>This study relies on information provided by {scope.clientName || "the Company"}, including project descriptions, payroll data, GL detail, and contract documentation.</li>
                <li>Qualified percentages for wage QREs reflect good-faith estimates supported by employee interviews and available time-tracking data.</li>
                <li>This study addresses the federal research credit under IRC §41; state R&D credits are not analyzed unless separately noted.</li>
                <li>Positions taken are based on current law as of {scope.taxYear}; legislative or regulatory changes may affect conclusions.</li>
                <li>This study is not a substitute for legal advice; conclusions may be subject to challenge on audit.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="serif text-xl font-bold text-stone-900 border-b border-stone-300 pb-1 mb-4">I. Authority Index</h2>
              <table className="w-full">
                <tbody>
                  {Object.entries(CITATIONS).map(([k, v]) => (
                    <tr key={k}>
                      <td className="mono text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-1.5 whitespace-nowrap align-top">{v.label}</td>
                      <td className="serif text-sm text-stone-800 p-2 leading-relaxed border border-stone-200">{v.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}

        <div className="mt-10 pt-4 border-t border-stone-300 mono text-[10px] uppercase tracking-widest text-stone-500">
          Report generated via R&D Study Workbench · {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SHARED UI
// ============================================================
function PhaseHeader({ number, title, description }) {
  return (
    <div className="border-b border-stone-200 pb-5 mb-6">
      <div className="flex items-center gap-3 mb-2">
        <span className="mono text-[10px] font-bold bg-sky-400 text-stone-900 px-2 py-0.5 rounded-sm tracking-widest">PHASE {number}</span>
      </div>
      <h2 className="serif text-3xl font-bold text-stone-900 tracking-tight leading-none mb-2">{title}</h2>
      <p className="sans text-sm text-stone-600 max-w-2xl leading-relaxed">{description}</p>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <div className="sans text-[10px] font-semibold text-stone-700 uppercase tracking-widest mb-1.5 flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
        {hint && <span className="text-stone-400 normal-case tracking-normal font-normal italic">— {hint}</span>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, value, onChange, cite }) {
  return (
    <div className="flex items-center justify-between p-3 bg-stone-50 border border-stone-200 rounded-sm">
      <div className="flex items-center gap-2">
        <span className="sans text-xs font-medium text-stone-800">{label}</span>
        {cite && <CiteBadge id={cite} />}
      </div>
      <button onClick={() => onChange(!value)} className={`relative w-10 h-5 rounded-full transition-colors ${value ? "bg-stone-900" : "bg-stone-300"}`}>
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}

function TriToggle({ current, onChange, compact }) {
  const opts = [{ v: true, l: "Yes", c: "emerald" }, { v: false, l: "No", c: "rose" }, { v: null, l: "Unsure", c: "stone" }];
  return (
    <div className={`flex gap-1 ${compact ? "" : "mt-1"}`}>
      {opts.map(o => (
        <button key={String(o.v)} onClick={() => onChange(o.v)}
          className={`sans font-medium border rounded-sm transition-colors ${compact ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1.5"} ${
            current === o.v
              ? o.c === "emerald" ? "bg-emerald-600 text-white border-emerald-700" :
                o.c === "rose" ? "bg-rose-600 text-white border-rose-700" :
                "bg-stone-700 text-white border-stone-800"
              : "bg-white border-stone-300 text-stone-600 hover:border-stone-500"
          }`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className={`flex items-start gap-2 p-2.5 border rounded-sm cursor-pointer transition-colors ${checked ? "border-emerald-400 bg-emerald-50" : "border-stone-200 bg-white hover:border-stone-400"}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="mt-0.5" />
      <span className="sans text-xs text-stone-800 leading-snug">{label}</span>
    </label>
  );
}

function StatCard({ label, count, color }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-800",
    amber: "bg-sky-50 border-sky-400 text-sky-800",
    rose: "bg-rose-50 border-rose-300 text-rose-800",
  };
  return (
    <div className={`p-4 border rounded-sm ${colors[color]}`}>
      <div className="sans text-[10px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
      <div className="serif text-3xl font-bold mt-1">{count}</div>
    </div>
  );
}

function QRECard({ label, value, cite, onCite, bold }) {
  return (
    <div className={`p-3 border rounded-sm ${bold ? "bg-stone-900 text-stone-100 border-stone-900" : "bg-white border-stone-300"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className={`sans text-[10px] uppercase tracking-widest font-semibold ${bold ? "text-sky-400" : "text-stone-500"}`}>{label}</span>
        {cite && <CiteBadge id={cite} onClick={onCite} />}
      </div>
      <div className={`mono num-tabular ${bold ? "text-2xl font-bold text-sky-400" : "text-lg font-semibold text-stone-900"}`}>${fmt(value)}</div>
    </div>
  );
}

function TestBox({ label, passed, detail }) {
  return (
    <div className={`p-3 border rounded-sm ${passed ? "border-emerald-400 bg-emerald-50" : "border-stone-300 bg-stone-50"}`}>
      <div className="flex items-center gap-2 mb-1">
        {passed ? <CheckCircle2 size={12} className="text-emerald-600" /> : <AlertCircle size={12} className="text-stone-400" />}
        <span className="sans text-[10px] uppercase tracking-widest font-semibold text-stone-700">{label}</span>
      </div>
      <div className="mono text-xs text-stone-900">{detail}</div>
    </div>
  );
}

function MetricCard({ label, value, highlight }) {
  return (
    <div className={`p-3 border rounded-sm ${highlight ? "bg-stone-900 text-sky-400 border-stone-900" : "bg-stone-50 border-stone-300"}`}>
      <div className={`sans text-[9px] uppercase tracking-widest font-semibold ${highlight ? "text-sky-400/70" : "text-stone-500"}`}>{label}</div>
      <div className={`serif text-xl font-bold mt-1 ${highlight ? "text-sky-400" : "text-stone-900"}`}>{value}</div>
    </div>
  );
}
